import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Users, Calendar, Package, MessageSquare, Plus, Clock, TrendingUp, Settings, FileText, HelpCircle, Edit, Trash2, RefreshCw, CheckSquare, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MaterialOrderForm from '@/components/MaterialOrderForm';
import StockManagementForm from '@/components/StockManagementForm';
import ClientManagement from '@/components/ClientManagement';
import { hasFeature, getCurrentPlanName } from '@/utils/features';
import { LockedFeatureButton, LockedFeatureSection } from '@/components/LockedFeature';
import { PushNotificationManager } from '@/components/PushNotificationManager';
import { NotificationCenter } from '@/components/NotificationCenter';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { IOSInstallBanner } from '@/components/IOSInstallBanner';
import { ShiftReminderSettings } from '@/components/ShiftReminderSettings';
import { DrivingLicensePrompt } from '@/components/DrivingLicensePrompt';
import { OnboardingTour } from '@/components/OnboardingTour';
import { StatsDetailsDialog } from '@/components/dashboard/StatsDetailsDialog';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';
import { HelpCenter } from '@/components/HelpCenter';
import { WelcomeGuide } from '@/components/WelcomeGuide';
import { forceAppUpdate, getAppVersion } from '@/lib/appUpdate';
import ChangePasswordDialog from '@/components/employee/ChangePasswordDialog';
import { MyEarnings } from '@/components/MyEarnings';
import { TaskManagement } from '@/components/TaskManagement';
import { InvoiceManagement } from '@/components/admin/InvoiceManagement';
import { useIsMobile } from '@/hooks/useIsMobile';
import { RoleTileGrid } from '@/components/dashboard/RoleTileGrid';
import { MobileScheduleBuilder } from '@/components/dashboard/MobileScheduleBuilder';

// Shared Styles
import '@/styles/companyHeader.css';

// Extracted Admin Components
import { ClockInCard } from '@/components/admin';
import { 
  PayrollExportDialog, 
  ExpenseExportDialog, 
  MaterialOrdersExportDialog, 
  CommunicationsExportDialog, 
  StockExportDialog 
} from '@/components/admin';
import { 
  EmployeeProfileDialog, 
  AddCertificateDialog, 
  AddLicenseDialog, 
  AddCredentialDialog 
} from '@/components/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_OPTIONS = [
  'Ladder',
  'Platform Ladder',
  'Welder Machine',
  'Welder Machine Wires',
  'Banding Sealer Tool',
  'Strapping Tool',
  'Tensioner Tool',
  'Sheet Metal Bending Brake',
  'Insulation Knife',
  'Propane Torch',
  'Propane Tank',
  'Other'
];

// Custom hook for sticky scrollbar
function useStickyScrollbar(tableRef) {
  const stickyScrollRef = useRef(null);
  const [showStickyScroll, setShowStickyScroll] = useState(false);

  useEffect(() => {
    const tableElement = tableRef.current;
    
    if (!tableElement) {
      return;
    }

    // Check if sticky scrollbar should be visible
    const checkScrollbarVisibility = () => {
      const tableRect = tableElement.getBoundingClientRect();
      const isScrollable = tableElement.scrollWidth > tableElement.clientWidth;
      const isTableBelowViewport = tableRect.bottom > window.innerHeight;
      const isTableAboveViewport = tableRect.top > window.innerHeight;
      
      // Show sticky scrollbar only if table is scrollable AND extends below viewport OR is completely below viewport
      const shouldShow = isScrollable && (isTableBelowViewport || isTableAboveViewport);
      
      setShowStickyScroll(shouldShow);
    };

    // Sync scroll positions
    const syncScroll = (source, target) => {
      if (target) {
        target.scrollLeft = source.scrollLeft;
      }
    };

    const handleTableScroll = () => {
      const stickyScrollElement = stickyScrollRef.current;
      if (stickyScrollElement) {
        syncScroll(tableElement, stickyScrollElement);
      }
    };
    
    const handleStickyScroll = () => {
      const stickyScrollElement = stickyScrollRef.current;
      if (stickyScrollElement) {
        syncScroll(stickyScrollElement, tableElement);
      }
    };

    // Add event listeners
    tableElement.addEventListener('scroll', handleTableScroll);
    window.addEventListener('scroll', checkScrollbarVisibility);
    window.addEventListener('resize', checkScrollbarVisibility);
    
    // Initial check
    checkScrollbarVisibility();

    // Cleanup function
    return () => {
      tableElement.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('scroll', checkScrollbarVisibility);
      window.removeEventListener('resize', checkScrollbarVisibility);
    };
  }, [tableRef]);

  // Separate useEffect for sticky scroll element events
  useEffect(() => {
    const stickyScrollElement = stickyScrollRef.current;
    const tableElement = tableRef.current;
    
    if (!stickyScrollElement || !tableElement) return;

    const handleStickyScroll = () => {
      tableElement.scrollLeft = stickyScrollElement.scrollLeft;
    };

    stickyScrollElement.addEventListener('scroll', handleStickyScroll);
    
    return () => {
      stickyScrollElement.removeEventListener('scroll', handleStickyScroll);
    };
  }, [showStickyScroll, tableRef]); // Re-run when sticky scroll becomes visible

  return { stickyScrollRef, showStickyScroll };
}

export default function AdminDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [subcontractorSearchQuery, setSubcontractorSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [subProjects, setSubProjects] = useState([]);
  const [showSubProjectDialog, setShowSubProjectDialog] = useState(false);
  const [showEditSubProjectDialog, setShowEditSubProjectDialog] = useState(false);
  const [selectedProjectForSubProject, setSelectedProjectForSubProject] = useState(null);
  const [editingSubProject, setEditingSubProject] = useState(null);
  const [subProjectForm, setSubProjectForm] = useState({
    name: '',
    description: '',
    budget: '',
    start_date: '',
    end_date: '',
    team_members: []
  });
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // Store all messages for filtering
  const [messageDateFilter, setMessageDateFilter] = useState({ startDate: '', endDate: '' });
  const [messageTypeFilter, setMessageTypeFilter] = useState('all');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  
  // Image Viewer
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [equipmentQuantities, setEquipmentQuantities] = useState({}); // Track quantities for each equipment type
  const [dashboardStats, setDashboardStats] = useState({
    activeProjects: 0,
    clockedInMembers: 0,
    weeklyHours: 0,
    biWeeklyHours: 0,
    biWeeklyPay: 0,
    biWeeklyPeriodStart: null,
    biWeeklyPeriodEnd: null,
    userWeeklyHours: 0,
    userBiWeeklyHours: 0,
    userBiWeeklyPay: 0
  });
  
  // Personal stats dialog
  const [showPersonalStatsDialog, setShowPersonalStatsDialog] = useState(false);
  const [personalStatsType, setPersonalStatsType] = useState('');
  const [personalStatsData, setPersonalStatsData] = useState(null);
  const [personalStatsLoading, setPersonalStatsLoading] = useState(false);

  // Mobile-only toggles for the Personal / Company stats "pages". When true
  // the inline tile grid hides and the matching stats panel takes over the
  // screen with a back button at the top.
  const [showPersonalStatsSection, setShowPersonalStatsSection] = useState(false);
  const [showCompanyStatsSection, setShowCompanyStatsSection] = useState(false);
  
  // Clock In/Out state for office staff
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [todayHours, setTodayHours] = useState(0);
  
  // Onboarding and Help state
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [teamMemberTimeWindows, setTeamMemberTimeWindows] = useState({});
  const [pollingStatus, setPollingStatus] = useState(false);

  const [employeeForm, setEmployeeForm] = useState({
    email: '', name: '', phone: '', position: '', hourly_rate: '', role: 'employee', password: '', employee_type: 'field_based', subcontractor_account_id: ''
  });
  
  // Subcontractor accounts state
  const [subcontractorAccounts, setSubcontractorAccounts] = useState([]);
  const [showSubcontractorDialog, setShowSubcontractorDialog] = useState(false);
  const [subcontractorForm, setSubcontractorForm] = useState({
    name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: ''
  });
  const [editingSubcontractor, setEditingSubcontractor] = useState(null);

  const [projectForm, setProjectForm] = useState({
    name: '', 
    address: '', 
    start_date: '', 
    client_id: null,
    status: 'active',
    stage: 'construction', // pre_construction or construction
    equipment: [], // Format: [{type: 'Ladder', quantity: 5, other_description: ''}]
    billing_date: '',
    billing_reminder_enabled: false
  });

  const [materialForm, setMaterialForm] = useState({
    pipe_insulation: [],
    pvc_sheets: [],
    pvc_rolls: [],
    pvc_fittings: {
      elbow_90: [],
      elbow_45: [],
      end_cap: [],
      tee: [],
      victaulic_90: [],
      victaulic_45: [],
      victaulic_tee: [],
      coupling_covers: []
    },
    duct_insulation: [],
    jacketing_items: [],
    aluminum_pipe_jackets: [],
    tape_items: {},
    equipment: [],
    others_notes: ''
  });

  const [equipment, setEquipment] = useState([]);
  const [equipmentLogs, setEquipmentLogs] = useState([]);
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({ type: '', custom_description: '', quantity: 1 });
  
  // Equipment Management (Admin)
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipmentSummary, setEquipmentSummary] = useState({ total_types: 0, total_units: 0, assigned_units: 0, available_units: 0 });
  const [showEquipmentManagementDialog, setShowEquipmentManagementDialog] = useState(false);
  const [showEditEquipmentDialog, setShowEditEquipmentDialog] = useState(false);
  const [showEquipmentAssignmentsDialog, setShowEquipmentAssignmentsDialog] = useState(false);
  const [showAssignEquipmentDialog, setShowAssignEquipmentDialog] = useState(false);
  const [equipmentManagementForm, setEquipmentManagementForm] = useState({ name: '', description: '', total_quantity: 1, unit: 'units' });
  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState(null);
  const [equipmentAssignments, setEquipmentAssignments] = useState([]);
  const [assignEquipmentForm, setAssignEquipmentForm] = useState({ project_id: '', quantity: 1, notes: '' });
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({ name: '', address: '', start_date: '', client_id: null, status: 'active', stage: 'construction' });

  const [materialOrders, setMaterialOrders] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showOrderDetailsDialog, setShowOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderComment, setOrderComment] = useState('');
  const [showDeliveryPhotoDialog, setShowDeliveryPhotoDialog] = useState(false);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [uploadingDeliveryPhotos, setUploadingDeliveryPhotos] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [detailsType, setDetailsType] = useState('');
  const [detailsData, setDetailsData] = useState([]);

  // Refs for sticky scrollbar
  const employeesTableRef = useRef(null);
  const projectsTableRef = useRef(null);
  const clientsTableRef = useRef(null);
  const equipmentTableRef = useRef(null);
  const stockTableRef = useRef(null);
  const ordersTableRef = useRef(null);
  const returnsTableRef = useRef(null);
  const transfersTableRef = useRef(null);

  // Use sticky scrollbar hooks
  const employeesScroll = useStickyScrollbar(employeesTableRef);
  const projectsScroll = useStickyScrollbar(projectsTableRef);
  const clientsScroll = useStickyScrollbar(clientsTableRef);
  const equipmentScroll = useStickyScrollbar(equipmentTableRef);
  const stockScroll = useStickyScrollbar(stockTableRef);
  const ordersScroll = useStickyScrollbar(ordersTableRef);
  const returnsScroll = useStickyScrollbar(returnsTableRef);
  const transfersScroll = useStickyScrollbar(transfersTableRef);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showEditStockDialog, setShowEditStockDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState(0);
  const [showStockManagementDialog, setShowStockManagementDialog] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportText, setExportText] = useState('');
  
  const [showProjectDetailsDialog, setShowProjectDetailsDialog] = useState(false);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);
  const [projectMessages, setProjectMessages] = useState([]);
  const [projectMessageText, setProjectMessageText] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // Message being replied to

  // Company Settings state
  const [showCompanySettingsDialog, setShowCompanySettingsDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(isMobile ? '__home__' : 'schedule');
  const [showQuickBuilder, setShowQuickBuilder] = useState(false);
  const onHomeMobile = isMobile && activeTab === '__home__';
  const [showMyProfileDialog, setShowMyProfileDialog] = useState(false);
  const [biWeeklyStartDate, setBiWeeklyStartDate] = useState('');
  const [companySettings, setCompanySettings] = useState({ 
    name: '', 
    tagline: '',
    logo_url: '',
    logo_base64: '',  // Persistent logo data
    name_animation: 'none',
    logo_animation: 'none',
    name_color: '#1e293b',
    name_font: 'Inter'
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [assigningUser, setAssigningUser] = useState(null);

  // Employee admin controls state
  const [showEditEmployeeDialog, setShowEditEmployeeDialog] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editEmployeeForm, setEditEmployeeForm] = useState({ name: '', position: '', hourly_rate: '', employee_type: 'field_based', subcontractor_account_id: 'direct_hire' });
  const [newRole, setNewRole] = useState('');
  const [selectedProjectsForAssignment, setSelectedProjectsForAssignment] = useState([]);
  
  // Additional Access Management
  const [showAdditionalAccessDialog, setShowAdditionalAccessDialog] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);
  const [additionalAccessForm, setAdditionalAccessForm] = useState([]);
  const AVAILABLE_PERMISSIONS = [
    { key: 'view_accounts_payroll', label: 'View Accounts & Payroll' },
    { key: 'manage_projects', label: 'Manage Projects' },
    { key: 'view_all_employees', label: 'View All Employees' },
    { key: 'manage_schedules', label: 'Manage Schedules' },
    { key: 'view_reports', label: 'View Reports' },
    { key: 'manage_orders', label: 'Manage Orders' },
    { key: 'view_stock_material', label: 'View Stock Material' },
    { key: 'manage_equipment', label: 'Manage Equipment' },
    { key: 'view_pre_construction', label: '📋 View Pre-Construction Projects', description: 'Allow access to pre-construction stage projects' }
  ];
  
  // Time management state
  const [showTimeResetDialog, setShowTimeResetDialog] = useState(false);
  const [showTimeAdjustDialog, setShowTimeAdjustDialog] = useState(false);
  const [timeAdjustForm, setTimeAdjustForm] = useState({ adjustment_date: '', hours: '', reason: '' });
  const [timeAdjustments, setTimeAdjustments] = useState([]);
  const [showAdjustmentsHistory, setShowAdjustmentsHistory] = useState(false);
  const [employeeDailyHours, setEmployeeDailyHours] = useState([]);
  const [loadingDailyHours, setLoadingDailyHours] = useState(false);

  // Schedule state
  const [schedules, setSchedules] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showEditScheduleDialog, setShowEditScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showScheduleDetailsDialog, setShowScheduleDetailsDialog] = useState(false);
  const [selectedScheduleDetails, setSelectedScheduleDetails] = useState(null);
  const [scheduleView, setScheduleView] = useState('list'); // 'list' (default for mobile), 'week', or 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day; // Start from Sunday
    return new Date(today.setDate(diff));
  });
  
  // Helper function to get next working day (skip weekends)
  const getNextWorkingDay = () => {
    const today = new Date();
    let nextDay = new Date(today);
    nextDay.setDate(nextDay.getDate() + 1); // Start with tomorrow
    // Skip weekends (Saturday = 6, Sunday = 0)
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    return nextDay.toISOString().split('T')[0];
  };
  
  const [scheduleForm, setScheduleForm] = useState({
    project_id: '',
    date: getNextWorkingDay(),
    team_members: [],
    is_recurring: false,
    recurring_pattern: 'daily',
    recurring_end_date: '',
    start_time: '07:00',
    end_time: '15:00',
    notes: '',
    status: 'scheduled'
  });
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [splitTimes, setSplitTimes] = useState({});

  // Real-time conflict checking when team members or date changes
  const checkScheduleConflicts = async () => {
    if (!scheduleForm.date || scheduleForm.team_members.length === 0 || !scheduleForm.project_id) {
      setScheduleConflicts([]);
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      // Fetch existing schedules for this date
      const response = await fetch(`${API_URL}/api/schedules?start_date=${scheduleForm.date}&end_date=${scheduleForm.date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const existingSchedules = await response.json();
        const conflicts = [];
        
        // Check each selected team member against existing schedules
        for (const memberId of scheduleForm.team_members) {
          for (const existingSchedule of existingSchedules) {
            // Skip if it's the same project
            if (existingSchedule.project_id === scheduleForm.project_id) continue;
            
            // Check if this member is already scheduled
            if (existingSchedule.team_members?.includes(memberId)) {
              const employee = employees.find(emp => (emp.user_id || emp.id) === memberId);
              conflicts.push({
                user_id: memberId,
                user_name: employee?.user?.name || employee?.name || 'Unknown',
                existing_project: existingSchedule.project_name || 'Unknown project'
              });
            }
          }
        }
        
        setScheduleConflicts(conflicts);
      }
    } catch (error) {
      console.error('Error checking schedule conflicts:', error);
    }
  };
  
  // Effect to check conflicts when date, project, or team members change
  useEffect(() => {
    if (showScheduleDialog) {
      checkScheduleConflicts();
    }
  }, [scheduleForm.date, scheduleForm.team_members, scheduleForm.project_id, showScheduleDialog]);

  const [messageText, setMessageText] = useState('');

  // Time tracking state for purchasers
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeSummary, setTimeSummary] = useState({
    today_hours: 0,
    week_hours: 0,
    pay_period_hours: 0,
    is_clocked_in: false,
    current_shift_start: null
  });
  const [clockingIn, setClockingIn] = useState(false);

  // Cost management state
  const [showAddCostDialog, setShowAddCostDialog] = useState(false);
  const [costForm, setCostForm] = useState({
    order_cost: '',
    delivery_charges: ''
  });

  // Material Returns & Transfers
  const [materialReturns, setMaterialReturns] = useState([]);
  const [materialTransfers, setMaterialTransfers] = useState([]);
  
  // Employee Database State
  const [selectedEmployeeForProfile, setSelectedEmployeeForProfile] = useState(null);
  const [showEmployeeProfileDialog, setShowEmployeeProfileDialog] = useState(false);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [showAddCertificateDialog, setShowAddCertificateDialog] = useState(false);
  const [showAddLicenseDialog, setShowAddLicenseDialog] = useState(false);
  const [showAddCredentialDialog, setShowAddCredentialDialog] = useState(false);
  const [showVisibilitySettingsDialog, setShowVisibilitySettingsDialog] = useState(false);
  const [visibilitySettings, setVisibilitySettings] = useState({
    show_certificates_to_managers: false,
    show_licenses_to_managers: false,
    show_credentials_to_managers: false,
    show_hourly_rate_to_managers: false,
    show_ytd_hours_to_managers: true,
    show_contact_info_to_managers: true
  });
  const [newCertificate, setNewCertificate] = useState({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '', document_url: '' });
  const [newLicense, setNewLicense] = useState({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '', document_url: '' });
  const [newCredential, setNewCredential] = useState({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  
  // Excel Export Dialogs
  const [showPayrollExportDialog, setShowPayrollExportDialog] = useState(false);
  const [showExpenseExportDialog, setShowExpenseExportDialog] = useState(false);
  const [showMaterialOrdersExportDialog, setShowMaterialOrdersExportDialog] = useState(false);
  const [showCommunicationsExportDialog, setShowCommunicationsExportDialog] = useState(false);
  const [showStockExportDialog, setShowStockExportDialog] = useState(false);
  const [exportForm, setExportForm] = useState({
    start_date: '',
    end_date: '',
    project_id: ''
  });

  useEffect(() => {
    fetchAllData();
    fetchCompanySettings();
    fetchReturnsAndTransfers();
    
    // Check if we should show welcome guide (after registration)
    const shouldShowGuide = localStorage.getItem('show_welcome_guide');
    const companyName = localStorage.getItem('new_company_name');
    if (shouldShowGuide === 'true') {
      setNewCompanyName(companyName || '');
      setShowWelcomeGuide(true);
      localStorage.removeItem('show_welcome_guide');
      localStorage.removeItem('new_company_name');
    }
    
    // Fetch equipment list for company_admin
    if (user && ['company_admin', 'general_manager', 'manager'].includes(user.role)) {
      fetchEquipmentList();
    }
    
    // Fetch time tracking data and clock status for ALL users who can clock in
    if (user && ['company_admin', 'purchaser', 'accounts', 'general_manager', 'project_coordinator', 'manager', 'director', 'estimator'].includes(user.role)) {
      fetchTimeEntries();
      fetchTimeSummary();
      checkClockStatus();
    }
  }, []);

  // Sync logoPreviewUrl with companySettings - prefer base64 for persistence
  useEffect(() => {
    if (companySettings.logo_base64) {
      setLogoPreviewUrl(companySettings.logo_base64);
    } else if (companySettings.logo_url) {
      setLogoPreviewUrl(companySettings.logo_url);
    }
  }, [companySettings.logo_url, companySettings.logo_base64]);

  // Sorted and filtered employees list
  const filteredEmployees = useMemo(() => {
    let result = [...employees];
    
    // Filter by search query
    if (employeeSearchQuery.trim()) {
      const query = employeeSearchQuery.toLowerCase();
      result = result.filter(emp => 
        (emp.user?.name || emp.name || '').toLowerCase().includes(query) ||
        (emp.user?.email || '').toLowerCase().includes(query) ||
        (emp.position || '').toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically by name
    result.sort((a, b) => {
      const nameA = (a.user?.name || a.name || '').toLowerCase();
      const nameB = (b.user?.name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return result;
  }, [employees, employeeSearchQuery]);

  // Sorted and filtered subcontractor accounts list
  const filteredSubcontractors = useMemo(() => {
    let result = [...subcontractorAccounts];
    
    // Filter by search query
    if (subcontractorSearchQuery.trim()) {
      const query = subcontractorSearchQuery.toLowerCase();
      result = result.filter(sub => 
        (sub.name || '').toLowerCase().includes(query) ||
        (sub.contact_name || '').toLowerCase().includes(query) ||
        (sub.contact_email || '').toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically by name
    result.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return result;
  }, [subcontractorAccounts, subcontractorSearchQuery]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId && !pollingStatus) {
      setPollingStatus(true);
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          toast.success('Payment successful! Subscription activated.');
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [empRes, projRes, clientsRes, matRes, msgRes, ordersRes, statsRes, equipRes, expenseRes, subcontractorRes] = await Promise.all([
        fetch(`${API_URL}/api/employees`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/materials`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/messages`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/material-orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/dashboard/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/equipment`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/expenses`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/subcontractor-accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (empRes.ok) setEmployees(await empRes.json());
      if (projRes.ok) setProjects(await projRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (equipRes.ok) setEquipment(await equipRes.json());
      if (matRes.ok) setMaterials(await matRes.json());
      if (msgRes.ok) {
        const msgs = await msgRes.json();
        setMessages(msgs);
        setAllMessages(msgs); // Store for filtering
      }
      if (ordersRes.ok) setMaterialOrders(await ordersRes.json());
      if (expenseRes.ok) setExpenses(await expenseRes.json());
      if (subcontractorRes.ok) setSubcontractorAccounts(await subcontractorRes.json());
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setDashboardStats({
          activeProjects: stats.active_projects,
          clockedInMembers: stats.clocked_in_members,
          weeklyHours: stats.weekly_hours,
          biWeeklyHours: stats.bi_weekly_hours || 0,
          biWeeklyPay: stats.bi_weekly_pay || 0,
          biWeeklyPeriodStart: stats.bi_weekly_period_start,
          biWeeklyPeriodEnd: stats.bi_weekly_period_end,
          userWeeklyHours: stats.user_weekly_hours || 0,
          userBiWeeklyHours: stats.user_bi_weekly_hours || 0,
          userBiWeeklyPay: stats.user_bi_weekly_pay || 0
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchMessagesWithFilter = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/messages`;
      const params = new URLSearchParams();
      
      if (messageDateFilter.startDate) {
        params.append('start_date', messageDateFilter.startDate);
      }
      if (messageDateFilter.endDate) {
        params.append('end_date', messageDateFilter.endDate);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        setAllMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages with filter:', error);
    }
  };

  const fetchEquipmentList = async () => {
    const token = localStorage.getItem('token');
    try {
      const [equipmentRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/equipment`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/equipment/summary`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (equipmentRes.ok) {
        const data = await equipmentRes.json();
        setEquipmentList(data);
      }
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setEquipmentSummary(summaryData);
      }
    } catch (error) {
      console.error('Error fetching equipment list:', error);
    }
  };

  const fetchEquipmentAssignments = async (equipmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/equipment/${equipmentId}/assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEquipmentAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching equipment assignments:', error);
    }
  };

  const fetchReturnsAndTransfers = async () => {
    const token = localStorage.getItem('token');
    try {
      const [returnsRes, transfersRes] = await Promise.all([
        fetch(`${API_URL}/api/material-returns`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/material-transfers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (returnsRes.ok) setMaterialReturns(await returnsRes.json());
      if (transfersRes.ok) setMaterialTransfers(await transfersRes.json());
    } catch (error) {
      console.error('Error fetching returns/transfers:', error);
    }
  };

  const handleReviewReturn = async (returnId, status, rejectionReason = null) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-returns/${returnId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, rejection_reason: rejectionReason })
      });

      if (response.ok) {
        toast.success(`Return request ${status}`);
        fetchReturnsAndTransfers();
        fetchAllData(); // Refresh stock data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to review return');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleReviewTransfer = async (transferId, status, rejectionReason = null) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-transfers/${transferId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, rejection_reason: rejectionReason })
      });

      if (response.ok) {
        toast.success(`Transfer request ${status}`);
        fetchReturnsAndTransfers();
        fetchAllData(); // Refresh stock data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to review transfer');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Clock In/Out Functions for Office Staff
  const checkClockStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const entries = await response.json();
        // Only entries explicitly marked as is_own_entry: true are the current user's
        const ownEntries = entries.filter(entry => entry.is_own_entry === true);
        const activeEntry = ownEntries.find(entry => !entry.clock_out);
        setIsClockedIn(!!activeEntry);
        if (activeEntry) {
          setClockInTime(new Date(activeEntry.clock_in));
        } else {
          setClockInTime(null);
        }
        
        // Fetch today's hours from backend (uses company timezone)
        try {
          const summaryRes = await fetch(`${API_URL}/api/time/summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (summaryRes.ok) {
            const summary = await summaryRes.json();
            // Add current running time if clocked in
            let todayTotal = summary.today_hours || 0;
            if (activeEntry) {
              const clockIn = new Date(activeEntry.clock_in);
              const now = new Date();
              todayTotal += (now - clockIn) / (1000 * 60 * 60);
            }
            setTodayHours(todayTotal);
          }
        } catch (err) {
          console.error('Error fetching time summary:', err);
        }
      }
    } catch (error) {
      console.error('Error checking clock status:', error);
    }
  };

  const handleClockIn = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: null, // Office work - no project
          latitude: null,
          longitude: null,
          address: 'Office'
        })
      });

      if (response.ok) {
        toast.success('Clocked in successfully');
        setIsClockedIn(true);
        setClockInTime(new Date());
        checkClockStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClockOut = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: null,
          longitude: null,
          address: 'Office',
          work_description: 'Office work'
        })
      });

      if (response.ok) {
        toast.success('Clocked out successfully');
        setIsClockedIn(false);
        setClockInTime(null);
        checkClockStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportPayroll = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `${API_URL}/api/export/payroll?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Payroll exported successfully');
        setShowPayrollExportDialog(false);
      } else {
        toast.error('Failed to export payroll');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportProjectExpenses = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/export/project-expenses?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;
      if (exportForm.project_id && exportForm.project_id !== 'all') {
        url += `&project_id=${exportForm.project_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `project_expenses_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Project expenses exported successfully');
        setShowExpenseExportDialog(false);
      } else {
        toast.error('Failed to export project expenses');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportMaterialOrders = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/export/material-orders?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;
      if (exportForm.project_id && exportForm.project_id !== 'all') {
        url += `&project_id=${exportForm.project_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `material_orders_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Material orders exported successfully');
        setShowMaterialOrdersExportDialog(false);
      } else {
        toast.error('Failed to export material orders');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportCommunications = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/export/project-communications?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;
      if (exportForm.project_id && exportForm.project_id !== 'all') {
        url += `&project_id=${exportForm.project_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `project_communications_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Communications exported successfully');
        setShowCommunicationsExportDialog(false);
      } else {
        toast.error('Failed to export communications');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportStockMovements = async () => {
    const token = localStorage.getItem('token');
    try {
      const url = `${API_URL}/api/export/stock-movements?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `stock_movements_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Stock movements exported successfully');
        setShowStockExportDialog(false);
      } else {
        toast.error('Failed to export stock movements');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleEquipmentExport = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/export/equipment?include_logs=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `equipment_report.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Equipment report exported successfully');
      } else {
        toast.error('Failed to export equipment report');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClientExport = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/export/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `client_projects_report.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Client report exported successfully');
      } else {
        toast.error('Failed to export client report');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Employee Database Functions
  const fetchEmployeeProfile = async (employeeId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const profile = await response.json();
        setEmployeeProfile(profile);
        setShowEmployeeProfileDialog(true);
      } else {
        toast.error('Failed to fetch employee profile');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const fetchVisibilitySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/employee-visibility`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        setVisibilitySettings(settings);
      }
    } catch (error) {
      console.error('Error fetching visibility settings:', error);
    }
  };

  const saveVisibilitySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/employee-visibility`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(visibilitySettings)
      });
      if (response.ok) {
        toast.success('Visibility settings saved');
        setShowVisibilitySettingsDialog(false);
      } else {
        toast.error('Failed to save visibility settings');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Document Upload Handler for Certificates and Licenses
  const handleDocumentUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsUploadingDocument(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/upload/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const documentUrl = data.url || data.file_url;
        
        if (type === 'certificate') {
          setNewCertificate(prev => ({ ...prev, document_url: documentUrl }));
        } else if (type === 'license') {
          setNewLicense(prev => ({ ...prev, document_url: documentUrl }));
        }
        toast.success('Document uploaded');
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Upload error');
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const addCertificate = async () => {
    if (!selectedEmployeeForProfile || !newCertificate.name || !newCertificate.document_url) {
      toast.error('Please fill required fields and upload document');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${selectedEmployeeForProfile}/certificates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCertificate)
      });
      if (response.ok) {
        toast.success('Certificate added');
        setShowAddCertificateDialog(false);
        setNewCertificate({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '', document_url: '' });
        fetchEmployeeProfile(selectedEmployeeForProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add certificate');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteCertificate = async (employeeId, certId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/certificates/${certId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Certificate deleted');
        fetchEmployeeProfile(employeeId);
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addLicense = async () => {
    if (!selectedEmployeeForProfile || !newLicense.license_type || !newLicense.document_url) {
      toast.error('Please fill required fields and upload document');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${selectedEmployeeForProfile}/licenses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLicense)
      });
      if (response.ok) {
        toast.success('License added');
        setShowAddLicenseDialog(false);
        setNewLicense({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '', document_url: '' });
        fetchEmployeeProfile(selectedEmployeeForProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add license');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteLicense = async (employeeId, licenseId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/licenses/${licenseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('License deleted');
        fetchEmployeeProfile(employeeId);
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addCredential = async () => {
    if (!selectedEmployeeForProfile || !newCredential.credential_type) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${selectedEmployeeForProfile}/credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCredential)
      });
      if (response.ok) {
        toast.success('Credential added');
        setShowAddCredentialDialog(false);
        setNewCredential({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
        fetchEmployeeProfile(selectedEmployeeForProfile);
      } else {
        toast.error('Failed to add credential');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteCredential = async (employeeId, credId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/credentials/${credId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Credential deleted');
        fetchEmployeeProfile(employeeId);
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const fetchSchedules = async (startDate, endDate) => {
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams();
      
      // Default to current week if no dates provided
      if (!startDate || !endDate) {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - dayOfWeek);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        startDate = toLocalDateString(weekStart);
        endDate = toLocalDateString(weekEnd);
      }
      
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      const response = await fetch(`${API_URL}/api/schedules?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSchedules(data);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };
  
  // Helper function for local date string (moved declaration earlier if not already)
  const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper to format date string without timezone conversion
  const formatDateDisplay = (dateString, options = {}) => {
    if (!dateString) return '';
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    if (options.weekday) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const date = new Date(`${year}-${month}-${day}T12:00:00Z`);
      const weekday = weekdays[date.getUTCDay()];
      const monthName = months[parseInt(month) - 1];
      return `${weekday}, ${monthName} ${parseInt(day)}, ${year}`;
    }
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const fetchTimeEntries = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTimeEntries(data);
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchTimeSummary = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTimeSummary(data);
      }
    } catch (error) {
      console.error('Error fetching time summary:', error);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scheduleForm)
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Check for conflicts
        if (result.status === 'conflict' && Array.isArray(result.conflicts)) {
          // Filter out any validation error objects before setting state
          const validConflicts = result.conflicts.filter(c => 
            c && typeof c === 'object' && c.user_name && !c.type && !c.loc && !c.msg
          );
          if (validConflicts.length > 0) {
            setScheduleConflicts(validConflicts);
            const newSplitTimes = {};
            validConflicts.forEach(c => {
              const existStart = c.existing_start_time || '07:00';
              const existEnd = c.existing_end_time || '15:00';
              const midHour = Math.floor((parseInt(existStart) + parseInt(existEnd)) / 2);
              const midTime = `${String(midHour).padStart(2, '0')}:00`;
              newSplitTimes[c.user_id] = {
                existing_start: existStart, existing_end: midTime,
                new_start: midTime, new_end: existEnd
              };
            });
            setSplitTimes(newSplitTimes);
            toast.warning('Scheduling conflicts detected! Review and resolve.');
            return;
          }
        }
        
        toast.success(result.message);
        setShowScheduleDialog(false);
        setScheduleForm({
          project_id: '',
          date: getNextWorkingDay(),
          team_members: [],
          is_recurring: false,
          recurring_pattern: 'daily',
          recurring_end_date: ''
        });
        setScheduleConflicts([]);
        fetchSchedules();
      } else {
        const error = await response.json();
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(error.detail)) {
          const errorMessages = error.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(error.detail || 'Failed to create schedule');
        }
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTimeSplit = async (conflicts) => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      for (const conflict of conflicts) {
        const times = splitTimes[conflict.user_id];
        if (!times || !conflict.existing_schedule_id) continue;
        await fetch(`${API_URL}/api/schedules/${conflict.existing_schedule_id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ start_time: times.existing_start, end_time: times.existing_end })
        });
      }
      const newStart = Object.values(splitTimes)[0]?.new_start || scheduleForm.start_time;
      const newEnd = Object.values(splitTimes)[0]?.new_end || scheduleForm.end_time;
      const createRes = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...scheduleForm, start_time: newStart, end_time: newEnd, force_create: true })
      });
      if (createRes.ok) {
        toast.success('Schedule created with time split applied');
        setShowScheduleDialog(false);
        setScheduleForm({ project_id: '', date: getNextWorkingDay(), team_members: [], is_recurring: false, recurring_pattern: 'daily', recurring_end_date: '' });
      } else { toast.error('Failed to create schedule'); }
      setScheduleConflicts([]); setSplitTimes({});
      fetchSchedules();
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      project_id: schedule.project_id || '',
      date: schedule.date || getNextWorkingDay(),
      team_members: schedule.team_members || [],
      is_recurring: false,
      recurring_pattern: 'daily',
      recurring_end_date: '',
      start_time: schedule.start_time || '07:00',
      end_time: schedule.end_time || '15:00',
      notes: schedule.notes || '',
      status: schedule.status || 'scheduled'
    });
    setShowEditScheduleDialog(true);
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    if (!editingSchedule) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/schedules/${editingSchedule.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: scheduleForm.date,
          team_members: scheduleForm.team_members,
          project_id: scheduleForm.project_id,
          start_time: scheduleForm.start_time,
          end_time: scheduleForm.end_time,
          notes: scheduleForm.notes,
          status: scheduleForm.status
        })
      });
      
      if (response.ok) {
        toast.success('Schedule updated successfully');
        setShowEditScheduleDialog(false);
        setEditingSchedule(null);
        setScheduleForm({
          project_id: '',
          date: getNextWorkingDay(),
          team_members: [],
          is_recurring: false,
          recurring_pattern: 'daily',
          recurring_end_date: '',
          start_time: '07:00',
          end_time: '15:00',
          notes: '',
          status: 'scheduled'
        });
        fetchSchedules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update schedule');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Schedule deleted');
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Format the data properly for the backend
      const employeeData = {
        email: employeeForm.email,
        name: employeeForm.name,
        phone: employeeForm.phone || null,
        position: employeeForm.position,
        hourly_rate: employeeForm.hourly_rate ? parseFloat(employeeForm.hourly_rate) : null,
        role: employeeForm.role,
        password: employeeForm.password || null,
        employee_type: employeeForm.employee_type || 'field_based',
        subcontractor_account_id: employeeForm.subcontractor_account_id && employeeForm.subcontractor_account_id !== 'direct_hire' ? employeeForm.subcontractor_account_id : null
      };

      const response = await fetch(`${API_URL}/api/employees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(employeeData)
      });

      if (response.ok) {
        toast.success('Employee added successfully');
        setShowEmployeeDialog(false);
        setEmployeeForm({ email: '', name: '', phone: '', position: '', hourly_rate: '', role: 'employee', password: '', employee_type: 'field_based', subcontractor_account_id: '' });
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add employee');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Admin employee controls handlers
  const handleOpenEditEmployee = (emp) => {
    setSelectedEmployee(emp);
    setEditEmployeeForm({
      name: emp.user?.name || '',
      position: emp.position || '',
      hourly_rate: emp.hourly_rate || '',
      employee_type: emp.employee_type || 'field_based',
      subcontractor_account_id: emp.subcontractor_account_id || 'direct_hire'
    });
    setShowEditEmployeeDialog(true);
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Update basic employee details
      const response = await fetch(`${API_URL}/api/admin/employees/${selectedEmployee.id}/details`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editEmployeeForm.name,
          position: editEmployeeForm.position,
          hourly_rate: parseFloat(editEmployeeForm.hourly_rate)
        })
      });

      if (response.ok) {
        // Also update employee type if changed
        if (editEmployeeForm.employee_type !== selectedEmployee.employee_type) {
          const typeResponse = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/type?employee_type=${editEmployeeForm.employee_type}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!typeResponse.ok) {
            const error = await typeResponse.json();
            toast.error(error.detail || 'Failed to update employee type');
          }
        }
        
        // Update subcontractor account if changed
        const newAccountId = editEmployeeForm.subcontractor_account_id === 'direct_hire' ? null : editEmployeeForm.subcontractor_account_id;
        if (newAccountId !== selectedEmployee.subcontractor_account_id) {
          const accountResponse = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/subcontractor-account${newAccountId ? `?subcontractor_account_id=${newAccountId}` : ''}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!accountResponse.ok) {
            const error = await accountResponse.json();
            toast.error(error.detail || 'Failed to update subcontractor account');
          }
        }
        
        toast.success('Employee updated successfully');
        setShowEditEmployeeDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update employee');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Additional Access Management handlers
  const handleOpenAdditionalAccess = async (emp) => {
    setSelectedUserForAccess(emp);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/users/${emp.user_id}/additional-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdditionalAccessForm(data.additional_access || []);
      }
    } catch (error) {
      console.error('Error fetching additional access:', error);
      setAdditionalAccessForm([]);
    }
    
    setShowAdditionalAccessDialog(true);
  };

  const handleSaveAdditionalAccess = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/users/${selectedUserForAccess.user_id}/additional-access`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ additional_access: additionalAccessForm })
      });
      
      if (response.ok) {
        toast.success('Additional access updated successfully');
        setShowAdditionalAccessDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update additional access');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permission) => {
    if (additionalAccessForm.includes(permission)) {
      setAdditionalAccessForm(additionalAccessForm.filter(p => p !== permission));
    } else {
      setAdditionalAccessForm([...additionalAccessForm, permission]);
    }
  };

  const handleOpenChangeRole = (emp) => {
    setSelectedEmployee(emp);
    setNewRole(emp.user?.role || 'employee');
    setShowChangeRoleDialog(true);
  };

  const handleChangeRole = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${selectedEmployee.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('Role updated successfully');
        setShowChangeRoleDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmployeeStatus = async (emp) => {
    const token = localStorage.getItem('token');
    const isPausing = emp.user?.is_active !== false;
    const action = isPausing ? 'pause' : 'activate';

    if (!confirm(`Are you sure you want to ${action} ${emp.user?.name}?`)) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${emp.id}/${action}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(`Employee ${action}d successfully`);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} employee`);
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (emp) => {
    if (!confirm(`Are you sure you want to DELETE ${emp.user?.name}? This action cannot be undone.`)) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${emp.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Employee deleted successfully');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete employee');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Time Management Handlers
  const handleOpenTimeReset = (emp) => {
    setSelectedEmployee(emp);
    setShowTimeResetDialog(true);
  };

  const handleResetEmployeeTime = async () => {
    if (!selectedEmployee) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/time/reset/${selectedEmployee.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowTimeResetDialog(false);
        setSelectedEmployee(null);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset time');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTimeAdjust = async (emp) => {
    setSelectedEmployee(emp);
    setTimeAdjustForm({ adjustment_date: '', hours: '', reason: '' });
    setEmployeeDailyHours([]);
    setShowTimeAdjustDialog(true);
    
    // Fetch daily hours for this employee
    const token = localStorage.getItem('token');
    setLoadingDailyHours(true);
    try {
      const response = await fetch(`${API_URL}/api/time/daily-hours/${emp.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeDailyHours(data.days || []);
      }
    } catch (error) {
      console.error('Error fetching daily hours:', error);
    } finally {
      setLoadingDailyHours(false);
    }
  };

  const handleAdjustEmployeeTime = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !timeAdjustForm.adjustment_date) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/time/adjust/${selectedEmployee.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adjustment_date: timeAdjustForm.adjustment_date,
          hours: parseFloat(timeAdjustForm.hours),
          reason: timeAdjustForm.reason
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowTimeAdjustDialog(false);
        setSelectedEmployee(null);
        setTimeAdjustForm({ adjustment_date: '', hours: '', reason: '' });
        setEmployeeDailyHours([]);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to adjust time');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAdjustmentsHistory = async (emp) => {
    setSelectedEmployee(emp);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/time/adjustments/${emp.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeAdjustments(data);
        setShowAdjustmentsHistory(true);
      } else {
        toast.error('Failed to load adjustment history');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Equipment Management Handlers
  const handleAddEquipmentItem = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: equipmentManagementForm.name,
          description: equipmentManagementForm.description,
          total_quantity: parseInt(equipmentManagementForm.total_quantity) || 1,
          unit: equipmentManagementForm.unit || 'units'
        })
      });

      if (response.ok) {
        toast.success('Equipment added to pool');
        setShowEquipmentManagementDialog(false);
        setEquipmentManagementForm({ name: '', description: '', total_quantity: 1, unit: 'units' });
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditEquipment = (item) => {
    setSelectedEquipmentItem(item);
    setEquipmentManagementForm({
      name: item.name || item.type || '',
      description: item.description || item.custom_description || '',
      total_quantity: item.total_quantity || item.quantity || 1,
      unit: item.unit || 'units'
    });
    setShowEditEquipmentDialog(true);
  };

  const handleEditEquipmentItem = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment/${selectedEquipmentItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: equipmentManagementForm.name,
          description: equipmentManagementForm.description,
          total_quantity: parseInt(equipmentManagementForm.total_quantity) || 1,
          unit: equipmentManagementForm.unit || 'units'
        })
      });

      if (response.ok) {
        toast.success('Equipment updated successfully');
        setShowEditEquipmentDialog(false);
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipmentItem = async (item) => {
    if (item.assigned_quantity > 0) {
      toast.error(`Cannot delete. ${item.assigned_quantity} units are currently assigned to projects.`);
      return;
    }
    if (!confirm(`Are you sure you want to delete "${item.name || item.type}"? This action cannot be undone.`)) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment/${item.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Equipment deleted successfully');
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewEquipmentAssignments = async (item) => {
    setSelectedEquipmentItem(item);
    await fetchEquipmentAssignments(item.id);
    setShowEquipmentAssignmentsDialog(true);
  };

  const handleOpenAssignEquipment = (item) => {
    setSelectedEquipmentItem(item);
    setAssignEquipmentForm({ project_id: '', quantity: 1, notes: '' });
    setShowAssignEquipmentDialog(true);
  };

  const handleAssignEquipmentToProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/projects/${assignEquipmentForm.project_id}/equipment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          equipment_id: selectedEquipmentItem.id,
          quantity: parseInt(assignEquipmentForm.quantity) || 1,
          notes: assignEquipmentForm.notes
        })
      });

      if (response.ok) {
        toast.success('Equipment assigned to project');
        setShowAssignEquipmentDialog(false);
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignEquipment = async (assignmentId, projectId) => {
    if (!confirm('Return this equipment to the pool?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/equipment/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Equipment returned to pool');
        if (selectedEquipmentItem) {
          await fetchEquipmentAssignments(selectedEquipmentItem.id);
        }
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to return equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Format equipment with quantities for backend
      const formattedEquipment = projectForm.equipment.map(eq => ({
        type: eq.type,
        quantity: eq.quantity || 1,
        other_description: eq.other_description || ''
      }));

      const projectPayload = {
        name: projectForm.name,
        address: projectForm.address,
        start_date: projectForm.start_date,
        client_id: projectForm.client_id || null,
        status: projectForm.status || 'active',
        stage: projectForm.stage || 'construction',
        equipment: formattedEquipment,
        billing_date: projectForm.billing_date || null,
        billing_reminder_enabled: projectForm.billing_reminder_enabled || false
      };

      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectPayload)
      });

      if (response.ok) {
        toast.success('Project created successfully');
        setShowProjectDialog(false);
        setProjectForm({ 
          name: '', 
          address: '', 
          start_date: '', 
          client_id: null,
          status: 'active',
          stage: 'construction',
          equipment: [],
          billing_date: '',
          billing_reminder_enabled: false
        });
        setEquipmentQuantities({});
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTeam = async (projectId) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Build assignments with time windows
      const assignments = selectedTeamMembers.map(userId => ({
        user_id: userId,
        start_time: teamMemberTimeWindows[userId]?.start_time || null,
        end_time: teamMemberTimeWindows[userId]?.end_time || null
      }));

      const response = await fetch(`${API_URL}/api/projects/${projectId}/assign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignments })
      });

      if (response.ok) {
        toast.success('Team assigned successfully');
        setShowAssignDialog(false);
        setSelectedProject(null);
        setSelectedTeamMembers([]);
        setTeamMemberTimeWindows({});
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign team');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const toggleTeamMember = (empId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const handleCreateMaterialOrder = async (orderData) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const newOrder = await response.json();
        toast.success('Material order created successfully');
        
        // Send notification to admins and purchasers
        const project = projects.find(p => p.id === orderData.project_id);
        const notificationMessage = `New material order created for project: ${project?.name || 'Unknown'}`;
        
        try {
          await fetch(`${API_URL}/api/messages`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ message: notificationMessage })
          });
        } catch (err) {
          console.error('Failed to send notification:', err);
        }
        
        setShowOrderDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsDialog(true);
  };
  const handleUpdateOrderStatusWithPhotos = async (orderId, newStatus, photoUrls = []) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          delivery_photos: photoUrls
        })
      });

      if (response.ok) {
        toast.success('Order status updated');
        setShowDeliveryPhotoDialog(false);
        setDeliveryPhotos([]);
        fetchAllData();
        if (selectedOrder) {
          const updatedOrder = await fetch(`${API_URL}/api/material-orders/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (updatedOrder.ok) {
            setSelectedOrder(await updatedOrder.json());
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleMarkReceivedAtSite = async () => {
    if (!selectedOrder) return;
    
    // If photos are selected, upload them first
    if (deliveryPhotos.length > 0) {
      setUploadingDeliveryPhotos(true);
      const token = localStorage.getItem('token');
      
      try {
        const formData = new FormData();
        deliveryPhotos.forEach(file => {
          formData.append('files', file);
        });
        
        const uploadResponse = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          const photoUrls = uploadResult.image_urls;
          await handleUpdateOrderStatusWithPhotos(selectedOrder.id, 'received_at_site', photoUrls);
        } else {
          toast.error('Failed to upload photos');
        }
      } catch (error) {
        toast.error('Upload error');
      } finally {
        setUploadingDeliveryPhotos(false);
      }
    } else {
      // No photos, just update status
      await handleUpdateOrderStatusWithPhotos(selectedOrder.id, 'received_at_site', []);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Order status updated to ${newStatus}`);
        
        // Update local state
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
        
        // Refresh orders list
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrderComment = async (orderId) => {
    if (!orderComment.trim()) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: orderComment })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Comment added successfully');
        
        // Update local state with new comment
        setSelectedOrder(prev => ({
          ...prev,
          comments: [...(prev.comments || []), result.comment]
        }));
        
        setOrderComment('');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add comment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCost = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    const orderCost = parseFloat(costForm.order_cost) || 0;
    const deliveryCharges = parseFloat(costForm.delivery_charges) || 0;

    if (orderCost < 0 || deliveryCharges < 0) {
      toast.error('Cost values must be positive');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${selectedOrder.id}/cost`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_cost: orderCost,
          delivery_charges: deliveryCharges
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Cost added successfully');
        
        // Update local state
        setSelectedOrder(prev => ({
          ...prev,
          order_cost: orderCost,
          delivery_charges: deliveryCharges,
          total_cost: result.total_cost
        }));
        
        setShowAddCostDialog(false);
        setCostForm({ order_cost: '', delivery_charges: '' });
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add cost');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaterial = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(materialForm)
      });

      if (response.ok) {
        toast.success('Material added successfully');
        
        // If equipment type is selected, also create equipment item
        if (materialForm.equipment_type && materialForm.equipment_type !== '') {
          try {
            const equipResponse = await fetch(`${API_URL}/api/equipment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                type: materialForm.equipment_type,
                custom_description: materialForm.equipment_other || ''
              })
            });
            
            if (equipResponse.ok) {
              toast.success('Equipment also added to inventory');
            }
          } catch (error) {
            console.error('Error creating equipment:', error);
          }
        }
        
        setShowMaterialDialog(false);
        setMaterialForm({ name: '', category: 'HVAC', unit: 'piece', price: '', stock_quantity: '', equipment_type: '', equipment_other: '' });
        fetchAllData();
      } else {
        toast.error('Failed to add material');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const quantity = parseInt(equipmentForm.quantity) || 1;
      let successCount = 0;

      // Create the specified quantity of equipment items
      for (let i = 0; i < quantity; i++) {
        const response = await fetch(`${API_URL}/api/equipment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: equipmentForm.type,
            custom_description: equipmentForm.type === 'Other' ? equipmentForm.custom_description : ''
          })
        });

        if (response.ok) {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} equipment item(s) added to inventory`);
        setShowAddEquipmentDialog(false);
        setEquipmentForm({ type: '', custom_description: '', quantity: 1 });
        fetchAllData();
      } else {
        toast.error('Failed to add equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnEquipment = async (equipmentId) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment/${equipmentId}/return`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Equipment returned to inventory');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to return equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStock = async () => {
    if (!editingMaterial || stockAdjustment === 0) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const newStock = editingMaterial.stock_quantity + stockAdjustment;
      const response = await fetch(`${API_URL}/api/materials/${editingMaterial.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stock_quantity: newStock })
      });

      if (response.ok) {
        toast.success(`Stock ${stockAdjustment > 0 ? 'increased' : 'decreased'} successfully`);
        setShowEditStockDialog(false);
        setEditingMaterial(null);
        setStockAdjustment(0);
        fetchAllData();
      } else {
        toast.error('Failed to update stock');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStockMovement = async (stockData) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/stock-movements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stockData)
      });

      if (response.ok) {
        const movementType = stockData.movement_type === 'new_arrival' ? 'New stock added' : 'Returns processed';
        toast.success(`${movementType} successfully`);
        setShowStockManagementDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process stock movement');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseStatusUpdate = async (expenseId, status) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success(`Expense ${status}`);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update expense');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportOrder = async (orderId) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setExportText(data.export_text);
        setShowExportDialog(true);
      } else {
        toast.error('Failed to export order');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyExport = async () => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(exportText);
      toast.success('Copied to clipboard!');
    } catch (err) {
      // Fallback: create a temporary textarea
      try {
        const textArea = document.createElement('textarea');
        textArea.value = exportText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Copied to clipboard!');
      } catch (fallbackErr) {
        // If both methods fail, just show success and let user manually copy
        toast.info('Please manually copy the text below');
      }
    }
  };

  const handleOpenAssignDialog = (employee) => {
    setAssigningUser(employee.user);
    // Load current assignments
    setSelectedProjectsForAssignment(employee.user.assigned_projects || []);
    setSelectedTeamMembers(employee.user.team_members || []);
    setShowAssignDialog(true);
  };

  const handleSaveAssignments = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Assign projects if applicable
      if (assigningUser.role === 'project_manager' || assigningUser.role === 'team_lead' || assigningUser.role === 'project_coordinator') {
        await fetch(`${API_URL}/api/users/${assigningUser.id}/assign-projects`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(selectedProjectsForAssignment)
        });
      }

      // Assign team members if Team Lead
      if (assigningUser.role === 'team_lead') {
        await fetch(`${API_URL}/api/users/${assigningUser.id}/assign-team`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(selectedTeamMembers)
        });
      }

      toast.success('Assignments saved successfully');
      setShowAssignDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProjectDetails = async (project) => {
    setSelectedProjectDetails(project);
    setSelectedProjectForSubProject(project);
    setShowProjectDetailsDialog(true);
    
    // Fetch sub-projects for this project
    fetchSubProjects(project.id);
    
    // Fetch project-specific messages
    const token = localStorage.getItem('token');
    try {
      console.log('Fetching messages for project:', project.id);
      const response = await fetch(`${API_URL}/api/projects/${project.id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const messages = await response.json();
        console.log('Project messages loaded:', messages.length, messages);
        setProjectMessages(messages);
      } else {
        console.error('Failed to fetch messages:', response.status);
      }
    } catch (error) {
      console.error('Error fetching project messages:', error);
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editProjectForm)
      });

      if (response.ok) {
        toast.success('Project updated successfully');
        setShowEditProjectDialog(false);
        fetchAllData();
        // Update selected project details
        setSelectedProjectDetails({
          ...selectedProjectDetails,
          ...editProjectForm
        });
      } else {
        toast.error('Failed to update project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipmentToProject = async (equipmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}/equipment/${equipmentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Equipment added to project');
        fetchAllData();
        handleViewProjectDetails(selectedProjectDetails); // Refresh project details
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Sub-project Management Functions
  const fetchSubProjects = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/sub-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubProjects(data);
      }
    } catch (error) {
      console.error('Error fetching sub-projects:', error);
    }
  };

  const handleAddSubProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/sub-projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: selectedProjectForSubProject.id,
          name: subProjectForm.name,
          description: subProjectForm.description,
          budget: subProjectForm.budget ? parseFloat(subProjectForm.budget) : null,
          start_date: subProjectForm.start_date || null,
          end_date: subProjectForm.end_date || null,
          team_members: subProjectForm.team_members
        })
      });

      if (response.ok) {
        toast.success('Sub-project created successfully');
        setShowSubProjectDialog(false);
        setSubProjectForm({
          name: '',
          description: '',
          budget: '',
          start_date: '',
          end_date: '',
          team_members: []
        });
        fetchSubProjects(selectedProjectForSubProject.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sub-project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubProject = async (subProjectId) => {
    if (!confirm('Are you sure you want to delete this sub-project?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/sub-projects/${subProjectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Sub-project deleted successfully');
        fetchSubProjects(selectedProjectForSubProject.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete sub-project');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleEditSubProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/sub-projects/${editingSubProject.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: subProjectForm.name,
          description: subProjectForm.description,
          budget: subProjectForm.budget ? parseFloat(subProjectForm.budget) : null,
          start_date: subProjectForm.start_date || null,
          end_date: subProjectForm.end_date || null,
          team_members: subProjectForm.team_members
        })
      });

      if (response.ok) {
        toast.success('Sub-project updated successfully');
        setShowEditSubProjectDialog(false);
        setEditingSubProject(null);
        setSubProjectForm({
          name: '',
          description: '',
          budget: '',
          start_date: '',
          end_date: '',
          team_members: []
        });
        fetchSubProjects(selectedProjectForSubProject.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update sub-project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const openEditSubProjectDialog = (subProject) => {
    setEditingSubProject(subProject);
    setSubProjectForm({
      name: subProject.name || '',
      description: subProject.description || '',
      budget: subProject.budget || '',
      start_date: subProject.start_date || '',
      end_date: subProject.end_date || '',
      team_members: subProject.team_members || []
    });
    setShowEditSubProjectDialog(true);
  };

  const handleRemoveEquipmentFromProject = async (equipmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}/equipment/${equipmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Equipment removed and returned to stock');
        fetchAllData();
        handleViewProjectDetails(selectedProjectDetails); // Refresh project details
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const fetchEquipmentLogs = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/equipment/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setEquipmentLogs(await response.json());
      }
    } catch (error) {
      console.error('Error fetching equipment logs:', error);
    }
  };

  const handleSendProjectMessage = async (e) => {
    e.preventDefault();
    if (!projectMessageText.trim() || !selectedProjectDetails) return;

    const token = localStorage.getItem('token');
    setUploadingImages(true);
    
    try {
      let imageUrls = [];
      let videoUrl = null;
      
      // Upload images if any are selected
      if (selectedImages.length > 0) {
        const formData = new FormData();
        selectedImages.forEach(file => {
          formData.append('files', file);
        });
        
        const uploadResponse = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          imageUrls = uploadResult.image_urls;
        } else {
          toast.error('Failed to upload images');
          return;
        }
      }
      
      // Upload video if selected
      if (selectedVideo) {
        setUploadingVideo(true);
        const videoFormData = new FormData();
        videoFormData.append('file', selectedVideo);
        
        const videoUploadResponse = await fetch(`${API_URL}/api/upload-video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: videoFormData
        });
        
        if (videoUploadResponse.ok) {
          const videoResult = await videoUploadResponse.json();
          videoUrl = videoResult.video_url;
        } else {
          const error = await videoUploadResponse.json();
          toast.error(error.detail || 'Failed to upload video');
          setUploadingVideo(false);
          return;
        }
        setUploadingVideo(false);
      }
      
      // Send message with image URLs and video URL in body
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          message: projectMessageText,
          message_type: 'project',
          project_id: selectedProjectDetails.id,
          reply_to_id: replyingTo?.id || null,
          image_urls: imageUrls,
          video_url: videoUrl
        })
      });

      if (response.ok) {
        setProjectMessageText('');
        setSelectedImages([]);
        setSelectedVideo(null);
        setReplyingTo(null);
        toast.success('Message sent');
        
        // Refresh project messages immediately
        const refreshResponse = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}/messages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (refreshResponse.ok) {
          const updatedMessages = await refreshResponse.json();
          setProjectMessages(updatedMessages);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Connection error');
    } finally {
      setUploadingImages(false);
      setUploadingVideo(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageText })
      });

      if (response.ok) {
        setMessageText('');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleDashboardCardClick = async (type) => {
    const token = localStorage.getItem('token');
    setDetailsLoading(true);
    setShowDetailsDialog(true);
    setDetailsType(type);
    
    try {
      let endpoint = '';
      
      if (type === 'clocked-in') {
        endpoint = '/api/dashboard/clocked-in';
      } else if (type === 'weekly-hours') {
        endpoint = '/api/dashboard/weekly-hours';
      } else if (type === 'biweekly-hours') {
        endpoint = '/api/dashboard/biweekly-hours';
      } else if (type === 'biweekly-payroll') {
        endpoint = '/api/dashboard/biweekly-payroll';
      }
      
      if (endpoint) {
        const response = await fetch(`${API_URL}${endpoint}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDetailsData(data);
        } else {
          toast.error('Failed to fetch details');
          setShowDetailsDialog(false);
        }
      }
    } catch (error) {
      console.error('Dashboard card click error:', error);
      toast.error('Connection error');
      setShowDetailsDialog(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePersonalStatsClick = async (type) => {
    const token = localStorage.getItem('token');
    setPersonalStatsType(type);
    setShowPersonalStatsDialog(true);
    setPersonalStatsLoading(true);

    // Bi-weekly cards need a 14-day window; weekly stays at 7 days.
    const isBiweekly = type === 'biweekly-hours' || type === 'biweekly-payroll';
    const qs = isBiweekly ? '?weeks=2' : '';
    try {
      const response = await fetch(`${API_URL}/api/time/personal-weekly-breakdown${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setPersonalStatsData(await response.json());
      }
    } catch (error) {
      console.error('Personal stats error:', error);
    } finally {
      setPersonalStatsLoading(false);
    }
  };

  const handleSubscription = async (planType) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_type: planType,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const fetchCompanySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      // Use /api/company/branding for all users (accessible by everyone)
      const brandingResponse = await fetch(`${API_URL}/api/company/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (brandingResponse.ok) {
        const branding = await brandingResponse.json();
        
        // Use base64 logo if available (persists across deployments), otherwise construct URL
        let logoUrl = branding.logo_base64 || '';
        if (!logoUrl && branding.logo_url) {
          logoUrl = branding.logo_url;
          if (logoUrl.startsWith('/uploads/') || logoUrl.startsWith('/api/uploads/')) {
            logoUrl = `${API_URL}${logoUrl}`;
          }
        }
        
        setCompanySettings({
          name: branding.name || '',
          tagline: branding.tagline || '',
          logo_url: logoUrl,
          logo_base64: branding.logo_base64 || '',
          name_animation: branding.name_animation || 'none',
          logo_animation: branding.logo_animation || 'none',
          name_color: branding.name_color || '#1e293b',
          name_font: branding.name_font || 'Inter'
        });
      }
      
      // Try to get full settings (only admins/managers can access this)
      const settingsResponse = await fetch(`${API_URL}/api/company/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        setBiWeeklyStartDate(settings.bi_weekly_start_date || '');
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const handleSaveCompanySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: companySettings.name,
          tagline: companySettings.tagline,
          logo_url: companySettings.logo_url,
          name_animation: companySettings.name_animation,
          logo_animation: companySettings.logo_animation,
          name_color: companySettings.name_color,
          name_font: companySettings.name_font,
          bi_weekly_start_date: biWeeklyStartDate
        })
      });

      if (response.ok) {
        toast.success('Company settings saved successfully');
        setShowCompanySettingsDialog(false);
        fetchCompanySettings(); // Refresh company settings to update header
        fetchAllData(); // Refresh dashboard stats
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getSchedulesForDate = (dateStr) => {
    // Group schedules by project and combine team members
    const dateSchedules = schedules.filter(schedule => {
      // Handle timezone-safe date comparison
      const scheduleDate = schedule.date.split('T')[0];
      return scheduleDate === dateStr;
    });
    
    // Group by project_id to combine schedules for same project on same day
    const groupedByProject = {};
    dateSchedules.forEach(schedule => {
      const key = schedule.project_id || schedule.project_name;
      if (!groupedByProject[key]) {
        groupedByProject[key] = {
          ...schedule,
          team_details: [...(schedule.team_details || [])],
          time_slots: schedule.start_time && schedule.end_time ? 
            [`${schedule.start_time} - ${schedule.end_time}`] : []
        };
      } else {
        // Merge team members (avoid duplicates)
        const existingIds = new Set(groupedByProject[key].team_details.map(t => t.user_id));
        (schedule.team_details || []).forEach(tm => {
          if (!existingIds.has(tm.user_id)) {
            groupedByProject[key].team_details.push(tm);
            existingIds.add(tm.user_id);
          }
        });
        // Add time slot if different
        if (schedule.start_time && schedule.end_time) {
          const slot = `${schedule.start_time} - ${schedule.end_time}`;
          if (!groupedByProject[key].time_slots.includes(slot)) {
            groupedByProject[key].time_slots.push(slot);
          }
        }
      }
    });
    
    return Object.values(groupedByProject);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
    // Fetch schedules for new month
    const startDate = new Date(newMonth.getFullYear(), newMonth.getMonth(), 1).toISOString().split('T')[0];
    const endDate = new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0).toISOString().split('T')[0];
    fetchSchedules(startDate, endDate);
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newWeekStart);
    // Fetch schedules for new week
    const weekEnd = new Date(newWeekStart);
    weekEnd.setDate(newWeekStart.getDate() + 6);
    fetchSchedules(newWeekStart.toISOString().split('T')[0], weekEnd.toISOString().split('T')[0]);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const renderMonthlyCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const daySchedules = getSchedulesForDate(dateStr);
      const isToday = dateStr === new Date().toISOString().split('T')[0];

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${daySchedules.length > 0 ? 'has-events' : ''}`}
          onClick={() => {
            setSelectedDate(dateStr);
            setScheduleForm({...scheduleForm, date: dateStr});
            setShowScheduleDialog(true);
          }}
        >
          <div className="day-number">{day}</div>
          {daySchedules.length > 0 && (
            <div className="schedule-indicators">
              {daySchedules.slice(0, 3).map((schedule, idx) => (
                <div key={idx} className="schedule-dot" title={schedule.project_name}>
                  {schedule.project_name.substring(0, 10)}...
                </div>
              ))}
              {daySchedules.length > 3 && (
                <div className="schedule-more">+{daySchedules.length - 3} more</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>Previous</Button>
          <h3 className="text-xl font-semibold">{monthName}</h3>
          <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>Next</Button>
        </div>
        <div className="calendar-grid-monthly">
          {dayNames.map(name => (
            <div key={name} className="calendar-day-name">{name}</div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  const renderWeeklyCalendar = () => {
    const weekDays = getWeekDays();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const weekStart = weekDays[0].toLocaleDateString('default', { month: 'short', day: 'numeric' });
    const weekEnd = weekDays[6].toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>Previous Week</Button>
          <h3 className="text-xl font-semibold">{weekStart} - {weekEnd}</h3>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>Next Week</Button>
        </div>
        <div className="calendar-grid-weekly">
          {weekDays.map((day, idx) => {
            const dateStr = toLocalDateString(day);
            const daySchedules = getSchedulesForDate(dateStr);
            const isToday = dateStr === toLocalDateString(new Date());

            return (
              <div key={idx} className={`weekly-day-column ${isToday ? 'today' : ''}`}>
                <div className="weekly-day-header">
                  <div className="day-name">{dayNames[idx]}</div>
                  <div className="day-date">{day.getDate()} {day.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                </div>
                <div className="weekly-day-content">
                  {daySchedules.map((schedule, schedIdx) => (
                    <div
                      key={schedIdx}
                      className="weekly-schedule-item cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={() => {
                        setSelectedScheduleDetails(schedule);
                        setShowScheduleDetailsDialog(true);
                      }}
                      title={schedule.team_details?.map(m => m.name).join(', ') || 'No members assigned'}
                    >
                      <div className="font-semibold text-sm">{schedule.project_name}</div>
                      <div className="text-xs text-gray-600">
                        {schedule.team_details?.length || 0} members
                      </div>
                      {schedule.time_slots && schedule.time_slots.length > 0 && (
                        <div className="text-xs text-blue-600">
                          {schedule.time_slots.join(', ')}
                        </div>
                      )}
                      {schedule.is_recurring && (
                        <Badge variant="secondary" className="text-xs mt-1">
                          {schedule.recurring_pattern}
                        </Badge>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => {
                      setSelectedDate(dateStr);
                      setScheduleForm({...scheduleForm, date: dateStr});
                      setShowScheduleDialog(true);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard modern-bg" data-testid="admin-dashboard">
      {/* Company Branding Header */}
      <div className="company-header">
        <div className="company-branding">
          <div className={`company-logo-circle logo-animation-${companySettings.logo_animation || 'none'}`}>
            {companySettings.logo_url ? (
              <img 
                src={companySettings.logo_url} 
                alt="Company logo" 
                className="company-logo-img" 
                onError={(e) => {
                  console.error('Logo failed to load:', companySettings.logo_url);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <span className="company-initials">
                {companySettings.name ? companySettings.name.substring(0, 2).toUpperCase() : 'MI'}
              </span>
            )}
          </div>
          <div className="company-info">
            <h2 
              className={`company-name name-animation-${companySettings.name_animation || 'none'}`}
              style={{ 
                color: companySettings.name_color || '#1e293b',
                fontFamily: companySettings.name_font || 'Inter',
                whiteSpace: 'nowrap'
              }}
            >
              {companySettings.name || 'Mechanical Insulation Co.'}
            </h2>
            <p className="company-tagline">
              {companySettings.tagline || 'Admin Control Center'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setShowHelpCenter(true)}
            className="text-gray-600 hover:text-blue-600"
            data-testid="help-btn"
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={async () => {
              toast.info('Updating app...');
              await forceAppUpdate();
            }}
            className="text-gray-600 hover:text-green-600"
            title="Update App"
            data-testid="update-app-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <NotificationCenter />
          <PushNotificationManager authToken={localStorage.getItem('token')} compact user={user} />
          <Button onClick={handleLogout} variant="outline" className="logout-btn" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* PWA Install Banner */}
      <PWAInstallBanner user={user} />

      {/* iOS Install Instructions Banner */}
      <IOSInstallBanner />

      {/* Personalized Dashboard Header */}
      <header className="dashboard-header-modern">
        <div>
          <h1 className="personalized-title" data-testid="dashboard-title">
            {user.name}'s Control Center
          </h1>
          <p className="welcome-text">
            Manage your workforce, projects, and operations.
            <Badge variant="secondary" className="ml-3">
              {getCurrentPlanName(user)} Plan
            </Badge>
          </p>
        </div>
        <div className="header-actions">
          <Button
            variant="outline"
            size="sm"
            className="action-btn-small"
            onClick={() => setShowMyProfileDialog(true)}
            data-testid="my-profile-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            My Profile
          </Button>
        </div>
      </header>

      {isMobile && !onHomeMobile && (
        <div className="px-3 pt-3 pb-1 sticky top-0 z-30 bg-white/95 backdrop-blur border-b">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setActiveTab('__home__')}
            data-testid="mobile-back-to-dashboard"
          >
            ← Dashboard
          </Button>
        </div>
      )}

      {(onHomeMobile || !isMobile) && (
        <>
      <div className="dashboard-overview" data-testid="dashboard-overview">
        {/* Your Personal Stats - Green Theme
            On phones the inline panel is hidden — users open it as a
            dedicated page from the tile grid below. */}
        {!['purchaser'].includes(user.role) && (!isMobile || showPersonalStatsSection) && (
          <div style={{ 
            border: '2px solid #16a34a', borderRadius: '12px', padding: '16px',
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', gridColumn: 'span 2'
          }}>
            <h3 style={{ color: '#16a34a', fontWeight: 700, marginBottom: '12px', fontSize: '1rem' }}>
              👤 Your Personal Stats
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="stat-card clickable" onClick={() => handlePersonalStatsClick('weekly-hours')}>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-500">Weekly Hours</p>
                  <p className="text-lg font-bold text-green-600">{dashboardStats.userWeeklyHours}</p>
                </CardContent>
              </Card>
              <Card className="stat-card clickable" onClick={() => handlePersonalStatsClick('biweekly-hours')}>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-500">Bi-Weekly Hours</p>
                  <p className="text-lg font-bold text-green-600">{dashboardStats.userBiWeeklyHours}</p>
                </CardContent>
              </Card>
              <Card className="stat-card clickable" onClick={() => handlePersonalStatsClick('biweekly-pay')}>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-500">Bi-Weekly Pay</p>
                  <p className="text-lg font-bold text-green-600">${dashboardStats.userBiWeeklyPay.toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Company Total Stats - Blue Theme — mobile gated, same as above */}
        {!['purchaser'].includes(user.role) && (!isMobile || showCompanyStatsSection) && (
          <div style={{ 
            border: '2px solid #2563eb', borderRadius: '12px', padding: '16px',
            background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', gridColumn: 'span 2'
          }}>
            <h3 style={{ color: '#2563eb', fontWeight: 700, marginBottom: '12px', fontSize: '1rem' }}>
              🏢 Company Total Stats
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="stat-card clickable" onClick={() => handleDashboardCardClick('weekly-hours')} data-testid="company-weekly-hours-card">
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-500">Company Weekly Hours</p>
                  <p className="text-lg font-bold text-blue-600">{dashboardStats.weeklyHours}</p>
                </CardContent>
              </Card>
              <Card className="stat-card clickable" onClick={() => handleDashboardCardClick('biweekly-hours')} data-testid="company-bi-weekly-hours-card">
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-500">Company Bi-Weekly Hours</p>
                  <p className="text-lg font-bold text-blue-600">{dashboardStats.biWeeklyHours}</p>
                </CardContent>
              </Card>
              <Card className="stat-card clickable" onClick={() => handleDashboardCardClick('biweekly-payroll')} data-testid="company-bi-weekly-payroll-card">
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-500">Company Bi-Weekly Payroll</p>
                  <p className="text-lg font-bold text-blue-600">${dashboardStats.biWeeklyPay.toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <Card 
          className={`stat-card clickable ${isMobile ? 'hidden' : ''}`}
          data-testid="active-projects-card"
          onClick={() => {
            setDetailsType('projects');
            setDetailsData(projects.filter(p => p.is_active && p.status !== 'completed'));
            setShowDetailsDialog(true);
          }}
        >
          <CardHeader>
            <CardTitle className="stat-title">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="stat-value">{dashboardStats.activeProjects}</div>
            <p className="stat-label">Currently Running</p>
          </CardContent>
        </Card>

        {!isMobile && !['purchaser'].includes(user.role) && (
          <Card 
            className="stat-card clickable" 
            data-testid="clocked-in-card"
            onClick={() => handleDashboardCardClick('clocked-in')}
          >
            <CardHeader>
              <CardTitle className="stat-title">Clocked In Now</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value">{dashboardStats.clockedInMembers}</div>
              <p className="stat-label">Team Members Working</p>
            </CardContent>
          </Card>
        )}

        {/* Clock In/Out Card for All Staff — compact on mobile */}
        <ClockInCard
          isClockedIn={isClockedIn}
          clockInTime={clockInTime}
          todayHours={todayHours}
          onClockIn={handleClockIn}
          onClockOut={handleClockOut}
          compact={isMobile}
        />

        {!isMobile && (
          <Card 
            className="stat-card clickable" 
            data-testid="open-orders-card"
            onClick={() => {
              setDetailsType('open-orders');
              setDetailsLoading(false);
              setShowDetailsDialog(true);
            }}
          >
            <CardHeader>
              <CardTitle className="stat-title">Open Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value">
                {materialOrders.filter(o => o.status !== 'complete').length}
              </div>
              <p className="stat-label">
                Pending & In Progress
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mobile stats "page" back button — visible only when one of the
          stat sections has been opened from the tile grid. */}
      {isMobile && (showPersonalStatsSection || showCompanyStatsSection) && (
        <div className="flex justify-end mb-2 px-3 mt-3">
          <button
            onClick={() => { setShowPersonalStatsSection(false); setShowCompanyStatsSection(false); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-semibold rounded-full border border-purple-200"
            data-testid="accounts-stats-back-btn"
          >
            ← Back to menu
          </button>
        </div>
      )}

      {/* Mobile Tile Grid — shortcuts into each tab + Quick Builder.
          Hidden when the user is looking at a stats sub-page. */}
      {isMobile && !showPersonalStatsSection && !showCompanyStatsSection && (
        <div className="mt-4 mb-4">
          <RoleTileGrid
            role={user.role}
            tabs={[
              'schedule', 'tasks', 'orders', 'subcontractors', 'view-projects',
              'view-employees', 'view-clients', 'stock', 'time-tracking',
              'communication', 'expenses', 'exports', 'employee-database',
              'invoices', 'reconciliation', 'returns', 'transfers',
            ]}
            badges={{
              orders: (materialOrders || []).filter((o) => o.status === 'pending').length || null,
            }}
            clockStatusLabel={isClockedIn ? 'Clocked In' : 'Clocked Out'}
            onNavigate={(t) => setActiveTab(t)}
            onOpenScheduleBuilder={() => setShowQuickBuilder(true)}
            onOpenClockStatus={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            onOpenProfile={() => setShowChangePasswordDialog(true)}
            onOpenPersonalStats={
              !['purchaser'].includes(user.role)
                ? () => { setShowPersonalStatsSection(true); setShowCompanyStatsSection(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }
                : undefined
            }
            onOpenCompanyStats={
              !['purchaser'].includes(user.role)
                ? () => { setShowCompanyStatsSection(true); setShowPersonalStatsSection(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }
                : undefined
            }
            personalStatsHint={`${dashboardStats.userWeeklyHours || 0} hrs this week`}
            companyStatsHint={`${dashboardStats.weeklyHours || 0} hrs this week`}
          />
        </div>
      )}
      </>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="dashboard-tabs">
        <div
          style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '20px' }}
          className={isMobile ? 'hidden' : ''}
        >
          <TabsList data-testid="tabs-list" className="tabs-list" style={{ display: 'inline-flex', minWidth: 'max-content', width: 'auto' }}>
            {/* Show for Admin, General Manager, and Accounts roles */}
            {!['purchaser'].includes(user.role) && (
              <>
              <TabsTrigger value="schedule" data-testid="schedule-tab">
                <Clock className="w-4 h-4 mr-2" />
                Schedule
              </TabsTrigger>
            </>
          )}

          {/* Task Management tab */}
          {!['purchaser'].includes(user.role) && (
            <TabsTrigger value="tasks" data-testid="tasks-tab">
              <CheckSquare className="w-4 h-4 mr-2" />
              Tasks
            </TabsTrigger>
          )}

          {(['purchaser', 'accounts', 'accounts_purchaser', 'admin', 'company_admin', 'director', 'project_manager', 'general_manager'].includes(user.role)) && (
            <TabsTrigger value="orders" data-testid="orders-tab" disabled={!hasFeature(user, 'material_ordering')}>
              <Package className="w-4 h-4 mr-2" />
              Material Orders
              {!hasFeature(user, 'material_ordering') && <span className="ml-1">🔒</span>}
            </TabsTrigger>
          )}

          {!['purchaser'].includes(user.role) && (
              <>
              <TabsTrigger value="projects" data-testid="projects-tab">
                <Calendar className="w-4 h-4 mr-2" />
                Projects
              </TabsTrigger>
              <TabsTrigger value="subcontractors" data-testid="subcontractors-tab">
                <Users className="w-4 h-4 mr-2" />
                Subcontractors
              </TabsTrigger>
              <TabsTrigger value="employees" data-testid="employees-tab">
                <Users className="w-4 h-4 mr-2" />
                Employees
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="clients-tab">
                <Users className="w-4 h-4 mr-2" />
                Clients
              </TabsTrigger>
            </>
          )}

          {/* Equipment Management */}
          {['company_admin', 'general_manager', 'manager'].includes(user.role) && (
            <TabsTrigger value="equipment" data-testid="equipment-tab" disabled={!hasFeature(user, 'equipment_management')}>
              <Package className="w-4 h-4 mr-2" />
              Equipment
              {!hasFeature(user, 'equipment_management') && <span className="ml-1">🔒</span>}
            </TabsTrigger>
          )}
          
          {/* Show for all roles (including purchaser) */}
          <TabsTrigger value="stock" data-testid="stock-tab">
            <Package className="w-4 h-4 mr-2" />
            Stock Material
          </TabsTrigger>

          {/* Show Time Tracking tab only for purchaser roles */}
          {['purchaser', 'accounts_purchaser'].includes(user.role) && (
            <>
              <TabsTrigger value="time-tracking" data-testid="time-tracking-tab">
                <Clock className="w-4 h-4 mr-2" />
                Time Tracking
              </TabsTrigger>
              <TabsTrigger value="view-clients" data-testid="view-clients-tab">
                <Users className="w-4 h-4 mr-2" />
                Clients
              </TabsTrigger>
              <TabsTrigger value="view-projects" data-testid="view-projects-tab">
                <Calendar className="w-4 h-4 mr-2" />
                Projects
              </TabsTrigger>
              <TabsTrigger value="view-employees" data-testid="view-employees-tab">
                <Users className="w-4 h-4 mr-2" />
                Employees
              </TabsTrigger>
            </>
          )}
          
          {/* Communication tab - Available for all roles including purchasers */}
          <TabsTrigger value="communication" data-testid="communication-tab">
            <MessageSquare className="w-4 h-4 mr-2" />
            Project Messages
          </TabsTrigger>

          {/* Show Expenses for Admin, Director, General Manager */}
          {!['purchaser', 'accounts_purchaser'].includes(user.role) && (
            <TabsTrigger value="expenses" data-testid="expenses-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Expenses
            </TabsTrigger>
          )}

          {/* Show Excel Exports for company_admin, accounts, and accounts_purchaser */}
          {(['company_admin', 'accounts', 'accounts_purchaser', 'admin'].includes(user.role)) && (
            <TabsTrigger value="exports" data-testid="exports-tab">
              <Package className="w-4 h-4 mr-2" />
              Excel Exports
            </TabsTrigger>
          )}

          {/* Invoice Management - Behind invoice_ocr feature flag, for Accounts roles */}
          {(['company_admin', 'accounts', 'accounts_purchaser', 'admin'].includes(user.role)) && hasFeature(user, 'invoice_ocr') && (
            <TabsTrigger value="invoices" data-testid="invoices-tab">
              <Receipt className="w-4 h-4 mr-2" />
              Invoice Management
            </TabsTrigger>
          )}

          {/* Employee Database - Company Admin, General Manager, Manager, Accounts */}
          {['company_admin', 'general_manager', 'manager', 'accounts'].includes(user.role) && (
            <TabsTrigger value="employee-database" data-testid="employee-database-tab">
              <Users className="w-4 h-4 mr-2" />
              Employee Database
            </TabsTrigger>
          )}

          {/* Material Returns & Transfers - For Purchasers & Managers */}
          {(['purchaser', 'accounts_purchaser', 'admin', 'company_admin', 'project_manager', 'general_manager'].includes(user.role)) && (
            <>
              <TabsTrigger value="returns" data-testid="returns-tab" disabled={!hasFeature(user, 'material_returns')}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Return Requests
                {!hasFeature(user, 'material_returns') && <span className="ml-1">🔒</span>}
              </TabsTrigger>
              <TabsTrigger value="transfers" data-testid="transfers-tab" disabled={!hasFeature(user, 'material_transfers')}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Transfer Requests
                {!hasFeature(user, 'material_transfers') && <span className="ml-1">🔒</span>}
              </TabsTrigger>
              <TabsTrigger value="notification-settings" data-testid="notification-settings-tab">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </TabsTrigger>
            </>
          )}
        </TabsList>
        </div>

        {/* Clients Tab - For Admin, Manager, Accounts roles */}
        {!['purchaser'].includes(user.role) && (
        <TabsContent value="clients" data-testid="clients-content">
          <ClientManagement />
        </TabsContent>
        )}

        {/* Employees Tab - For Admin, Manager, Accounts roles */}
        {!['purchaser'].includes(user.role) && (
        <TabsContent value="employees" data-testid="employees-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <CardTitle>Employees</CardTitle>
                  <div className="relative">
                    <Input
                      placeholder="Search by name, email, position..."
                      value={employeeSearchQuery}
                      onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                      className="w-64 pl-8"
                      data-testid="employee-search-input"
                    />
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  </div>
                  {employeeSearchQuery && (
                    <span className="text-sm text-gray-500">
                      {filteredEmployees.length} of {employees.length} employees
                    </span>
                  )}
                </div>
                <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-employee-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Employee
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-employee-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Employee</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddEmployee} className="form-grid">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={employeeForm.name}
                          onChange={(e) => {
                            const name = e.target.value;
                            const firstName = name.split(' ')[0];
                            const autoPassword = firstName ? `${firstName}123` : '';
                            setEmployeeForm({ 
                              ...employeeForm, 
                              name: name,
                              password: autoPassword
                            });
                          }}
                          required
                          data-testid="employee-name-input"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={employeeForm.email}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                          required
                          data-testid="employee-email-input"
                        />
                      </div>
                      <div>
                        <Label>Position</Label>
                        <Input
                          value={employeeForm.position}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                          required
                          data-testid="employee-position-input"
                        />
                      </div>
                      <div>
                        <Label>Hourly Rate</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={employeeForm.hourly_rate}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, hourly_rate: e.target.value })}
                          data-testid="employee-rate-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Login Password</Label>
                        <Input
                          type="text"
                          value={employeeForm.password}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                          required
                          placeholder="Auto-generated from name"
                          data-testid="employee-password-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Default: FirstName123 (e.g., John123). You can edit this.
                        </p>
                      </div>
                      <div className="col-span-2">
                        <Label>Role</Label>
                        <Select 
                          value={employeeForm.role} 
                          onValueChange={(val) => setEmployeeForm({ ...employeeForm, role: val })}
                        >
                          <SelectTrigger data-testid="employee-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general_manager">General Manager (1 per company)</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="project_manager">Project Manager</SelectItem>
                            <SelectItem value="accounts">Accounts</SelectItem>
                            <SelectItem value="purchaser">Purchaser</SelectItem>
                            <SelectItem value="accounts_purchaser">Accounts + Purchaser</SelectItem>
                            <SelectItem value="team_lead">Team Lead</SelectItem>
                            <SelectItem value="project_coordinator">Project Coordinator</SelectItem>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="subcontractor">Subcontractor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Employee Type</Label>
                        <Select 
                          value={employeeForm.employee_type} 
                          onValueChange={(val) => setEmployeeForm({ ...employeeForm, employee_type: val })}
                        >
                          <SelectTrigger data-testid="new-employee-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="field_based">
                              <div className="flex items-center gap-2">
                                <span>🏗️</span>
                                <span>Field-Based (requires work progress on clock-out)</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="office_based">
                              <div className="flex items-center gap-2">
                                <span>🏢</span>
                                <span>Office-Based (no work progress required)</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Subcontractor Account</Label>
                        <Select 
                          value={employeeForm.subcontractor_account_id || 'direct_hire'} 
                          onValueChange={(val) => setEmployeeForm({ ...employeeForm, subcontractor_account_id: val === 'direct_hire' ? '' : val })}
                        >
                          <SelectTrigger data-testid="new-employee-account-select">
                            <SelectValue placeholder="Select account..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="direct_hire">
                              <div className="flex items-center gap-2">
                                <span>🏠</span>
                                <span>Direct Hire (No Subcontractor)</span>
                              </div>
                            </SelectItem>
                            {subcontractorAccounts.filter(a => a.is_active !== false).map(account => (
                              <SelectItem key={account.id} value={account.id}>
                                <div className="flex items-center gap-2">
                                  <span>🏢</span>
                                  <span>{account.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="col-span-2" disabled={loading} data-testid="submit-employee-btn">
                        Add Employee
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-container-with-sticky-scroll">
                <div className="table-responsive" ref={employeesTableRef}>
                  <table className="data-table" data-testid="employees-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Position</th>
                      <th>Account</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>Hourly Rate</th>
                      <th>Password</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp, index) => (
                      <tr key={emp.id} data-testid={`employee-row-${index}`} className={emp.user?.is_active === false ? 'opacity-60 bg-red-50' : ''}>
                        <td>
                          <div className="flex items-center gap-2">
                            {emp.user?.name}
                            {emp.user?.is_active === false && (
                              <Badge variant="destructive" className="text-xs px-1.5 py-0">Paused</Badge>
                            )}
                          </div>
                        </td>
                        <td>{emp.user?.email}</td>
                        <td>{emp.position}</td>
                        <td>
                          <Badge 
                            variant={emp.account_name === 'Direct Hire' ? 'outline' : 'secondary'}
                            className={emp.account_name === 'Direct Hire' ? 'bg-gray-100 text-gray-700' : 'bg-purple-100 text-purple-800'}
                          >
                            {emp.account_name || 'Direct Hire'}
                          </Badge>
                        </td>
                        <td>
                          <Badge 
                            variant={emp.employee_type === 'office_based' ? 'secondary' : 'default'}
                            className={emp.employee_type === 'office_based' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}
                          >
                            {emp.employee_type === 'office_based' ? '🏢 Office' : '🏗️ Field'}
                          </Badge>
                        </td>
                        <td className="capitalize">{emp.user?.role?.replace('_', ' ')}</td>
                        <td>${emp.hourly_rate || 0}/hr</td>
                        <td>
                          {user.role === 'company_admin' ? (
                            <Select
                              value=""
                              onValueChange={async (value) => {
                                const token = localStorage.getItem('token');
                                
                                if (value === 'reset') {
                                  // Reset password - only company admin can do this
                                  if (!confirm(`Reset password for ${emp.user?.name}?\n\nA new default password will be generated.`)) {
                                    return;
                                  }
                                  
                                  try {
                                    const response = await fetch(`${API_URL}/api/users/${emp.user_id}/reset-password`, {
                                      method: 'PUT',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });
                                    if (response.ok) {
                                      const data = await response.json();
                                      alert(`Password reset successful!\n\nNew Password: ${data.new_password}\n\nPlease share this with the employee securely.`);
                                      toast.success(`Password reset completed`);
                                      fetchAllData();
                                    } else {
                                      toast.error('Failed to reset password');
                                    }
                                  } catch (error) {
                                    toast.error('Connection error');
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="w-[180px] h-9">
                                <SelectValue placeholder="••••••" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="reset">🔄 Request Password Reset</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-sm text-gray-500 italic">Contact company admin</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            {emp.user && ['project_manager', 'team_lead', 'project_coordinator'].includes(emp.user.role) && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleOpenAssignDialog(emp)}
                              >
                                Assign
                              </Button>
                            )}
                            {['company_admin', 'general_manager', 'manager', 'accounts', 'accounts_purchaser'].includes(user.role) && (
                              <Select onValueChange={(action) => {
                                if (action === 'edit') handleOpenEditEmployee(emp);
                                else if (action === 'role') handleOpenChangeRole(emp);
                                else if (action === 'access') handleOpenAdditionalAccess(emp);
                                else if (action === 'toggle') handleToggleEmployeeStatus(emp);
                                else if (action === 'delete') handleDeleteEmployee(emp);
                                else if (action === 'time-reset') handleOpenTimeReset(emp);
                                else if (action === 'time-adjust') handleOpenTimeAdjust(emp);
                                else if (action === 'time-history') handleViewAdjustmentsHistory(emp);
                              }}>
                                <SelectTrigger className="w-[140px] h-9">
                                  <SelectValue placeholder="Manage..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {/* Edit - All admin roles except company_admin employees */}
                                  {emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="edit">✏️ Edit Details</SelectItem>
                                  )}
                                  {/* Change Role - For company_admin, general_manager, manager, accounts, accounts_purchaser */}
                                  {['company_admin', 'general_manager', 'manager', 'accounts', 'accounts_purchaser'].includes(user.role) && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="role">🔄 Change Role</SelectItem>
                                  )}
                                  {/* Manage Additional Access - Only for company_admin */}
                                  {user.role === 'company_admin' && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="access">🔑 Manage Access</SelectItem>
                                  )}
                                  {/* Time Reset - Only for company_admin */}
                                  {user.role === 'company_admin' && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="time-reset">🔄 Reset Hours</SelectItem>
                                  )}
                                  {/* Time Adjust - For accounts roles */}
                                  {['accounts', 'accounts_purchaser', 'company_admin'].includes(user.role) && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="time-adjust">⏱️ Adjust Hours</SelectItem>
                                  )}
                                  {/* View Time Adjustment History */}
                                  {['accounts', 'accounts_purchaser', 'company_admin', 'general_manager'].includes(user.role) && (
                                    <SelectItem value="time-history">📋 Hour Adjustments</SelectItem>
                                  )}
                                  {/* Pause/Activate - For company_admin, accounts, accounts_purchaser */}
                                  {['company_admin', 'accounts', 'accounts_purchaser'].includes(user.role) && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="toggle">
                                      {emp.user?.is_active ? '⏸️ Pause Account' : '▶️ Activate Account'}
                                    </SelectItem>
                                  )}
                                  {/* Delete - Only for company_admin, general_manager, manager */}
                                  {['company_admin', 'general_manager', 'manager'].includes(user.role) && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="delete">🗑️ Delete</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                
                {/* Sticky scrollbar at bottom of viewport */}
                {employeesScroll.showStickyScroll && (
                  <div className="sticky-scrollbar-container">
                    <div 
                      className="sticky-scrollbar-track" 
                      ref={employeesScroll.stickyScrollRef}
                    >
                      <div 
                        className="sticky-scrollbar-content"
                        style={{ width: employeesTableRef.current?.scrollWidth + 'px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Employee Dialog */}
          <Dialog open={showEditEmployeeDialog} onOpenChange={setShowEditEmployeeDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Employee Details</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditEmployee} className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editEmployeeForm.name}
                    onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Position</Label>
                  <Input
                    value={editEmployeeForm.position}
                    onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, position: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Hourly Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editEmployeeForm.hourly_rate}
                    onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, hourly_rate: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Subcontractor Account</Label>
                  <Select 
                    value={editEmployeeForm.subcontractor_account_id || 'direct_hire'} 
                    onValueChange={(val) => setEditEmployeeForm({ ...editEmployeeForm, subcontractor_account_id: val })}
                  >
                    <SelectTrigger data-testid="edit-employee-account-select">
                      <SelectValue placeholder="Select account..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct_hire">
                        <div className="flex items-center gap-2">
                          <span>🏠</span>
                          <span>Direct Hire (No Subcontractor)</span>
                        </div>
                      </SelectItem>
                      {subcontractorAccounts.filter(a => a.is_active !== false).map(account => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex items-center gap-2">
                            <span>🏢</span>
                            <span>{account.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employee Type</Label>
                  <Select 
                    value={editEmployeeForm.employee_type} 
                    onValueChange={(val) => setEditEmployeeForm({ ...editEmployeeForm, employee_type: val })}
                  >
                    <SelectTrigger data-testid="employee-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="field_based">
                        <div className="flex items-center gap-2">
                          <span>🏗️</span>
                          <span>Field-Based</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="office_based">
                        <div className="flex items-center gap-2">
                          <span>🏢</span>
                          <span>Office-Based</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editEmployeeForm.employee_type === 'office_based' 
                      ? 'Office-based employees do not need to submit work progress when clocking out.' 
                      : 'Field-based employees must submit work progress when clocking out.'}
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditEmployeeDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Change Role Dialog */}
          <Dialog open={showChangeRoleDialog} onOpenChange={setShowChangeRoleDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Employee Role</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Employee: {selectedEmployee?.user?.name}</Label>
                </div>
                <div>
                  <Label>New Role</Label>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general_manager">General Manager (1 per company)</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="purchaser">Purchaser</SelectItem>
                      <SelectItem value="accounts_purchaser">Accounts + Purchaser</SelectItem>
                      <SelectItem value="team_lead">Team Lead</SelectItem>
                      <SelectItem value="project_coordinator">Project Coordinator</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                      <SelectItem value="director">Director</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowChangeRoleDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleChangeRole} disabled={loading}>
                    Change Role
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Additional Access Dialog - Company Admin Only */}
          <Dialog open={showAdditionalAccessDialog} onOpenChange={setShowAdditionalAccessDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Manage Additional Access</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{selectedUserForAccess?.user?.name}</strong>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Primary Role: {selectedUserForAccess?.user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-3 block">Grant Additional Permissions:</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {AVAILABLE_PERMISSIONS.map(perm => (
                      <label 
                        key={perm.key} 
                        className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={additionalAccessForm.includes(perm.key)}
                          onChange={() => togglePermission(perm.key)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm">{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAdditionalAccessDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveAdditionalAccess} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Access'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Time Reset Dialog - Company Admin Only */}
          <Dialog open={showTimeResetDialog} onOpenChange={setShowTimeResetDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset Employee Hours</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">⚠️ Warning: This action cannot be undone!</p>
                  <p className="text-red-700 text-sm mt-2">
                    You are about to delete ALL time entries for <strong>{selectedEmployee?.user?.name}</strong>.
                    This will permanently remove their clock-in/out history.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowTimeResetDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleResetEmployeeTime} 
                    disabled={loading}
                    data-testid="confirm-time-reset-btn"
                  >
                    {loading ? 'Resetting...' : 'Reset All Hours'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Time Adjust Dialog - Accounts Role */}
          <Dialog open={showTimeAdjustDialog} onOpenChange={setShowTimeAdjustDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Adjust Employee Hours</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdjustEmployeeTime} className="space-y-4">
                <div>
                  <Label>Employee</Label>
                  <p className="text-lg font-medium">{selectedEmployee?.user?.name}</p>
                </div>
                
                {/* Day Selection - Last 30 Days */}
                <div>
                  <Label className="mb-2 block">Select Day to Adjust *</Label>
                  {loadingDailyHours ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500">Loading daily hours...</span>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      {employeeDailyHours.map((day) => (
                        <div 
                          key={day.date}
                          onClick={() => setTimeAdjustForm({ ...timeAdjustForm, adjustment_date: day.date })}
                          className={`flex items-center justify-between p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                            timeAdjustForm.adjustment_date === day.date ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          } ${day.is_today ? 'bg-green-50' : ''}`}
                          data-testid={`day-select-${day.date}`}
                        >
                          <div className="flex items-center gap-3">
                            <input 
                              type="radio" 
                              name="adjustment_date"
                              checked={timeAdjustForm.adjustment_date === day.date}
                              onChange={() => setTimeAdjustForm({ ...timeAdjustForm, adjustment_date: day.date })}
                              className="w-4 h-4 text-blue-600"
                            />
                            <div>
                              <p className="font-medium">
                                {day.day_name}
                                {day.is_today && <Badge variant="success" className="ml-2 text-xs">Today</Badge>}
                              </p>
                              <p className="text-sm text-gray-500">{formatDateDisplay(day.date)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${day.logged_hours > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              {day.logged_hours} hrs logged
                            </p>
                            {day.adjustment_total !== 0 && (
                              <p className={`text-xs ${day.adjustment_total > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                {day.adjustment_total > 0 ? '+' : ''}{day.adjustment_total} hrs adjusted
                              </p>
                            )}
                            {day.entry_count > 0 && (
                              <p className="text-xs text-gray-400">{day.entry_count} time entries</p>
                            )}
                          </div>
                        </div>
                      ))}
                      {employeeDailyHours.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No data available for the last 30 days</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="adjust-hours">Hours to Add/Subtract *</Label>
                  <Input
                    id="adjust-hours"
                    type="number"
                    step="0.25"
                    placeholder="e.g., 2.5 to add, -1.5 to subtract"
                    value={timeAdjustForm.hours}
                    onChange={(e) => setTimeAdjustForm({ ...timeAdjustForm, hours: e.target.value })}
                    required
                    data-testid="adjust-hours-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Use positive numbers to add hours, negative to subtract
                  </p>
                </div>
                <div>
                  <Label htmlFor="adjust-reason">Reason *</Label>
                  <Textarea
                    id="adjust-reason"
                    placeholder="Explain why this adjustment is being made..."
                    value={timeAdjustForm.reason}
                    onChange={(e) => setTimeAdjustForm({ ...timeAdjustForm, reason: e.target.value })}
                    required
                    minLength={3}
                    data-testid="adjust-reason-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowTimeAdjustDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading || !timeAdjustForm.adjustment_date || !timeAdjustForm.hours || !timeAdjustForm.reason}
                    data-testid="submit-time-adjust-btn"
                  >
                    {loading ? 'Saving...' : 'Save Adjustment'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Time Adjustments History Dialog */}
          <Dialog open={showAdjustmentsHistory} onOpenChange={setShowAdjustmentsHistory}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Hour Adjustments History - {selectedEmployee?.user?.name}</DialogTitle>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto">
                {timeAdjustments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No adjustments recorded for this employee</p>
                ) : (
                  <div className="space-y-3">
                    {timeAdjustments.map((adj) => (
                      <div key={adj.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className={`font-semibold ${adj.adjustment_hours > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {adj.adjustment_hours > 0 ? '+' : ''}{adj.adjustment_hours} hours
                            </span>
                            {adj.adjustment_date && (
                              <Badge variant="outline" className="ml-2">
                                {formatDateDisplay(adj.adjustment_date)}
                              </Badge>
                            )}
                            <p className="text-sm text-gray-600 mt-1">{adj.reason}</p>
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            <p>By: {adj.adjusted_by_name}</p>
                            <p>{new Date(adj.created_at).toLocaleDateString()} {new Date(adj.created_at).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Subcontractors Tab - Company Admin and Accounts roles */}
        {['company_admin', 'general_manager', 'manager', 'accounts', 'accounts_purchaser'].includes(user.role) && (
        <TabsContent value="subcontractors" data-testid="subcontractors-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <CardTitle>Subcontractor Accounts</CardTitle>
                  <div className="relative">
                    <Input
                      placeholder="Search by name, contact..."
                      value={subcontractorSearchQuery}
                      onChange={(e) => setSubcontractorSearchQuery(e.target.value)}
                      className="w-64 pl-8"
                      data-testid="subcontractor-search-input"
                    />
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  </div>
                  {subcontractorSearchQuery && (
                    <span className="text-sm text-gray-500">
                      {filteredSubcontractors.length} of {subcontractorAccounts.length} subcontractors
                    </span>
                  )}
                </div>
                <Dialog open={showSubcontractorDialog} onOpenChange={(open) => {
                  setShowSubcontractorDialog(open);
                  if (!open) {
                    setEditingSubcontractor(null);
                    setSubcontractorForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-subcontractor-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Subcontractor
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingSubcontractor ? 'Edit Subcontractor' : 'Add Subcontractor Account'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const token = localStorage.getItem('token');
                      setLoading(true);
                      try {
                        const url = editingSubcontractor 
                          ? `${API_URL}/api/subcontractor-accounts/${editingSubcontractor.id}`
                          : `${API_URL}/api/subcontractor-accounts`;
                        const method = editingSubcontractor ? 'PUT' : 'POST';
                        
                        const response = await fetch(url, {
                          method,
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify(subcontractorForm)
                        });
                        
                        if (response.ok) {
                          toast.success(editingSubcontractor ? 'Subcontractor updated' : 'Subcontractor added');
                          setShowSubcontractorDialog(false);
                          setEditingSubcontractor(null);
                          setSubcontractorForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: '' });
                          fetchAllData();
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || 'Failed to save subcontractor');
                        }
                      } catch (error) {
                        toast.error('Connection error');
                      } finally {
                        setLoading(false);
                      }
                    }} className="space-y-4">
                      <div>
                        <Label>Company Name *</Label>
                        <Input
                          value={subcontractorForm.name}
                          onChange={(e) => setSubcontractorForm({ ...subcontractorForm, name: e.target.value })}
                          placeholder="e.g., ABC Contracting Inc."
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Name</Label>
                          <Input
                            value={subcontractorForm.contact_name}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, contact_name: e.target.value })}
                            placeholder="John Smith"
                          />
                        </div>
                        <div>
                          <Label>Contact Email</Label>
                          <Input
                            type="email"
                            value={subcontractorForm.contact_email}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, contact_email: e.target.value })}
                            placeholder="contact@example.com"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Phone</Label>
                          <Input
                            value={subcontractorForm.contact_phone}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, contact_phone: e.target.value })}
                            placeholder="416-555-1234"
                          />
                        </div>
                        <div>
                          <Label>HST Number</Label>
                          <Input
                            value={subcontractorForm.hst_number}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, hst_number: e.target.value })}
                            placeholder="123456789RT0001"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input
                          value={subcontractorForm.address}
                          onChange={(e) => setSubcontractorForm({ ...subcontractorForm, address: e.target.value })}
                          placeholder="123 Main St, Toronto, ON"
                        />
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <textarea
                          className="w-full border rounded-md p-2 text-sm"
                          rows={2}
                          value={subcontractorForm.notes}
                          onChange={(e) => setSubcontractorForm({ ...subcontractorForm, notes: e.target.value })}
                          placeholder="Additional notes..."
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => {
                          setShowSubcontractorDialog(false);
                          setEditingSubcontractor(null);
                          setSubcontractorForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: '' });
                        }}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                          {editingSubcontractor ? 'Update' : 'Add'} Subcontractor
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Manage subcontractor accounts. Employees can be assigned to a subcontractor for payroll tracking.
              </p>
              {subcontractorAccounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No subcontractor accounts yet</p>
                  <p className="text-sm">Add a subcontractor account to assign employees for payment tracking</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="data-table" data-testid="subcontractors-table">
                    <thead>
                      <tr>
                        <th>Company Name</th>
                        <th>Contact</th>
                        <th>Phone</th>
                        <th>HST Number</th>
                        <th>Employees</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubcontractors.map((account, index) => (
                        <tr key={account.id} data-testid={`subcontractor-row-${index}`}>
                          <td className="font-medium">{account.name}</td>
                          <td>
                            {account.contact_name && <div>{account.contact_name}</div>}
                            {account.contact_email && <div className="text-sm text-gray-500">{account.contact_email}</div>}
                          </td>
                          <td>{account.contact_phone || '-'}</td>
                          <td>{account.hst_number || '-'}</td>
                          <td>
                            <Badge variant="secondary">
                              {account.employee_count || 0} employee{(account.employee_count || 0) !== 1 ? 's' : ''}
                            </Badge>
                          </td>
                          <td>
                            <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {account.is_active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditingSubcontractor(account);
                                  setSubcontractorForm({
                                    name: account.name || '',
                                    contact_name: account.contact_name || '',
                                    contact_email: account.contact_email || '',
                                    contact_phone: account.contact_phone || '',
                                    hst_number: account.hst_number || '',
                                    address: account.address || '',
                                    notes: account.notes || ''
                                  });
                                  setShowSubcontractorDialog(true);
                                }}
                              >
                                Edit
                              </Button>
                              {account.employee_count === 0 && (
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={async () => {
                                    if (!confirm(`Delete ${account.name}? This cannot be undone.`)) return;
                                    const token = localStorage.getItem('token');
                                    try {
                                      const response = await fetch(`${API_URL}/api/subcontractor-accounts/${account.id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (response.ok) {
                                        toast.success('Subcontractor deleted');
                                        fetchAllData();
                                      } else {
                                        const error = await response.json();
                                        toast.error(error.detail || 'Failed to delete');
                                      }
                                    } catch (error) {
                                      toast.error('Connection error');
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Projects Tab - For Admin, Manager, Accounts roles */}
        {!['purchaser'].includes(user.role) && (
        <TabsContent value="projects" data-testid="projects-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Projects</CardTitle>
                <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-project-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-project-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Project</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddProject} className="form-grid">
                      <div className="col-span-2">
                        <Label>Project Name</Label>
                        <Input
                          value={projectForm.name}
                          onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                          required
                          placeholder="Enter project name"
                          data-testid="project-name-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Project Address</Label>
                        <Input
                          value={projectForm.address}
                          onChange={(e) => setProjectForm({ ...projectForm, address: e.target.value })}
                          required
                          placeholder="Enter project address"
                          data-testid="project-address-input"
                        />
                      </div>
                      
                      {/* Client Selection */}
                      <div className="col-span-2">
                        <Label>Client (Optional)</Label>
                        <Select
                          value={projectForm.client_id || "none"}
                          onValueChange={(value) => setProjectForm({ ...projectForm, client_id: value === "none" ? null : value })}
                        >
                          <SelectTrigger data-testid="project-client-select">
                            <SelectValue placeholder="Select a client" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.client_name} ({client.client_code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-2">
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={projectForm.start_date}
                          onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                          required
                          data-testid="project-start-date-input"
                        />
                      </div>
                      
                      {/* Project Status */}
                      <div className="col-span-2">
                        <Label>Project Status</Label>
                        <Select
                          value={projectForm.status}
                          onValueChange={(value) => setProjectForm({ ...projectForm, status: value })}
                        >
                          <SelectTrigger data-testid="project-status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="future">Future</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Project Stage */}
                      <div className="col-span-2">
                        <Label>Project Stage</Label>
                        <Select
                          value={projectForm.stage || 'construction'}
                          onValueChange={(value) => setProjectForm({ ...projectForm, stage: value })}
                        >
                          <SelectTrigger data-testid="project-stage-select">
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pre_construction">
                              <div className="flex items-center gap-2">
                                <span>📋</span>
                                <span>Pre-Construction</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="construction">
                              <div className="flex items-center gap-2">
                                <span>🏗️</span>
                                <span>Construction</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-gray-500 mt-1">
                          Pre-construction projects are only visible to office staff (estimators, coordinators)
                        </p>
                      </div>

                      {/* Billing Date */}
                      <div className="col-span-2">
                        <Label>Client Billing Date</Label>
                        <Input
                          type="date"
                          value={projectForm.billing_date || ''}
                          onChange={(e) => setProjectForm({ ...projectForm, billing_date: e.target.value })}
                          data-testid="project-billing-date-input"
                        />
                      </div>

                      {/* Billing Reminder */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="billing-reminder"
                            checked={projectForm.billing_reminder_enabled || false}
                            onChange={(e) => setProjectForm({ ...projectForm, billing_reminder_enabled: e.target.checked })}
                            data-testid="billing-reminder-checkbox"
                          />
                          <Label htmlFor="billing-reminder" className="cursor-pointer">
                            Enable Billing Reminder (4 days before)
                          </Label>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Get email & dashboard notifications 4 days before the billing date
                        </p>
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Equipment (Optional)</Label>
                        <p className="text-sm text-gray-500 mb-2">Select equipment and specify quantities</p>
                        {EQUIPMENT_OPTIONS.map((equip) => (
                          <div key={equip} className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              id={`equip-${equip}`}
                              checked={projectForm.equipment.some(e => e.type === equip)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setProjectForm({
                                    ...projectForm,
                                    equipment: [...projectForm.equipment, { type: equip, quantity: 1, other_description: '' }]
                                  });
                                } else {
                                  setProjectForm({
                                    ...projectForm,
                                    equipment: projectForm.equipment.filter(eq => eq.type !== equip)
                                  });
                                }
                              }}
                            />
                            <Label htmlFor={`equip-${equip}`} className="cursor-pointer w-48">{equip}</Label>
                            
                            {/* Quantity Input */}
                            {projectForm.equipment.some(e => e.type === equip) && (
                              <>
                                <Input
                                  type="number"
                                  min="1"
                                  placeholder="Qty"
                                  className="w-20"
                                  value={projectForm.equipment.find(e => e.type === equip)?.quantity || 1}
                                  onChange={(e) => {
                                    const qty = parseInt(e.target.value) || 1;
                                    setProjectForm({
                                      ...projectForm,
                                      equipment: projectForm.equipment.map(eq =>
                                        eq.type === equip ? { ...eq, quantity: qty } : eq
                                      )
                                    });
                                  }}
                                  data-testid={`equip-qty-${equip}`}
                                />
                                
                                {/* Other description for 'Other' type */}
                                {equip === 'Other' && (
                                  <Input
                                    placeholder="Specify other equipment"
                                    className="flex-1"
                                    value={projectForm.equipment.find(e => e.type === 'Other')?.other_description || ''}
                                    onChange={(e) => {
                                      setProjectForm({
                                        ...projectForm,
                                        equipment: projectForm.equipment.map(eq =>
                                          eq.type === 'Other' ? { ...eq, other_description: e.target.value } : eq
                                        )
                                      });
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button type="submit" className="col-span-2" disabled={loading} data-testid="submit-project-btn">
                        Create Project
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search filter */}
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <Input
                    type="text"
                    placeholder="Search by project name, client, or address…"
                    value={projectSearchQuery}
                    onChange={(e) => setProjectSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="projects-search-input"
                  />
                  {projectSearchQuery && (
                    <button
                      onClick={() => setProjectSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 px-1"
                      data-testid="projects-search-clear"
                      aria-label="Clear search"
                      type="button"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="table-container-with-sticky-scroll">
                <div className="table-responsive" ref={projectsTableRef}>
                  <table className="data-table" data-testid="projects-table">
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Client</th>
                      <th>Address</th>
                      <th>Team Members</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...projects]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .filter((p) => {
                        if (!projectSearchQuery) return true;
                        const q = projectSearchQuery.toLowerCase();
                        return (
                          (p.name || '').toLowerCase().includes(q) ||
                          (p.client_name || '').toLowerCase().includes(q) ||
                          (p.client_code || '').toLowerCase().includes(q) ||
                          (p.address || '').toLowerCase().includes(q)
                        );
                      })
                      .map((project, index) => (
                      <tr key={project.id} data-testid={`project-row-${index}`}>
                        <td>
                          <strong 
                            className="text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                            onClick={() => handleViewProjectDetails(project)}
                            data-testid={`project-name-${index}`}
                          >
                            {project.name}
                          </strong>
                        </td>
                        <td>
                          {project.client_name ? (
                            <div>
                              <div className="font-medium">{project.client_name}</div>
                              <div className="text-sm text-gray-500">{project.client_code}</div>
                            </div>
                          ) : project.client_code ? (
                            <div className="text-sm text-gray-600">{project.client_code}</div>
                          ) : (
                            <span className="text-gray-400">No client</span>
                          )}
                        </td>
                        <td>{project.address}</td>
                        <td>{project.team_members?.length || 0} members</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <Badge 
                              variant={
                                project.status === 'active' ? 'success' : 
                                project.status === 'completed' ? 'default' : 
                                'secondary'
                              }
                            >
                              {project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : 'Active'}
                            </Badge>
                            <Badge 
                              variant="outline"
                              className={project.stage === 'pre_construction' ? 'text-blue-600 border-blue-300' : 'text-orange-600 border-orange-300'}
                            >
                              {project.stage === 'pre_construction' ? '📋 Pre-Con' : '🏗️ Construction'}
                            </Badge>
                          </div>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedProject(project);
                              setSelectedTeamMembers(project.team_members || []);
                              setShowAssignDialog(true);
                            }}
                            data-testid={`assign-team-${index}`}
                          >
                            Assign Team
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                
                {/* Sticky scrollbar at bottom of viewport */}
                {projectsScroll.showStickyScroll && (
                  <div className="sticky-scrollbar-container">
                    <div 
                      className="sticky-scrollbar-track" 
                      ref={projectsScroll.stickyScrollRef}
                    >
                      <div 
                        className="sticky-scrollbar-content"
                        style={{ width: projectsTableRef.current?.scrollWidth + 'px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Assign Team Dialog */}
          <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
            <DialogContent data-testid="assign-team-dialog" className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Assign Team to {selectedProject?.name}</DialogTitle>
              </DialogHeader>
              <div className="team-assignment">
                <Label>Select Team Members & Time Windows (Optional)</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Add time windows to prevent overlapping assignments
                </p>
                <div className="team-list space-y-4">
                  {employees.map((emp) => (
                    <div key={emp.id} className="team-item-with-time border p-3 rounded">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={selectedTeamMembers.includes(emp.user_id)}
                          onChange={() => toggleTeamMember(emp.user_id)}
                          data-testid={`team-member-${emp.id}`}
                        />
                        <label className="ml-2 font-medium">
                          {emp.user?.name} - {emp.position}
                        </label>
                      </div>
                      
                      {selectedTeamMembers.includes(emp.user_id) && (
                        <div className="grid grid-cols-2 gap-2 ml-6">
                          <div>
                            <Label className="text-xs">Start Time</Label>
                            <Input
                              type="time"
                              value={teamMemberTimeWindows[emp.user_id]?.start_time || ''}
                              onChange={(e) => setTeamMemberTimeWindows({
                                ...teamMemberTimeWindows,
                                [emp.user_id]: {
                                  ...teamMemberTimeWindows[emp.user_id],
                                  start_time: e.target.value
                                }
                              })}
                              placeholder="09:00"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">End Time</Label>
                            <Input
                              type="time"
                              value={teamMemberTimeWindows[emp.user_id]?.end_time || ''}
                              onChange={(e) => setTeamMemberTimeWindows({
                                ...teamMemberTimeWindows,
                                [emp.user_id]: {
                                  ...teamMemberTimeWindows[emp.user_id],
                                  end_time: e.target.value
                                }
                              })}
                              placeholder="17:00"
                              className="text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => handleAssignTeam(selectedProject?.id)}
                  disabled={loading || selectedTeamMembers.length === 0}
                  className="w-full mt-4"
                  data-testid="save-team-assignment"
                >
                  Save Team Assignment
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Schedule Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts_purchaser'].includes(user.role) && (
        <TabsContent value="schedule" data-testid="schedule-content">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <CardTitle>Team Schedule</CardTitle>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                    <Button
                      variant={scheduleView === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('list');
                        fetchSchedules();
                      }}
                    >
                      List
                    </Button>
                    <Button
                      variant={scheduleView === 'week' ? 'default' : 'ghost'}
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('week');
                        const weekEnd = new Date(currentWeekStart);
                        weekEnd.setDate(currentWeekStart.getDate() + 6);
                        fetchSchedules(currentWeekStart.toISOString().split('T')[0], weekEnd.toISOString().split('T')[0]);
                      }}
                    >
                      Week
                    </Button>
                    <Button
                      variant={scheduleView === 'calendar' ? 'default' : 'ghost'}
                      size="sm"
                      className="hidden sm:inline-flex text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('calendar');
                        const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0];
                        const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString().split('T')[0];
                        fetchSchedules(startDate, endDate);
                      }}
                    >
                      Month
                    </Button>
                  </div>
                  <Button 
                    className="flex-1 sm:flex-none"
                    onClick={() => {
                      setShowScheduleDialog(true);
                      fetchSchedules();
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Create Schedule</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2 sm:p-6">
              {scheduleView === 'list' ? (
                <div className="schedule-list">
                  <div className="mb-4 flex flex-col sm:flex-row gap-2">
                    <Input
                      type="date"
                      value={selectedDate}
                      className="w-full sm:w-auto"
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        // Fetch schedules for selected week
                        const selected = new Date(e.target.value);
                        const dayOfWeek = selected.getDay();
                        const weekStart = new Date(selected);
                        weekStart.setDate(selected.getDate() - dayOfWeek);
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekStart.getDate() + 6);
                        fetchSchedules(toLocalDateString(weekStart), toLocalDateString(weekEnd));
                      }}
                    />
                    <Button 
                      onClick={() => {
                        // Reset to current week
                        const today = new Date();
                        const dayOfWeek = today.getDay();
                        const weekStart = new Date(today);
                        weekStart.setDate(today.getDate() - dayOfWeek);
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekStart.getDate() + 6);
                        setSelectedDate(toLocalDateString(today));
                        fetchSchedules(toLocalDateString(weekStart), toLocalDateString(weekEnd));
                      }} 
                      className="w-full sm:w-auto"
                    >
                      This Week
                    </Button>
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="hidden md:block">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Project</th>
                          <th>Team Members</th>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules.map((schedule, index) => (
                          <tr key={schedule.id}>
                            <td>{formatDateDisplay(schedule.date)}</td>
                            <td>
                              <strong>{schedule.project_name}</strong>
                              <br />
                              <span className="text-sm text-gray-500">{schedule.project_address}</span>
                            </td>
                            <td>
                              {schedule.team_details?.map(tm => tm.name).join(', ') || 'None'}
                            </td>
                            <td>
                              {schedule.start_time && schedule.end_time ? (
                                <span className="text-sm">{schedule.start_time} - {schedule.end_time}</span>
                              ) : (
                                <span className="text-gray-400">Not set</span>
                              )}
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs ${
                                schedule.status === 'completed' ? 'bg-green-100 text-green-800' :
                                schedule.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {schedule.status || 'scheduled'}
                              </span>
                            </td>
                            <td className="space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditSchedule(schedule)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteSchedule(schedule.id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-3">
                    {schedules.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No schedules found. Tap "Add" to create one.
                      </div>
                    ) : (
                      schedules.map((schedule) => (
                        <div 
                          key={schedule.id} 
                          className="border rounded-lg p-3 bg-white shadow-sm"
                          data-testid={`schedule-card-${schedule.id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold text-sm">{schedule.project_name}</div>
                              <div className="text-xs text-gray-500">{schedule.project_address}</div>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              schedule.status === 'completed' ? 'bg-green-100 text-green-800' :
                              schedule.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {schedule.status || 'scheduled'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDateDisplay(schedule.date)}
                            </span>
                            {schedule.start_time && schedule.end_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {schedule.start_time} - {schedule.end_time}
                              </span>
                            )}
                          </div>
                          {schedule.team_details?.length > 0 && (
                            <div className="text-xs text-gray-600 mb-2">
                              <span className="font-medium">Team:</span> {schedule.team_details.map(tm => tm.name).join(', ')}
                            </div>
                          )}
                          <div className="flex gap-2 mt-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 text-xs"
                              onClick={() => handleEditSchedule(schedule)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 text-xs text-red-600"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : scheduleView === 'calendar' ? (
                renderMonthlyCalendar()
              ) : (
                renderWeeklyCalendar()
              )}
            </CardContent>
          </Card>

          {/* Create Schedule Dialog */}
          <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Team Schedule</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateSchedule} className="space-y-4">
                <div>
                  <Label>Project *</Label>
                  <ProjectCombobox
                    projects={projects}
                    value={scheduleForm.project_id}
                    onChange={(value) => setScheduleForm({...scheduleForm, project_id: value})}
                    testId="accounts-create-schedule-project"
                  />
                </div>

                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm({...scheduleForm, date: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Team Members *</Label>
                  {/* Search input for mobile-friendly filtering */}
                  <Input
                    type="text"
                    placeholder="Search team members..."
                    className="mb-2"
                    value={scheduleForm.memberSearch || ''}
                    onChange={(e) => setScheduleForm({...scheduleForm, memberSearch: e.target.value})}
                    data-testid="team-member-search"
                  />
                  <div className="border rounded p-3 max-h-48 overflow-y-auto">
                    {[...employees]
                      .filter(emp => {
                        const search = (scheduleForm.memberSearch || '').toLowerCase();
                        if (!search) return true;
                        const name = (emp.user?.name || emp.name || '').toLowerCase();
                        const position = (emp.position || '').toLowerCase();
                        return name.includes(search) || position.includes(search);
                      })
                      .sort((a, b) => (a.user?.name || a.name || '').localeCompare(b.user?.name || b.name || ''))
                      .map(emp => (
                      <div key={emp.id} className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={scheduleForm.team_members.includes(emp.user_id || emp.id)}
                          onChange={(e) => {
                            const memberId = emp.user_id || emp.id;
                            if (e.target.checked) {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: [...scheduleForm.team_members, memberId]
                              });
                            } else {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: scheduleForm.team_members.filter(id => id !== memberId)
                              });
                            }
                          }}
                        />
                        <label className="flex-1 text-sm">
                          <span className="font-medium">{emp.user?.name || emp.name}</span>
                          {emp.position && <span className="text-gray-500 ml-1">- {emp.position}</span>}
                        </label>
                      </div>
                    ))}
                    {employees.filter(emp => {
                      const search = (scheduleForm.memberSearch || '').toLowerCase();
                      if (!search) return true;
                      const name = (emp.user?.name || emp.name || '').toLowerCase();
                      const position = (emp.position || '').toLowerCase();
                      return name.includes(search) || position.includes(search);
                    }).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-2">No team members found</p>
                    )}
                  </div>
                  {scheduleForm.team_members.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1">{scheduleForm.team_members.length} member(s) selected</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scheduleForm.is_recurring}
                    onChange={(e) => setScheduleForm({...scheduleForm, is_recurring: e.target.checked})}
                  />
                  <Label>Make Recurring</Label>
                </div>

                {scheduleForm.is_recurring && (
                  <>
                    <div>
                      <Label>Recurring Pattern</Label>
                      <Select
                        value={scheduleForm.recurring_pattern}
                        onValueChange={(value) => setScheduleForm({...scheduleForm, recurring_pattern: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>End Date *</Label>
                      <Input
                        type="date"
                        value={scheduleForm.recurring_end_date}
                        onChange={(e) => setScheduleForm({...scheduleForm, recurring_end_date: e.target.value})}
                        required={scheduleForm.is_recurring}
                      />
                    </div>
                  </>
                )}

                {/* Conflict warnings with Time Split */}
                {Array.isArray(scheduleConflicts) && scheduleConflicts.length > 0 && (
                  <div className="space-y-3">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="font-semibold text-red-800 mb-3">Scheduling Conflicts Detected</p>
                      {scheduleConflicts
                        .filter(c => c && typeof c === 'object' && c.user_name && !c.type && !c.loc)
                        .map((conflict, idx) => {
                          const times = splitTimes[conflict.user_id] || {};
                          return (
                            <div key={idx} className="mb-3 p-3 bg-white rounded border border-red-100">
                              <p className="font-medium text-red-700 mb-2">{conflict.user_name}</p>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="p-2 bg-orange-50 rounded border border-orange-200">
                                  <p className="text-xs font-semibold text-orange-800 mb-1">{conflict.existing_project || 'Existing Project'}</p>
                                  <p className="text-xs text-orange-600 mb-2">Currently: {conflict.existing_start_time || '07:00'} - {conflict.existing_end_time || '15:00'}</p>
                                  <div className="flex gap-1 items-center">
                                    <Input type="time" className="h-8 text-xs" value={times.existing_start || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [conflict.user_id]: { ...prev[conflict.user_id], existing_start: e.target.value } }))} />
                                    <span className="text-xs text-gray-400">to</span>
                                    <Input type="time" className="h-8 text-xs" value={times.existing_end || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [conflict.user_id]: { ...prev[conflict.user_id], existing_end: e.target.value } }))} />
                                  </div>
                                </div>
                                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-xs font-semibold text-blue-800 mb-1">New Schedule</p>
                                  <p className="text-xs text-blue-600 mb-2">Requested: {scheduleForm.start_time || '07:00'} - {scheduleForm.end_time || '15:00'}</p>
                                  <div className="flex gap-1 items-center">
                                    <Input type="time" className="h-8 text-xs" value={times.new_start || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [conflict.user_id]: { ...prev[conflict.user_id], new_start: e.target.value } }))} />
                                    <span className="text-xs text-gray-400">to</span>
                                    <Input type="time" className="h-8 text-xs" value={times.new_end || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [conflict.user_id]: { ...prev[conflict.user_id], new_end: e.target.value } }))} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      <p className="text-xs text-gray-500 mt-2">Adjust times above to split the day between projects.</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setShowScheduleDialog(false); setScheduleConflicts([]); setSplitTimes({}); }}>
                    Cancel
                  </Button>
                  {scheduleConflicts.length > 0 && (
                    <>
                      <Button type="button" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => {
                        const conflictUserIds = scheduleConflicts.map(c => c.user_id);
                        const cleanMembers = scheduleForm.team_members.filter(id => !conflictUserIds.includes(id));
                        if (cleanMembers.length === 0) { toast.error('No team members left'); return; }
                        setScheduleForm(prev => ({ ...prev, team_members: cleanMembers }));
                        setScheduleConflicts([]); setSplitTimes({});
                        toast.info('Removed conflicting members. Click "Create Schedule" to proceed.');
                      }}>
                        Remove Conflicting
                      </Button>
                      <Button type="button" onClick={() => handleApplyTimeSplit(scheduleConflicts)} disabled={loading}>
                        {loading ? 'Applying...' : 'Apply Time Split & Create'}
                      </Button>
                    </>
                  )}
                  {scheduleConflicts.length === 0 && (
                    <Button type="submit" disabled={loading || scheduleForm.team_members.length === 0}>
                      Create Schedule
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Schedule Dialog */}
          <Dialog open={showEditScheduleDialog} onOpenChange={setShowEditScheduleDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Schedule</DialogTitle>
                <DialogDescription>
                  Update schedule details for {editingSchedule?.project_name}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateSchedule} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={scheduleForm.date}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Project</Label>
                    <ProjectCombobox
                      projects={projects}
                      value={scheduleForm.project_id}
                      onChange={(val) => setScheduleForm({ ...scheduleForm, project_id: val })}
                      filterActive={false}
                      testId="accounts-edit-schedule-project"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select 
                    value={scheduleForm.status} 
                    onValueChange={(val) => setScheduleForm({ ...scheduleForm, status: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Team Members</Label>
                  <Input
                    type="text"
                    placeholder="Search team members..."
                    className="mb-2"
                    value={scheduleForm.editMemberSearch || ''}
                    onChange={(e) => setScheduleForm({...scheduleForm, editMemberSearch: e.target.value})}
                  />
                  <div className="border rounded p-2 max-h-40 overflow-y-auto">
                    {[...employees]
                      .filter(emp => {
                        const search = (scheduleForm.editMemberSearch || '').toLowerCase();
                        if (!search) return true;
                        const name = (emp.user?.name || emp.name || '').toLowerCase();
                        const position = (emp.position || '').toLowerCase();
                        return name.includes(search) || position.includes(search);
                      })
                      .sort((a, b) => (a.user?.name || a.name || '').localeCompare(b.user?.name || b.name || ''))
                      .map((emp) => (
                      <div key={emp.id} className="flex items-center gap-2 py-1">
                        <input
                          type="checkbox"
                          id={`edit-team-${emp.id}`}
                          checked={scheduleForm.team_members.includes(emp.user_id || emp.id)}
                          onChange={(e) => {
                            const memberId = emp.user_id || emp.id;
                            if (e.target.checked) {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: [...scheduleForm.team_members, memberId]
                              });
                            } else {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: scheduleForm.team_members.filter(id => id !== memberId)
                              });
                            }
                          }}
                        />
                        <label htmlFor={`edit-team-${emp.id}`} className="text-sm">
                          {emp.user?.name || emp.name} - {emp.position}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={scheduleForm.notes}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                    placeholder="Add any notes about this schedule..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowEditScheduleDialog(false);
                    setEditingSchedule(null);
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Update Schedule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Schedule Details Dialog */}
          <Dialog open={showScheduleDetailsDialog} onOpenChange={setShowScheduleDetailsDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Schedule Details
                </DialogTitle>
              </DialogHeader>
              {selectedScheduleDetails && (
                <div className="space-y-4">
                  {/* Project Info */}
                  <div className="border-b pb-3">
                    <h3 className="font-semibold text-lg">{selectedScheduleDetails.project_name}</h3>
                    {selectedScheduleDetails.project_address && (
                      <p className="text-sm text-gray-500">{selectedScheduleDetails.project_address}</p>
                    )}
                  </div>

                  {/* Date & Time */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>{formatDateDisplay(selectedScheduleDetails.date, { weekday: true })}</span>
                    </div>
                    {selectedScheduleDetails.start_time && selectedScheduleDetails.end_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{selectedScheduleDetails.start_time} - {selectedScheduleDetails.end_time}</span>
                      </div>
                    )}
                  </div>

                  {/* Team Members */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Team Members ({selectedScheduleDetails.team_details?.length || 0})
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      {selectedScheduleDetails.team_details?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedScheduleDetails.team_details.map((member, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded border">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-700">
                                  {member.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-sm">{member.name}</div>
                                {member.position && (
                                  <div className="text-xs text-gray-500">{member.position}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No team members assigned</p>
                      )}
                    </div>
                  </div>

                  {/* Status & Recurring Info */}
                  <div className="flex items-center gap-3">
                    <Badge variant={
                      selectedScheduleDetails.status === 'completed' ? 'success' :
                      selectedScheduleDetails.status === 'in_progress' ? 'default' :
                      selectedScheduleDetails.status === 'cancelled' ? 'destructive' :
                      'secondary'
                    }>
                      {selectedScheduleDetails.status || 'scheduled'}
                    </Badge>
                    {selectedScheduleDetails.is_recurring && (
                      <Badge variant="outline">
                        Recurring: {selectedScheduleDetails.recurring_pattern}
                      </Badge>
                    )}
                  </div>

                  {/* Notes */}
                  {selectedScheduleDetails.notes && (
                    <div>
                      <h4 className="font-medium mb-1 text-sm">Notes</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{selectedScheduleDetails.notes}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setShowScheduleDetailsDialog(false);
                        handleEditSchedule(selectedScheduleDetails);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Schedule
                    </Button>
                    <Button 
                      variant="outline" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setShowScheduleDetailsDialog(false);
                        handleDeleteSchedule(selectedScheduleDetails.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Equipment Management Tab - Company Admin Only */}
        {['company_admin', 'general_manager', 'manager'].includes(user.role) && (
        <TabsContent value="equipment" data-testid="equipment-content">
          {/* Equipment Pool Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-700">{equipmentSummary.total_types}</div>
                <div className="text-sm text-blue-600">Equipment Types</div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-purple-700">{equipmentSummary.total_units}</div>
                <div className="text-sm text-purple-600">Total Units</div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-700">{equipmentSummary.assigned_units}</div>
                <div className="text-sm text-amber-600">Assigned to Projects</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-700">{equipmentSummary.available_units}</div>
                <div className="text-sm text-green-600">Available in Pool</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Equipment Pool</CardTitle>
                <Button onClick={() => {
                  setEquipmentManagementForm({ name: '', description: '', total_quantity: 1, unit: 'units' });
                  setShowEquipmentManagementDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Equipment
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Equipment</th>
                      <th>Description</th>
                      <th className="text-center">Total</th>
                      <th className="text-center">Assigned</th>
                      <th className="text-center">Available</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipmentList.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-gray-500 py-8">
                          No equipment in pool. Click "Add Equipment" to add some.
                        </td>
                      </tr>
                    ) : (
                      equipmentList.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td 
                            className="font-medium cursor-pointer hover:text-blue-600"
                            onClick={() => handleViewEquipmentAssignments(item)}
                          >
                            {item.name || item.type}
                            {item.assignment_count > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.assignment_count} project{item.assignment_count !== 1 ? 's' : ''})
                              </span>
                            )}
                          </td>
                          <td className="text-gray-600">{item.description || item.custom_description || '-'}</td>
                          <td className="text-center">
                            <Badge variant="outline">{item.total_quantity || item.quantity || 1} {item.unit || 'units'}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge variant={item.assigned_quantity > 0 ? 'default' : 'secondary'} className={item.assigned_quantity > 0 ? 'bg-amber-100 text-amber-800' : ''}>
                              {item.assigned_quantity || 0}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge variant={item.available_quantity > 0 ? 'default' : 'destructive'} className={item.available_quantity > 0 ? 'bg-green-100 text-green-800' : ''}>
                              {item.available_quantity || 0}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewEquipmentAssignments(item)}
                                data-testid={`view-assignments-${item.id}`}
                              >
                                View
                              </Button>
                              {item.available_quantity > 0 && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleOpenAssignEquipment(item)}
                                  data-testid={`assign-equipment-${item.id}`}
                                >
                                  Assign
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenEditEquipment(item)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteEquipmentItem(item)}
                                disabled={item.assigned_quantity > 0}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Add Equipment Dialog */}
          <Dialog open={showEquipmentManagementDialog} onOpenChange={setShowEquipmentManagementDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Equipment to Pool</DialogTitle>
                <DialogDescription>
                  Add equipment with quantity. You can then assign units to projects as needed.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddEquipmentItem} className="space-y-4">
                <div>
                  <Label>Equipment Name *</Label>
                  <Input
                    value={equipmentManagementForm.name}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, name: e.target.value })}
                    required
                    placeholder="e.g., Ladder, Drill, Safety Harness"
                    data-testid="equipment-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={equipmentManagementForm.total_quantity}
                      onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, total_quantity: e.target.value })}
                      required
                      data-testid="equipment-quantity-input"
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select 
                      value={equipmentManagementForm.unit}
                      onValueChange={(val) => setEquipmentManagementForm({ ...equipmentManagementForm, unit: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="units">Units</SelectItem>
                        <SelectItem value="pieces">Pieces</SelectItem>
                        <SelectItem value="sets">Sets</SelectItem>
                        <SelectItem value="pairs">Pairs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={equipmentManagementForm.description}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, description: e.target.value })}
                    placeholder="Additional details or specifications"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEquipmentManagementDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Add to Pool
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Equipment Dialog */}
          <Dialog open={showEditEquipmentDialog} onOpenChange={setShowEditEquipmentDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Equipment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditEquipmentItem} className="space-y-4">
                <div>
                  <Label>Equipment Name *</Label>
                  <Input
                    value={equipmentManagementForm.name}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={equipmentManagementForm.total_quantity}
                      onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, total_quantity: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select 
                      value={equipmentManagementForm.unit}
                      onValueChange={(val) => setEquipmentManagementForm({ ...equipmentManagementForm, unit: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="units">Units</SelectItem>
                        <SelectItem value="pieces">Pieces</SelectItem>
                        <SelectItem value="sets">Sets</SelectItem>
                        <SelectItem value="pairs">Pairs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={equipmentManagementForm.description}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, description: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditEquipmentDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* View Equipment Assignments Dialog */}
          <Dialog open={showEquipmentAssignmentsDialog} onOpenChange={setShowEquipmentAssignmentsDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {selectedEquipmentItem?.name || selectedEquipmentItem?.type} - Project Assignments
                </DialogTitle>
                <DialogDescription>
                  Total: {selectedEquipmentItem?.total_quantity || 0} | 
                  Assigned: {selectedEquipmentItem?.assigned_quantity || 0} | 
                  Available: {selectedEquipmentItem?.available_quantity || 0}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {equipmentAssignments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No project assignments yet. This equipment is fully available in the pool.
                  </p>
                ) : (
                  <table className="data-table w-full">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Status</th>
                        <th className="text-center">Qty Assigned</th>
                        <th>Assigned</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipmentAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td className="font-medium">{assignment.project_name}</td>
                          <td>
                            <Badge variant={assignment.project_status === 'active' ? 'default' : 'secondary'}>
                              {assignment.project_status}
                            </Badge>
                          </td>
                          <td className="text-center">{assignment.quantity}</td>
                          <td className="text-sm text-gray-500">
                            {new Date(assignment.assigned_at).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnassignEquipment(assignment.id, assignment.project_id)}
                            >
                              Return to Pool
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Assign Equipment to Project Dialog */}
          <Dialog open={showAssignEquipmentDialog} onOpenChange={setShowAssignEquipmentDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign {selectedEquipmentItem?.name || selectedEquipmentItem?.type} to Project</DialogTitle>
                <DialogDescription>
                  Available: {selectedEquipmentItem?.available_quantity || 0} {selectedEquipmentItem?.unit || 'units'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAssignEquipmentToProject} className="space-y-4">
                <div>
                  <Label>Project *</Label>
                  <ProjectCombobox
                    projects={projects.filter(p => p.status === 'active')}
                    value={assignEquipmentForm.project_id}
                    onChange={(val) => setAssignEquipmentForm({ ...assignEquipmentForm, project_id: val })}
                    placeholder="Select a project..."
                    filterActive={false}
                    testId="assign-equipment-project"
                  />
                </div>
                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedEquipmentItem?.available_quantity || 1}
                    value={assignEquipmentForm.quantity}
                    onChange={(e) => setAssignEquipmentForm({ ...assignEquipmentForm, quantity: e.target.value })}
                    required
                    data-testid="assign-quantity-input"
                  />
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={assignEquipmentForm.notes}
                    onChange={(e) => setAssignEquipmentForm({ ...assignEquipmentForm, notes: e.target.value })}
                    placeholder="Any notes about this assignment"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAssignEquipmentDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading || !assignEquipmentForm.project_id}>
                    Assign Equipment
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Invoice Management Tab - Behind invoice_ocr feature flag */}
        {(['company_admin', 'accounts', 'accounts_purchaser', 'admin'].includes(user.role)) && hasFeature(user, 'invoice_ocr') && (
          <TabsContent value="invoices" data-testid="invoices-content">
            <InvoiceManagement projects={projects} onProjectsChanged={fetchAllData} />
          </TabsContent>
        )}

        {/* Stock Material Tab - Visible to all including purchasers */}
        <TabsContent value="stock" data-testid="stock-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Equipment Inventory</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setShowStockManagementDialog(true)}
                    variant="outline"
                    data-testid="manage-stock-btn"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Manage Stock
                  </Button>
                  <Dialog open={showAddEquipmentDialog} onOpenChange={setShowAddEquipmentDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-equipment-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Equipment
                      </Button>
                    </DialogTrigger>
                  <DialogContent data-testid="add-equipment-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Equipment to Inventory</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddEquipment} className="space-y-4">
                      <div>
                        <Label>Equipment Type</Label>
                        <Select
                          value={equipmentForm.type}
                          onValueChange={(value) => setEquipmentForm({ ...equipmentForm, type: value })}
                          required
                        >
                          <SelectTrigger data-testid="equipment-type-select">
                            <SelectValue placeholder="Select equipment type" />
                          </SelectTrigger>
                          <SelectContent>
                            {EQUIPMENT_OPTIONS.map(equip => (
                              <SelectItem key={equip} value={equip}>{equip}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {equipmentForm.type === 'Other' && (
                        <div>
                          <Label>Description</Label>
                          <Input
                            value={equipmentForm.custom_description}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, custom_description: e.target.value })}
                            placeholder="Describe the equipment"
                            required
                            data-testid="equipment-description-input"
                          />
                        </div>
                      )}
                      
                      <div>
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={equipmentForm.quantity}
                          onChange={(e) => setEquipmentForm({ ...equipmentForm, quantity: e.target.value })}
                          required
                          data-testid="equipment-quantity-input"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Number of items to add to inventory
                        </p>
                      </div>
                      
                      <Button type="submit" className="w-full" disabled={loading || !equipmentForm.type} data-testid="submit-equipment-btn">
                        Add to Inventory
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-container-with-sticky-scroll">
                <div className="table-responsive" ref={equipmentTableRef}>
                  <table className="data-table" data-testid="equipment-table">
                  <thead>
                    <tr>
                      <th>Equipment Type</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Project</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment
                      .sort((a, b) => {
                        // Sort: in_use first, then available, then others
                        if (a.status === 'in_use' && b.status !== 'in_use') return -1;
                        if (a.status !== 'in_use' && b.status === 'in_use') return 1;
                        if (a.status === 'available' && b.status !== 'available') return -1;
                        if (a.status !== 'available' && b.status === 'available') return 1;
                        return 0;
                      })
                      .map((equip, index) => (
                      <tr key={equip.id} data-testid={`equipment-row-${index}`}>
                        <td><strong>{equip.type}</strong></td>
                        <td>{equip.custom_description || '-'}</td>
                        <td>
                          <Badge 
                            variant={
                              equip.status === 'available' ? 'success' :
                              equip.status === 'in_use' ? 'default' :
                              'secondary'
                            }
                          >
                            {equip.status === 'available' ? 'Available' :
                             equip.status === 'in_use' ? 'In Use' :
                             equip.status}
                          </Badge>
                        </td>
                        <td>
                          {equip.current_project_name || '-'}
                        </td>
                        <td>
                          {equip.status === 'in_use' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleReturnEquipment(equip.id)}
                              data-testid={`return-equipment-${index}`}
                            >
                              Return
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {equipment.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-gray-500 py-4">
                          No equipment in inventory. Click "Add Equipment" to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                
                {/* Sticky scrollbar */}
                {equipmentScroll.showStickyScroll && (
                  <div className="sticky-scrollbar-container">
                    <div className="sticky-scrollbar-track" ref={equipmentScroll.stickyScrollRef}>
                      <div className="sticky-scrollbar-content" style={{ width: equipmentTableRef.current?.scrollWidth + 'px' }} />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Orders Tab */}
        <TabsContent value="orders" data-testid="orders-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Material Orders</CardTitle>
                <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-order-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Order
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-order-dialog">
                    <DialogHeader>
                      <DialogTitle>Create Material Order</DialogTitle>
                    </DialogHeader>
                    <MaterialOrderForm 
                      projects={projects}
                      onSubmit={handleCreateMaterialOrder}
                      onCancel={() => setShowOrderDialog(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-responsive" ref={ordersTableRef}>
                <table className="data-table" data-testid="orders-table">
                  <thead>
                    <tr>
                      <th>Order Date</th>
                      <th>Project</th>
                      <th>Order Type</th>
                      <th>Delivery Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialOrders.map((order, index) => {
                      const project = projects.find(p => p.id === order.project_id);
                      return (
                        <tr key={order.id} data-testid={`order-row-${index}`}>
                          <td>{new Date(order.order_date).toLocaleDateString()}</td>
                          <td>{project?.name || 'Unknown'}</td>
                          <td>
                            <Badge variant="outline">{order.order_type}</Badge>
                          </td>
                          <td>{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '-'}</td>
                          <td>
                            <Badge
                              variant={
                                order.status === 'completed'
                                  ? 'success'
                                  : order.status === 'pending'
                                  ? 'warning'
                                  : 'default'
                              }
                            >
                              {order.status}
                            </Badge>
                            {order.source === 'invoice_backdated' && (
                              <Badge
                                className="ml-1 bg-purple-100 text-purple-700 border-purple-200 text-[10px]"
                                title={`Back-dated from invoice ${order.source_invoice_number || ''}`}
                                data-testid={`order-from-invoice-badge-${index}`}
                              >
                                From Invoice
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleViewOrderDetails(order)}
                              data-testid={`view-order-${index}`}
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Sticky scrollbar at bottom of viewport */}
              {ordersScroll.showStickyScroll && (
                <div className="sticky-scrollbar-container">
                  <div 
                    className="sticky-scrollbar-track" 
                    ref={ordersScroll.stickyScrollRef}
                  >
                    <div 
                      className="sticky-scrollbar-content"
                      style={{ width: ordersTableRef.current?.scrollWidth + 'px' }}
                    />
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Tracking Tab - For Purchasers Only */}
        {['purchaser', 'accounts_purchaser'].includes(user.role) && (
          <TabsContent value="time-tracking" data-testid="time-tracking-content">
            <Card>
              <CardHeader>
                <CardTitle>Time Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Clock In/Out Controls */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Current Status</h3>
                      <p className="text-sm text-gray-600">
                        {timeSummary.is_clocked_in ? (
                          <>
                            <span className="text-green-600 font-semibold">● Clocked In</span>
                            <span className="ml-2 text-gray-500">
                              since {timeSummary.current_shift_start ? new Date(timeSummary.current_shift_start).toLocaleTimeString() : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-500">● Not Clocked In</span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!timeSummary.is_clocked_in ? (
                        <Button 
                          onClick={handleClockIn} 
                          disabled={clockingIn}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Clock In
                        </Button>
                      ) : (
                        <Button 
                          onClick={handleClockOut} 
                          disabled={clockingIn}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Clock Out
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Hours Summary */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600">Today</p>
                      <p className="text-2xl font-bold text-blue-600">{timeSummary.today_hours.toFixed(2)}h</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600">This Week</p>
                      <p className="text-2xl font-bold text-blue-600">{timeSummary.week_hours.toFixed(2)}h</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600">Pay Period</p>
                      <p className="text-2xl font-bold text-blue-600">{timeSummary.pay_period_hours.toFixed(2)}h</p>
                    </div>
                  </div>
                </div>

                {/* Time Entries History */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Time Entries History</h3>
                  <Button 
                    onClick={() => { fetchTimeEntries(); fetchTimeSummary(); }} 
                    className="mb-4"
                    variant="outline"
                    size="sm"
                  >
                    Refresh
                  </Button>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Clock In</th>
                          <th className="px-4 py-2 text-left">Clock Out</th>
                          <th className="px-4 py-2 text-left">Hours</th>
                          <th className="px-4 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timeEntries.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No time entries found
                            </td>
                          </tr>
                        ) : (
                          timeEntries
                            .sort((a, b) => new Date(b.clock_in) - new Date(a.clock_in))
                            .map((entry) => {
                              const clockIn = new Date(entry.clock_in);
                              const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
                              const hours = clockOut 
                                ? ((clockOut - clockIn) / (1000 * 60 * 60)).toFixed(2)
                                : '-';
                              
                              return (
                                <tr key={entry.id} className="border-b hover:bg-gray-50">
                                  <td className="px-4 py-2">{clockIn.toLocaleDateString()}</td>
                                  <td className="px-4 py-2">{clockIn.toLocaleTimeString()}</td>
                                  <td className="px-4 py-2">
                                    {clockOut ? clockOut.toLocaleTimeString() : '-'}
                                  </td>
                                  <td className="px-4 py-2 font-semibold">{hours}</td>
                                  <td className="px-4 py-2">
                                    {clockOut ? (
                                      <Badge variant="secondary">Complete</Badge>
                                    ) : (
                                      <Badge variant="default" className="bg-green-600">Active</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* View-Only Tabs for Purchasers */}
        {['purchaser', 'accounts_purchaser'].includes(user.role) && (
          <>
            {/* View Clients Tab */}
            <TabsContent value="view-clients" data-testid="view-clients-content">
              <Card>
                <CardHeader>
                  <CardTitle>Clients (View Only)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto" ref={clientsTableRef}>
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Client Code</th>
                          <th className="px-4 py-2 text-left">Company Name</th>
                          <th className="px-4 py-2 text-left">Contact Person</th>
                          <th className="px-4 py-2 text-left">Phone</th>
                          <th className="px-4 py-2 text-left">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No clients found
                            </td>
                          </tr>
                        ) : (
                          clients.map((client) => (
                            <tr key={client.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-mono text-sm">{client.client_code}</td>
                              <td className="px-4 py-2 font-semibold">{client.company_name}</td>
                              <td className="px-4 py-2">{client.billing_contact?.name || '-'}</td>
                              <td className="px-4 py-2">{client.billing_contact?.phone || '-'}</td>
                              <td className="px-4 py-2 text-sm">{client.billing_contact?.email || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Sticky scrollbar at bottom of viewport */}
                  {clientsScroll.showStickyScroll && (
                    <div className="sticky-scrollbar-container">
                      <div 
                        className="sticky-scrollbar-track" 
                        ref={clientsScroll.stickyScrollRef}
                      >
                        <div 
                          className="sticky-scrollbar-content"
                          style={{ width: clientsTableRef.current?.scrollWidth + 'px' }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* View Projects Tab */}
            <TabsContent value="view-projects" data-testid="view-projects-content">
              <Card>
                <CardHeader>
                  <CardTitle>Projects (View Only)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Project Name</th>
                          <th className="px-4 py-2 text-left">Client</th>
                          <th className="px-4 py-2 text-left">Address</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Start Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No projects found
                            </td>
                          </tr>
                        ) : (
                          projects.map((project) => {
                            const client = clients.find(c => c.id === project.client_id);
                            return (
                              <tr key={project.id} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-2 font-semibold">{project.name}</td>
                                <td className="px-4 py-2">{client?.company_name || '-'}</td>
                                <td className="px-4 py-2 text-sm">{project.address}</td>
                                <td className="px-4 py-2">
                                  <Badge variant={
                                    project.status === 'active' ? 'success' :
                                    project.status === 'completed' ? 'secondary' :
                                    'default'
                                  }>
                                    {project.status}
                                  </Badge>
                                </td>
                                <td className="px-4 py-2">
                                  {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* View Employees Tab */}
            <TabsContent value="view-employees" data-testid="view-employees-content">
              <Card>
                <CardHeader>
                  <CardTitle>Employees (View Only)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Position</th>
                          <th className="px-4 py-2 text-left">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                              No employees found
                            </td>
                          </tr>
                        ) : (
                          employees.map((emp) => (
                            <tr key={emp.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-semibold">{emp.user?.name || emp.name}</td>
                              <td className="px-4 py-2 text-sm">{emp.user?.email || emp.email}</td>
                              <td className="px-4 py-2">{emp.position}</td>
                              <td className="px-4 py-2">
                                <Badge variant="secondary">{emp.user?.role || emp.role}</Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}

        {/* Project Messages Tab - Available for all roles including purchasers */}
        <TabsContent value="communication" data-testid="communication-content">
          <Card>
            <CardHeader>
              <CardTitle>All Messages & Communication</CardTitle>
              {/* Message Type Filter Chips */}
              <div className="flex gap-2 flex-wrap items-center mt-3" data-testid="message-type-filter">
                <span className="text-sm text-gray-600 font-medium mr-1">Select From:</span>
                {[
                  { key: 'all', label: 'All', testId: 'filter-type-all' },
                  { key: 'announcement', label: 'Announcement', testId: 'filter-type-announcement' },
                  { key: 'project', label: 'Project', testId: 'filter-type-project' },
                  { key: 'work_progress', label: 'Daily Clock Out Work Progress', testId: 'filter-type-work-progress' },
                ].map((opt) => (
                  <Button
                    key={opt.key}
                    size="sm"
                    variant={messageTypeFilter === opt.key ? 'default' : 'outline'}
                    onClick={() => setMessageTypeFilter(opt.key)}
                    data-testid={opt.testId}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="mb-4 space-y-3">
                {/* Date Filter */}
                <div className="flex gap-2 items-center flex-wrap">
                  <Label className="min-w-[100px]">Filter by Date:</Label>
                  <div className="flex gap-2 items-center flex-1">
                    <Input
                      type="date"
                      value={messageDateFilter.startDate}
                      onChange={(e) => setMessageDateFilter({ ...messageDateFilter, startDate: e.target.value })}
                      className="max-w-[160px]"
                      placeholder="Start Date"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="date"
                      value={messageDateFilter.endDate}
                      onChange={(e) => setMessageDateFilter({ ...messageDateFilter, endDate: e.target.value })}
                      className="max-w-[160px]"
                      placeholder="End Date"
                    />
                    <Button 
                      size="sm" 
                      onClick={fetchMessagesWithFilter}
                      variant="outline"
                    >
                      Apply Filter
                    </Button>
                    {(messageDateFilter.startDate || messageDateFilter.endDate) && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => {
                          setMessageDateFilter({ startDate: '', endDate: '' });
                          fetchAllData();
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Project Filter */}
                <div className="flex gap-2 items-center">
                  <Label className="min-w-[100px]">Filter by Project:</Label>
                  <select
                    className="border rounded px-3 py-2 flex-1"
                    onChange={(e) => {
                      const projectId = e.target.value;
                      if (projectId === 'all') {
                        setMessages(allMessages);
                      } else {
                        // Filter messages for specific project
                        const filtered = allMessages.filter(m => m.project_id === projectId);
                        setMessages(filtered);
                      }
                    }}
                  >
                    <option value="all">All Projects & Announcements</option>
                    {projects.map(proj => (
                      <option key={proj.id} value={proj.id}>{proj.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="messages-container max-h-96 overflow-y-auto" data-testid="messages-container">
                {messages
                  .filter((msg) => messageTypeFilter === 'all' || msg.message_type === messageTypeFilter)
                  .map((msg, index) => (
                  <div key={msg.id} className="message p-3 mb-3 border rounded bg-white" data-testid={`message-${index}`}>
                    <div className="message-header flex justify-between items-start mb-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <strong>{msg.sender_name}</strong>
                          {msg.message_type === 'work_progress' && (
                            <Badge variant="success" className="text-xs">Work Progress</Badge>
                          )}
                          {msg.message_type === 'announcement' && (
                            <Badge variant="secondary" className="text-xs">Announcement</Badge>
                          )}
                          {msg.message_type === 'project' && (
                            <Badge variant="default" className="text-xs">Project</Badge>
                          )}
                        </div>
                        {msg.project_name && (
                          <span className="text-xs text-gray-600">📁 {msg.project_name}</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{new Date(msg.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="message-body text-sm" style={{whiteSpace: 'pre-wrap'}}>{msg.message}</div>
                    
                    {/* Display images if present */}
                    {msg.image_urls && msg.image_urls.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {msg.image_urls.map((url, idx) => {
                          // Convert to API endpoint URL
                          const filename = url.split('/').pop();
                          const displayUrl = `${API_URL}/api/image/${filename}`;
                          return (
                            <img 
                              key={idx} 
                              src={displayUrl}
                              alt="Attachment" 
                              className="w-32 h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => {
                                setViewerImages(msg.image_urls);
                                setViewerIndex(idx);
                                setShowImageViewer(true);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Display video if present */}
                    {msg.video_url && (
                      <div className="mt-2">
                        <video controls className="w-full max-w-md rounded border">
                          <source src={msg.video_url} type="video/mp4" />
                          Your browser does not support video playback.
                        </video>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold mb-2">💡 Tips:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>To send with media:</strong> Projects tab → Select project → Project Communication → Upload & send</li>
                  <li>• <strong>All messages sync:</strong> Messages sent here or in project view appear in both places</li>
                  <li>• <strong>Refresh to see updates:</strong> Press F5 to load latest messages from all sources</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        {!['purchaser'].includes(user.role) && (
        <TabsContent value="tasks" data-testid="tasks-content">
          <TaskManagement employees={employees} />
        </TabsContent>
        )}

        {/* Subscription Tab Removed */}
        {/* Expenses Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts_purchaser'].includes(user.role) && (
        <TabsContent value="expenses" data-testid="expenses-content">
          <Card>
            <CardHeader>
              <CardTitle>Expense Reimbursements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-yellow-800">
                          {expenses.filter(expense => expense.status === 'pending').length}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Approved</p>
                        <p className="text-2xl font-bold text-green-800">
                          ${expenses.filter(expense => expense.status === 'approved').reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Rejected</p>
                        <p className="text-2xl font-bold text-red-800">
                          {expenses.filter(expense => expense.status === 'rejected').length}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Expenses Table */}
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Description</th>
                        <th>Pay Period</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((expense, index) => (
                        <tr key={expense.id} data-testid={`expense-row-${index}`}>
                          <td>{expense.employee_name || 'Unknown'}</td>
                          <td className="capitalize">{expense.expense_type.replace('_', ' ')}</td>
                          <td className="font-semibold">${expense.amount.toFixed(2)}</td>
                          <td>{expense.description}</td>
                          <td>
                            <div className="text-sm">
                              {new Date(expense.pay_period_start).toLocaleDateString()} -<br/>
                              {new Date(expense.pay_period_end).toLocaleDateString()}
                            </div>
                          </td>
                          <td>{new Date(expense.submitted_at).toLocaleDateString()}</td>
                          <td>
                            <Badge variant={
                              expense.status === 'approved' ? 'success' :
                              expense.status === 'rejected' ? 'destructive' : 'warning'
                            }>
                              {expense.status}
                            </Badge>
                          </td>
                          <td>
                            {expense.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleExpenseStatusUpdate(expense.id, 'approved')}
                                  disabled={loading}
                                  data-testid={`approve-expense-${index}`}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleExpenseStatusUpdate(expense.id, 'rejected')}
                                  disabled={loading}
                                  data-testid={`reject-expense-${index}`}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                            {expense.status !== 'pending' && (
                              <span className="text-sm text-gray-500">
                                {expense.status === 'approved' ? 'Approved' : 'Rejected'} by {expense.reviewed_by}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {expenses.length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center text-gray-500 py-8">
                            No expenses submitted yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Excel Exports Tab */}
        {(['company_admin', 'accounts', 'accounts_purchaser', 'admin'].includes(user.role)) && (
        <TabsContent value="exports" data-testid="exports-content">
          <Card>
            <CardHeader>
              <CardTitle>Excel Exports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hasFeature(user, 'payroll_summary') && (
                <div className="p-4 border rounded bg-blue-50">
                  <h3 className="font-semibold mb-2">📊 Payroll Export</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export employee hours, hourly rates, and total pay for a date range
                  </p>
                  <Button onClick={() => setShowPayrollExportDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    Export Payroll
                  </Button>
                </div>
                )}
                
                {hasFeature(user, 'excel_export') && (
                <div className="p-4 border rounded bg-green-50">
                  <h3 className="font-semibold mb-2">💰 Project Expenses Export</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export material costs, labor costs, and equipment costs by project
                  </p>
                  <Button onClick={() => setShowExpenseExportDialog(true)} className="bg-green-600 hover:bg-green-700">
                    Export Project Expenses
                  </Button>
                </div>
                )}
                
                <div className="p-4 border rounded bg-emerald-50">
                  <h3 className="font-semibold mb-2">📦 Material Orders Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export all material orders with status, items, and project details
                  </p>
                  <Button onClick={() => setShowMaterialOrdersExportDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    Export Material Orders
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-indigo-50">
                  <h3 className="font-semibold mb-2">💬 Project Communications</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export all project messages, announcements, and communications
                  </p>
                  <Button onClick={() => setShowCommunicationsExportDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                    Export Communications
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-orange-50">
                  <h3 className="font-semibold mb-2">📥 Stock In/Out Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export stock movements: deliveries, returns, and transfers
                  </p>
                  <Button onClick={() => setShowStockExportDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                    Export Stock Movements
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-amber-50">
                  <h3 className="font-semibold mb-2">🔧 Equipment Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export equipment list with service history and usage logs
                  </p>
                  <Button onClick={() => handleEquipmentExport()} className="bg-amber-600 hover:bg-amber-700">
                    Export Equipment
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-purple-50">
                  <h3 className="font-semibold mb-2">👥 Client Projects Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export client list with all projects, status, and dates
                  </p>
                  <Button onClick={() => handleClientExport()} className="bg-purple-600 hover:bg-purple-700">
                    Export Clients
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Employee Database Tab - Company Admin, Manager, Accounts */}
        {['company_admin', 'general_manager', 'manager', 'accounts'].includes(user.role) && (
        <TabsContent value="employee-database" data-testid="employee-database-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Employee Database</CardTitle>
              <Button 
                variant="outline"
                onClick={() => {
                  fetchVisibilitySettings();
                  setShowVisibilitySettingsDialog(true);
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Visibility Settings
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Manage employee credentials, certificates, licenses, and view documents. Click on an employee to view/edit their full profile.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-left">Email</th>
                      <th className="px-4 py-2 text-left">Documents</th>
                      <th className="px-4 py-2 text-left">Certificates</th>
                      <th className="px-4 py-2 text-left">Licenses</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => {
                      // Collect all document URLs
                      const allDocs = [
                        ...(emp.certificates || []).filter(c => c.document_url).map(c => ({ url: c.document_url, type: 'cert', name: c.name })),
                        ...(emp.licenses || []).filter(l => l.document_url).map(l => ({ url: l.document_url, type: 'license', name: l.license_type })),
                        ...(emp.credentials || []).filter(c => c.document_url).map(c => ({ url: c.document_url, type: 'cred', name: c.credential_name }))
                      ];
                      const docCount = allDocs.length;
                      const missingCount = (emp.certificates?.length || 0) + (emp.licenses?.length || 0) + (emp.credentials?.length || 0) - docCount;
                      
                      return (
                        <tr key={emp.id} className="border-b hover:bg-gray-50 cursor-pointer">
                          <td className="px-4 py-2 font-medium">{emp.user?.name || emp.name || 'N/A'}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {emp.role || emp.user?.role || 'employee'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">{emp.user?.email || 'N/A'}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {/* Document thumbnails */}
                              <div className="flex -space-x-2">
                                {allDocs.slice(0, 3).map((doc, idx) => (
                                  <div 
                                    key={idx}
                                    className="w-8 h-8 rounded border border-gray-300 bg-gray-100 flex items-center justify-center cursor-pointer hover:z-10 hover:scale-110 transition-transform"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`${API_URL}${doc.url}`, '_blank');
                                    }}
                                    title={doc.name}
                                  >
                                    {doc.url?.endsWith('.pdf') ? (
                                      <FileText className="w-4 h-4 text-red-500" />
                                    ) : (
                                      <img 
                                        src={`${API_URL}${doc.url}`} 
                                        alt={doc.name}
                                        className="w-full h-full object-cover rounded"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.parentElement.innerHTML = '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>';
                                        }}
                                      />
                                    )}
                                  </div>
                                ))}
                                {allDocs.length > 3 && (
                                  <div className="w-8 h-8 rounded border border-gray-300 bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                    +{allDocs.length - 3}
                                  </div>
                                )}
                              </div>
                              {/* Document count badge */}
                              <span className={`px-2 py-1 rounded text-xs ${docCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {docCount} docs
                              </span>
                              {missingCount > 0 && (
                                <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs" title="Documents missing uploads">
                                  {missingCount} missing
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              {emp.certificates?.length || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              {emp.licenses?.length || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedEmployeeForProfile(emp.id);
                                fetchEmployeeProfile(emp.id);
                              }}
                            >
                              View Profile
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {employees.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Material Returns Tab */}
        {(['purchaser', 'accounts_purchaser', 'admin', 'company_admin', 'project_manager', 'general_manager'].includes(user.role)) && (
        <TabsContent value="returns" data-testid="returns-content">
          <Card>
            <CardHeader>
              <CardTitle>Material Return Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {!hasFeature(user, 'material_returns') ? (
                <LockedFeatureSection 
                  featureName="material_returns" 
                  title="Material Returns Management"
                />
              ) : (
                <>
              <div className="overflow-x-auto" ref={returnsTableRef}>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Employee</th>
                      <th className="px-4 py-2 text-left">Project</th>
                      <th className="px-4 py-2 text-left">Reason</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialReturns.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No return requests
                        </td>
                      </tr>
                    ) : (
                      materialReturns.map((returnReq) => {
                        const employee = employees.find(e => e.id === returnReq.employee_id);
                        const project = projects.find(p => p.id === returnReq.project_id);
                        return (
                          <tr key={returnReq.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2">{employee?.user?.name || employee?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{project?.name || 'Unknown'}</td>
                            <td className="px-4 py-2 text-sm">{returnReq.reason.replace(/_/g, ' ')}</td>
                            <td className="px-4 py-2">{new Date(returnReq.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-2">
                              <Badge variant={
                                returnReq.status === 'approved' ? 'success' :
                                returnReq.status === 'rejected' ? 'destructive' :
                                'default'
                              }>
                                {returnReq.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              {returnReq.status === 'pending' && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600"
                                    onClick={() => handleReviewReturn(returnReq.id, 'approved')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => {
                                      const reason = prompt('Rejection reason:');
                                      if (reason) handleReviewReturn(returnReq.id, 'rejected', reason);
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Sticky scrollbar at bottom of viewport */}
              {returnsScroll.showStickyScroll && (
                <div className="sticky-scrollbar-container">
                  <div 
                    className="sticky-scrollbar-track" 
                    ref={returnsScroll.stickyScrollRef}
                  >
                    <div 
                      className="sticky-scrollbar-content"
                      style={{ width: returnsTableRef.current?.scrollWidth + 'px' }}
                    />
                  </div>
                </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Material Transfers Tab */}
        {(['purchaser', 'accounts_purchaser', 'admin', 'company_admin', 'project_manager', 'general_manager'].includes(user.role)) && (
        <TabsContent value="transfers" data-testid="transfers-content">
          <Card>
            <CardHeader>
              <CardTitle>Material Transfer Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {!hasFeature(user, 'material_transfers') ? (
                <LockedFeatureSection 
                  featureName="material_transfers" 
                  title="Material Transfers Management"
                />
              ) : (
                <>
              <div className="overflow-x-auto" ref={transfersTableRef}>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Employee</th>
                      <th className="px-4 py-2 text-left">From Project</th>
                      <th className="px-4 py-2 text-left">To Project</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialTransfers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No transfer requests
                        </td>
                      </tr>
                    ) : (
                      materialTransfers.map((transfer) => {
                        const employee = employees.find(e => e.id === transfer.employee_id);
                        const sourceProj = projects.find(p => p.id === transfer.source_project_id);
                        const destProj = projects.find(p => p.id === transfer.destination_project_id);
                        return (
                          <tr key={transfer.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2">{employee?.user?.name || employee?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{sourceProj?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{destProj?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{new Date(transfer.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-2">
                              <Badge variant={
                                transfer.status === 'approved' ? 'success' :
                                transfer.status === 'rejected' ? 'destructive' :
                                'default'
                              }>
                                {transfer.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              {transfer.status === 'pending' && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600"
                                    onClick={() => handleReviewTransfer(transfer.id, 'approved')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => {
                                      const reason = prompt('Rejection reason:');
                                      if (reason) handleReviewTransfer(transfer.id, 'rejected', reason);
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Sticky scrollbar at bottom of viewport */}
              {transfersScroll.showStickyScroll && (
                <div className="sticky-scrollbar-container">
                  <div 
                    className="sticky-scrollbar-track" 
                    ref={transfersScroll.stickyScrollRef}
                  >
                    <div 
                      className="sticky-scrollbar-content"
                      style={{ width: transfersTableRef.current?.scrollWidth + 'px' }}
                    />
                  </div>
                </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Notification Settings Tab */}
        {!['purchaser', 'accounts_purchaser'].includes(user.role) && (
        <TabsContent value="notification-settings" data-testid="notification-settings-content">
          <div className="space-y-6">
            <ShiftReminderSettings />
          </div>
        </TabsContent>
        )}

      </Tabs>

      {/* Details Dialog - Dashboard Stats Cards (uses shared component with drill-in support) */}
      <StatsDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        detailsType={detailsType}
        detailsData={detailsData}
        detailsLoading={detailsLoading}
        projects={projects}
        materialOrders={materialOrders}
        employees={employees}
        onViewOrderDetails={handleViewOrderDetails}
      />

      {/* Edit Stock Dialog */}
      <Dialog open={showEditStockDialog} onOpenChange={setShowEditStockDialog}>
        <DialogContent data-testid="edit-stock-dialog">
          <DialogHeader>
            <DialogTitle>Edit Stock - {editingMaterial?.name}</DialogTitle>
          </DialogHeader>
          <div className="edit-stock-content">
            <div className="current-stock">
              <Label>Current Stock</Label>
              <div className="stock-display">{editingMaterial?.stock_quantity} {editingMaterial?.unit}</div>
            </div>

            <div className="stock-adjustment">
              <Label>Adjust Stock</Label>
              <div className="adjustment-controls">
                <Button 
                  variant="outline" 
                  onClick={() => setStockAdjustment(Math.max(-(editingMaterial?.stock_quantity || 0), stockAdjustment - 1))}
                  data-testid="stock-down-btn"
                >
                  -
                </Button>
                <Input 
                  type="number" 
                  value={stockAdjustment}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setStockAdjustment(Math.max(-(editingMaterial?.stock_quantity || 0), val));
                  }}
                  className="adjustment-input"
                  data-testid="stock-adjustment-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => setStockAdjustment(stockAdjustment + 1)}
                  data-testid="stock-up-btn"
                >
                  +
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {stockAdjustment > 0 && <>Stock Up: +{stockAdjustment}</>}
                {stockAdjustment < 0 && <>Stock Down: {stockAdjustment}</>}
                {stockAdjustment === 0 && <>No change</>}
              </p>
            </div>

            <div className="new-stock">
              <Label>New Stock</Label>
              <div className="stock-display new">
                {(editingMaterial?.stock_quantity || 0) + stockAdjustment} {editingMaterial?.unit}
              </div>
            </div>

            <div className="dialog-actions">
              <Button variant="outline" onClick={() => setShowEditStockDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateStock}
                disabled={stockAdjustment === 0 || loading}
                data-testid="save-stock-btn"
              >
                Update Stock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Management Dialog */}
      <Dialog open={showStockManagementDialog} onOpenChange={setShowStockManagementDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stock Management</DialogTitle>
          </DialogHeader>
          <StockManagementForm
            projects={projects}
            onSubmit={handleSubmitStockMovement}
            onCancel={() => setShowStockManagementDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Export Order Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Export Material Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copy the text below and paste it into your email or procurement system:
            </p>
            <div className="relative">
              <textarea
                value={exportText}
                readOnly
                className="w-full h-96 p-4 font-mono text-sm border rounded bg-gray-50"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowExportDialog(false)}>
                Close
              </Button>
              <Button onClick={handleCopyExport}>
                📋 Copy to Clipboard
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assign Projects & Team - {assigningUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Project Assignment - For Project Managers, Team Leads, Project Coordinators */}
            {assigningUser && ['project_manager', 'team_lead', 'project_coordinator'].includes(assigningUser.role) && (
              <div>
                <Label className="text-lg font-semibold mb-3 block">Assign Projects</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                  {projects.map(project => (
                    <div key={project.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`project-${project.id}`}
                        checked={selectedProjectsForAssignment.includes(project.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProjectsForAssignment([...selectedProjectsForAssignment, project.id]);
                          } else {
                            setSelectedProjectsForAssignment(selectedProjectsForAssignment.filter(id => id !== project.id));
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <label htmlFor={`project-${project.id}`} className="cursor-pointer flex-1">
                        <span className="font-medium">{project.name}</span>
                        <span className="text-sm text-gray-500 ml-2">{project.address}</span>
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedProjectsForAssignment.length} project(s) selected
                </p>
              </div>
            )}

            {/* Team Assignment - Only for Team Leads */}
            {assigningUser && assigningUser.role === 'team_lead' && (
              <div>
                <Label className="text-lg font-semibold mb-3 block">Assign Team Members</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                  {employees
                    .filter(emp => emp.user?.role === 'employee' || emp.user?.role === 'subcontractor')
                    .map(emp => (
                      <div key={emp.user.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`team-${emp.user.id}`}
                          checked={selectedTeamMembers.includes(emp.user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTeamMembers([...selectedTeamMembers, emp.user.id]);
                            } else {
                              setSelectedTeamMembers(selectedTeamMembers.filter(id => id !== emp.user.id));
                            }
                          }}
                          className="cursor-pointer"
                        />
                        <label htmlFor={`team-${emp.user.id}`} className="cursor-pointer flex-1">
                          <span className="font-medium">{emp.user.name}</span>
                          <span className="text-sm text-gray-500 ml-2">{emp.position}</span>
                        </label>
                      </div>
                    ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedTeamMembers.length} team member(s) selected
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveAssignments} disabled={loading}>
                Save Assignments
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDetailsDialog} onOpenChange={setShowOrderDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Material Order Details</DialogTitle>
              {selectedOrder && (
                <Button
                  onClick={() => handleExportOrder(selectedOrder.id)}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  data-testid="export-order-btn"
                >
                  📧 Export for Email
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedOrder && (
            <div className="order-details-content">
              {/* Order ID */}
              <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                <Label className="text-sm font-semibold text-blue-800">Order ID</Label>
                <p className="font-mono text-lg text-blue-900">{selectedOrder.id}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded">
                <div>
                  <Label className="text-sm text-gray-600">Order Date</Label>
                  <p className="font-semibold">{new Date(selectedOrder.order_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Order Type</Label>
                  <p className="font-semibold capitalize">{selectedOrder.order_type}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Delivery Date</Label>
                  <p className="font-semibold">
                    {selectedOrder.delivery_date ? new Date(selectedOrder.delivery_date).toLocaleDateString() : 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Status</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      selectedOrder.status === 'delivered' ? 'success' : 
                      selectedOrder.status === 'ordered' ? 'default' : 
                      'warning'
                    }>
                      {selectedOrder.status}
                    </Badge>
                    {(['purchaser', 'accounts_purchaser', 'admin', 'company_admin'].includes(user.role)) && (
                      <div className="flex gap-1">
                        {selectedOrder.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleUpdateOrderStatus(selectedOrder.id, 'ordered')}
                            disabled={loading}
                          >
                            Mark as Ordered
                          </Button>
                        )}
                        {selectedOrder.status === 'ordered' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setShowDeliveryPhotoDialog(true);
                            }}
                            disabled={loading}
                          >
                            📷 Mark as Received at Site
                          </Button>
                        )}
                        {selectedOrder.status === 'received_at_site' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleUpdateOrderStatus(selectedOrder.id, 'complete')}
                            disabled={loading}
                          >
                            Mark as Complete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Project</Label>
                  <p className="font-semibold">
                    {projects.find(p => p.id === selectedOrder.project_id)?.name || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {projects.find(p => p.id === selectedOrder.project_id)?.address || ''}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Created By</Label>
                  {(() => {
                    const creator = employees.find(e => e.user_id === selectedOrder.ordered_by);
                    const phone = creator?.user?.phone || creator?.phone;
                    const name = creator?.user?.name || creator?.name || 'Unknown';
                    return (
                      <div>
                        <p className="font-semibold">
                          {name}
                          {phone && <span className="text-sm font-normal text-gray-600 ml-2">({phone})</span>}
                        </p>
                        {!phone && <p className="text-sm text-gray-400">No contact number</p>}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Cost Information Section - Only visible to managers and above, hidden from employees */}
              {!['employee', 'subcontractor'].includes(user.role) && (
              <div className="mb-6 p-4 bg-yellow-50 rounded border border-yellow-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-yellow-900">Cost Details</h3>
                  {['purchaser', 'accounts_purchaser', 'company_admin', 'accounts'].includes(user.role) && (
                    <Button
                      onClick={() => {
                        setCostForm({
                          order_cost: selectedOrder.order_cost?.toString() || '',
                          delivery_charges: selectedOrder.delivery_charges?.toString() || ''
                        });
                        setShowAddCostDialog(true);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      {selectedOrder.order_cost ? 'Edit Cost' : 'Add Cost'}
                    </Button>
                  )}
                </div>
                
                {selectedOrder.order_cost !== null && selectedOrder.order_cost !== undefined ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm text-yellow-700">Order Cost (without HST)</Label>
                      <p className="text-lg font-bold text-yellow-900">
                        ${selectedOrder.order_cost?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-yellow-700">Delivery Charges</Label>
                      <p className="text-lg font-bold text-yellow-900">
                        ${selectedOrder.delivery_charges?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-yellow-700">Total Cost</Label>
                      <p className="text-xl font-bold text-yellow-900">
                        ${selectedOrder.total_cost?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-yellow-700">No cost information added yet</p>
                    {['purchaser', 'accounts_purchaser', 'company_admin', 'accounts'].includes(user.role) && (
                      <Button
                        onClick={() => {
                          setCostForm({
                            order_cost: '',
                            delivery_charges: ''
                          });
                          setShowAddCostDialog(true);
                        }}
                        size="sm"
                      >
                        Add Cost
                      </Button>
                    )}
                  </div>
                )}
              </div>
              )}

              {selectedOrder.insulation_items && (
                <div className="insulation-details">
                  <h3 className="text-lg font-semibold mb-4">Order Items</h3>
                  
                  {/* Pipe Insulation */}
                  {selectedOrder.insulation_items.pipe_insulation && selectedOrder.insulation_items.pipe_insulation.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Pipe Insulation</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Type</th>
                            <th className="text-left p-2 border">Size</th>
                            <th className="text-left p-2 border">Thickness</th>
                            <th className="text-right p-2 border">Quantity (ft)</th>
                            <th className="text-right p-2 border">Pieces</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.pipe_insulation.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border capitalize">{item.pipe_type || '-'}</td>
                              <td className="p-2 border">{item.size || '-'}</td>
                              <td className="p-2 border">{item.thickness || '-'}</td>
                              <td className="text-right p-2 border">{item.quantity_ft || '-'}</td>
                              <td className="text-right p-2 border">{item.quantity_pieces || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* PVC Sheets */}
                  {selectedOrder.insulation_items.pvc_sheets && selectedOrder.insulation_items.pvc_sheets.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">PVC Sheets</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">PVC Number</th>
                            <th className="text-right p-2 border">Quantity (sheets)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.pvc_sheets.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border">{item.pvc_number}</td>
                              <td className="text-right p-2 border">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* PVC Rolls */}
                  {selectedOrder.insulation_items.pvc_rolls && selectedOrder.insulation_items.pvc_rolls.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">PVC Rolls</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-right p-2 border">Number of Rolls</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.pvc_rolls.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="text-right p-2 border">{item.rolls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Duct Insulation */}
                  {selectedOrder.insulation_items.duct_insulation && selectedOrder.insulation_items.duct_insulation.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Duct Insulation</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Type</th>
                            <th className="text-left p-2 border">Thickness</th>
                            <th className="text-right p-2 border">Quantity (sheets)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.duct_insulation.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border capitalize">{item.duct_type ? item.duct_type.replace('_', ' ') : '-'}</td>
                              <td className="p-2 border">{item.thickness || '-'}</td>
                              <td className="text-right p-2 border">{item.quantity || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Jacketing Items */}
                  {selectedOrder.insulation_items.jacketing_items && selectedOrder.insulation_items.jacketing_items.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Jacketing Materials</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Type</th>
                            <th className="text-right p-2 border">Rolls</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.jacketing_items.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border">{item.type}</td>
                              <td className="text-right p-2 border">{item.rolls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Tape Items */}
                  {selectedOrder.insulation_items.tape_items && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Tape & Fasteners</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Item</th>
                            <th className="text-right p-2 border">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.tape_items.silver_tape && (
                            <tr className="border">
                              <td className="p-2 border">Silver Tape</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.silver_tape} rolls</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.aerp_tape && (
                            <tr className="border">
                              <td className="p-2 border">Aero Tape</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.aerp_tape} rolls</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.vinyl_white_tape && (
                            <tr className="border">
                              <td className="p-2 border">Vinyl White Tape</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.vinyl_white_tape} rolls</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.corner_beads && (
                            <tr className="border">
                              <td className="p-2 border">Corner Beads</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.corner_beads} packets</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.canvas_lagging && (
                            <tr className="border">
                              <td className="p-2 border">Canvas Lagging</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.canvas_lagging} litres</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.rubber_glue && (
                            <tr className="border">
                              <td className="p-2 border">Rubber Glue</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.rubber_glue} litres</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.silicon_sealant && (
                            <tr className="border">
                              <td className="p-2 border">Silicon Sealant Outdoor</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.silicon_sealant} tubes</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.cup_head_pins && selectedOrder.insulation_items.tape_items.cup_head_pins.length > 0 && (
                            selectedOrder.insulation_items.tape_items.cup_head_pins.map((pin, idx) => (
                              <tr key={idx} className="border">
                                <td className="p-2 border">Cup Head Pins ({pin.size})</td>
                                <td className="text-right p-2 border">{pin.quantity} boxes</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {selectedOrder.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <Label className="text-sm font-semibold">Notes</Label>
                  <p className="mt-1 text-sm">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Communication Section */}
              <div className="mt-6 border-t pt-4">
                <h4 className="font-semibold text-md mb-3">Communication</h4>
                
                {/* Display existing comments */}
                {selectedOrder.comments && selectedOrder.comments.length > 0 && (
                  <div className="mb-4 max-h-64 overflow-y-auto space-y-2">
                    {selectedOrder.comments.map((comment, idx) => {
                      const commenter = employees.find(e => e.user_id === comment.user_id);
                      return (
                        <div key={idx} className="p-3 bg-blue-50 rounded border border-blue-200">
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-semibold text-sm">{commenter?.user?.name || commenter?.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-sm text-gray-700">{comment.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new comment */}
                <div className="space-y-2">
                  <Label className="text-sm">Add Comment / Report Issue</Label>
                  <Textarea
                    value={orderComment}
                    onChange={(e) => setOrderComment(e.target.value)}
                    placeholder="e.g., Item not available from supplier, need alternative..."
                    rows={3}
                  />
                  <Button 
                    onClick={() => handleAddOrderComment(selectedOrder.id)}
                    disabled={loading || !orderComment.trim()}
                    size="sm"
                  >
                    Send Message
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => {
                  setShowOrderDetailsDialog(false);
                  setOrderComment('');
                }}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={showProjectDetailsDialog} onOpenChange={setShowProjectDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Project Details - {selectedProjectDetails?.name}</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditProjectForm({
                    name: selectedProjectDetails.name,
                    address: selectedProjectDetails.address,
                    start_date: selectedProjectDetails.start_date || '',
                    client_id: selectedProjectDetails.client_id || null,
                    status: selectedProjectDetails.status || 'active',
                    stage: selectedProjectDetails.stage || 'construction'
                  });
                  setShowEditProjectDialog(true);
                }}
              >
                ✏️ Edit Project
              </Button>
            </div>
          </DialogHeader>
          {selectedProjectDetails && (
            <div className="project-details-content">
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded">
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Project Name</Label>
                  <p className="font-semibold text-lg">{selectedProjectDetails.name}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Address</Label>
                  <p className="text-gray-700">{selectedProjectDetails.address}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Start Date</Label>
                  <p className="font-semibold">
                    {selectedProjectDetails.start_date ? new Date(selectedProjectDetails.start_date).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Status</Label>
                  <Badge variant={selectedProjectDetails.is_active ? 'success' : 'default'}>
                    {selectedProjectDetails.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              {/* Team Members */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Team Members ({selectedProjectDetails.team_members?.length || 0})</h3>
                <div className="grid grid-cols-2 gap-2">
                  {selectedProjectDetails.team_members?.map((memberId) => {
                    const emp = employees.find(e => e.user_id === memberId);
                    const assignment = selectedProjectDetails.team_assignments?.find(a => a.user_id === memberId);
                    return (
                      <div key={memberId} className="p-3 border rounded bg-white">
                        <p className="font-semibold">{emp?.user?.name || emp?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-600">{emp?.position || 'N/A'}</p>
                        {assignment?.start_time && assignment?.end_time && (
                          <p className="text-xs text-gray-500 mt-1">
                            {assignment.start_time} - {assignment.end_time}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {(!selectedProjectDetails.team_members || selectedProjectDetails.team_members.length === 0) && (
                    <p className="text-gray-500 col-span-2">No team members assigned yet</p>
                  )}
                </div>
              </div>

              {/* Sub-projects Section */}
              {['admin', 'company_admin', 'general_manager', 'project_manager', 'project_coordinator'].includes(user.role) && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold">
                      Sub-projects ({subProjects.length})
                      {!hasFeature(user, 'sub_projects') && <Badge variant="secondary" className="ml-2 text-xs">🔒 Big Business+</Badge>}
                    </h3>
                    {hasFeature(user, 'sub_projects') ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProjectForSubProject(selectedProjectDetails);
                          setShowSubProjectDialog(true);
                          fetchSubProjects(selectedProjectDetails.id);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Sub-project
                      </Button>
                    ) : (
                      <LockedFeatureButton featureName="sub_projects" size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Sub-project
                      </LockedFeatureButton>
                    )}
                  </div>
                  <div className="space-y-2">
                    {subProjects.length === 0 ? (
                      <p className="text-gray-500 text-sm">No sub-projects yet</p>
                    ) : (
                      subProjects.map((subProject) => (
                        <div key={subProject.id} className="p-4 border rounded bg-white">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-base">{subProject.name}</h4>
                              {subProject.description && (
                                <p className="text-sm text-gray-600 mt-1">{subProject.description}</p>
                              )}
                              <div className="flex gap-4 mt-2 text-sm">
                                {subProject.budget && (
                                  <div>
                                    <span className="text-gray-600">Budget:</span>
                                    <span className="font-semibold ml-1">${subProject.budget.toLocaleString()}</span>
                                  </div>
                                )}
                                {subProject.start_date && (
                                  <div>
                                    <span className="text-gray-600">Start:</span>
                                    <span className="ml-1">{new Date(subProject.start_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                                {subProject.end_date && (
                                  <div>
                                    <span className="text-gray-600">End:</span>
                                    <span className="ml-1">{new Date(subProject.end_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2">
                                <Badge variant={subProject.status === 'active' ? 'success' : 'default'}>
                                  {subProject.status}
                                </Badge>
                                {subProject.team_members.length > 0 && (
                                  <span className="text-xs text-gray-600 ml-2">
                                    {subProject.team_members.length} team member(s)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditSubProjectDialog(subProject)}
                              >
                                ✏️
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSubProject(subProject.id)}
                              >
                                🗑️
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Equipment */}
              {/* Equipment Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">
                    Equipment ({selectedProjectDetails.equipment?.length || 0})
                    {!hasFeature(user, 'equipment_management') && <Badge variant="secondary" className="ml-2 text-xs">🔒 Big Business+</Badge>}
                  </h3>
                  {hasFeature(user, 'equipment_management') ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEquipmentDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Equipment
                    </Button>
                  ) : (
                    <LockedFeatureButton featureName="equipment_management" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Equipment
                    </LockedFeatureButton>
                  )}
                </div>
                {hasFeature(user, 'equipment_management') ? (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedProjectDetails.equipment?.map((equip, idx) => (
                      <div key={idx} className="p-3 border rounded bg-white flex justify-between items-center">
                        <div>
                          <p className="font-semibold">{equip.type}</p>
                          {equip.custom_description && (
                            <p className="text-sm text-gray-600">{equip.custom_description}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveEquipmentFromProject(equip.equipment_id)}
                        >
                          ❌
                        </Button>
                      </div>
                    ))}
                    {(!selectedProjectDetails.equipment || selectedProjectDetails.equipment.length === 0) && (
                      <p className="text-gray-500 col-span-2">No equipment assigned yet</p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed rounded text-center">
                    <p className="text-sm text-gray-600">
                      🔒 Equipment management requires Big Business plan or higher
                    </p>
                  </div>
                )}
              </div>

              {/* Project Communication */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">Project Communication</h3>
                
                {/* Display project messages */}
                <div className="mb-4 max-h-96 overflow-y-auto space-y-2 bg-gray-50 p-3 rounded">
                  {projectMessages.length > 0 ? (
                    projectMessages.map((msg, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border">
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-semibold text-sm">{msg.sender_name}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(msg.timestamp).toLocaleString()}
                          </p>
                        </div>
                        
                        {/* Show if replying to someone */}
                        {msg.reply_to_sender && (
                          <div className="mb-2 pl-3 border-l-2 border-blue-400 bg-blue-50 p-2 rounded text-xs">
                            <p className="font-semibold text-blue-700">Replying to {msg.reply_to_sender}</p>
                            <p className="text-gray-600 italic">{msg.reply_to_message}</p>
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-700">{msg.message}</p>
                        
                        {/* Display video if present */}
                        {msg.video_url && (
                          <div className="mt-2">
                            <video 
                              controls
                              className="max-w-md rounded border"
                              src={`${API_URL}${msg.video_url}`}
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                        
                        {/* Display images if present */}
                        {msg.image_urls && msg.image_urls.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {msg.image_urls.map((url, imgIdx) => {
                              const filename = url.split('/').pop();
                              const displayUrl = `${API_URL}/api/image/${filename}`;
                              return (
                                <img 
                                  key={imgIdx}
                                  src={displayUrl}
                                  alt="Uploaded"
                                  className="w-32 h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                  onClick={() => {
                                    setViewerImages(msg.image_urls);
                                    setViewerIndex(imgIdx);
                                    setShowImageViewer(true);
                                  }}
                                />
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Reply Button */}
                        <button
                          onClick={() => {
                            setReplyingTo(msg);
                            setProjectMessageText('');
                            // Scroll to input
                            document.querySelector('#project-message-input')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          ↩️ Reply
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-4">No messages yet</p>
                  )}
                </div>

                {/* Send project message */}
                <form onSubmit={handleSendProjectMessage} className="space-y-2">
                  {/* Show who we're replying to */}
                  {replyingTo && (
                    <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-blue-700">Replying to {replyingTo.sender_name}</p>
                        <p className="text-xs text-gray-600 italic">{replyingTo.message.substring(0, 60)}...</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setReplyingTo(null)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  <Textarea
                    id="project-message-input"
                    value={projectMessageText}
                    onChange={(e) => setProjectMessageText(e.target.value)}
                    placeholder={replyingTo ? `Reply to ${replyingTo.sender_name}...` : "Type a message to project team..."}
                    rows={3}
                  />
                  
                  {/* Image upload */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="project-images"
                      multiple
                      accept="image/*"
                      onChange={(e) => setSelectedImages(Array.from(e.target.files))}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('project-images').click()}
                    >
                      📷 Add Images ({selectedImages.length})
                    </Button>
                    
                    {/* Video upload */}
                    <input
                      type="file"
                      id="project-video"
                      accept="video/*"
                      onChange={(e) => setSelectedVideo(e.target.files[0])}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('project-video').click()}
                    >
                      🎥 Add Video {selectedVideo ? '(1)' : ''}
                    </Button>
                    
                    <Button 
                      type="submit"
                      disabled={uploadingImages || uploadingVideo || loading || !projectMessageText.trim()}
                      size="sm"
                    >
                      {uploadingImages || uploadingVideo ? 'Uploading...' : 'Send Message'}
                    </Button>
                  </div>
                  
                  {/* Video preview */}
                  {selectedVideo && (
                    <div className="bg-gray-100 p-2 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Video: {selectedVideo.name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedVideo(null)}
                          className="bg-red-500 text-white rounded px-2 py-1 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Image preview */}
                  {selectedImages.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {selectedImages.map((file, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={URL.createObjectURL(file)}
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedImages(selectedImages.filter((_, i) => i !== idx))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </form>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => {
                  setShowProjectDetailsDialog(false);
                  setProjectMessageText('');
                  setSelectedImages([]);
                }}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delivery Photo Upload Dialog */}
      <Dialog open={showDeliveryPhotoDialog} onOpenChange={setShowDeliveryPhotoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Received at Site</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Please upload photos to verify delivery:</p>
            
            <div>
              <Label>1. Delivery Slip Photo</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    setDeliveryPhotos(prev => [...prev, e.target.files[0]]);
                  }
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            
            <div>
              <Label>2. Received Material Photos</Label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setDeliveryPhotos(prev => [...prev, ...files]);
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            
            {deliveryPhotos.length > 0 && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-semibold mb-2">Selected Photos ({deliveryPhotos.length}):</p>
                <ul className="text-sm text-gray-600">
                  {deliveryPhotos.map((file, idx) => (
                    <li key={idx} className="flex justify-between items-center">
                      <span>{file.name}</span>
                      <button
                        onClick={() => setDeliveryPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 text-xs"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeliveryPhotoDialog(false);
                  setDeliveryPhotos([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkReceivedAtSite}
                disabled={uploadingDeliveryPhotos}
              >
                {uploadingDeliveryPhotos ? 'Uploading...' : deliveryPhotos.length > 0 ? 'Upload & Mark Received' : 'Mark Received (No Photos)'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProjectDialog} onOpenChange={setShowEditProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditProject} className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={editProjectForm.name}
                onChange={(e) => setEditProjectForm({...editProjectForm, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Input
                value={editProjectForm.address}
                onChange={(e) => setEditProjectForm({...editProjectForm, address: e.target.value})}
                required
              />
            </div>
            
            {/* Client Selection */}
            <div>
              <Label>Client (Optional)</Label>
              <Select
                value={editProjectForm.client_id || "none"}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, client_id: value === "none" ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.client_name} ({client.client_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={editProjectForm.start_date}
                onChange={(e) => setEditProjectForm({...editProjectForm, start_date: e.target.value})}
              />
            </div>
            
            {/* Project Status */}
            <div>
              <Label>Project Status</Label>
              <Select
                value={editProjectForm.status}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="future">Future</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Project Stage */}
            <div>
              <Label>Project Stage</Label>
              <Select
                value={editProjectForm.stage || 'construction'}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, stage: value })}
              >
                <SelectTrigger data-testid="edit-project-stage-select">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_construction">
                    <div className="flex items-center gap-2">
                      <span>📋</span>
                      <span>Pre-Construction</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="construction">
                    <div className="flex items-center gap-2">
                      <span>🏗️</span>
                      <span>Construction</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Pre-construction: visible to office staff only
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Sub-project Dialog */}
      <Dialog open={showSubProjectDialog} onOpenChange={setShowSubProjectDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Sub-project for {selectedProjectForSubProject?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubProject} className="space-y-4">
            <div>
              <Label>Sub-project Name *</Label>
              <Input
                value={subProjectForm.name}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, name: e.target.value })}
                required
                placeholder="e.g., Phase 1, Building A, Floor 2"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[80px]"
                value={subProjectForm.description}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, description: e.target.value })}
                placeholder="Brief description of this sub-project"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={subProjectForm.budget}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, budget: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={subProjectForm.status || 'active'}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.start_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.end_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, end_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Assign Team Members</Label>
              <div className="border rounded p-3 max-h-48 overflow-y-auto">
                {employees.filter(emp => 
                  selectedProjectForSubProject?.team_members?.includes(emp.user_id)
                ).map((emp) => (
                  <label key={emp.user_id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={subProjectForm.team_members.includes(emp.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: [...subProjectForm.team_members, emp.user_id]
                          });
                        } else {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: subProjectForm.team_members.filter(id => id !== emp.user_id)
                          });
                        }
                      }}
                    />
                    <span>{emp.user?.name || emp.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowSubProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                Create Sub-project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Sub-project Dialog */}
      <Dialog open={showEditSubProjectDialog} onOpenChange={setShowEditSubProjectDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Sub-project: {editingSubProject?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubProject} className="space-y-4">
            <div>
              <Label>Sub-project Name *</Label>
              <Input
                value={subProjectForm.name}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, name: e.target.value })}
                required
                placeholder="e.g., Phase 1, Building A, Floor 2"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[80px]"
                value={subProjectForm.description}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, description: e.target.value })}
                placeholder="Brief description of this sub-project"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={subProjectForm.budget}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, budget: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={subProjectForm.status || 'active'}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.start_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.end_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, end_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Assign Team Members</Label>
              <div className="border rounded p-3 max-h-48 overflow-y-auto">
                {employees.filter(emp => 
                  selectedProjectForSubProject?.team_members?.includes(emp.user_id)
                ).map((emp) => (
                  <label key={emp.user_id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={subProjectForm.team_members.includes(emp.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: [...subProjectForm.team_members, emp.user_id]
                          });
                        } else {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: subProjectForm.team_members.filter(id => id !== emp.user_id)
                          });
                        }
                      }}
                    />
                    <span>{emp.user?.name || emp.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditSubProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                Update Sub-project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Equipment to Project Dialog */}
      <Dialog open={showEquipmentDialog} onOpenChange={setShowEquipmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Equipment to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Select available equipment to add to this project:</p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {equipment.filter(e => e.status === 'available').map((equip) => (
                <div
                  key={equip.id}
                  className="p-3 border rounded hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                  onClick={() => {
                    handleAddEquipmentToProject(equip.id);
                    setShowEquipmentDialog(false);
                  }}
                >
                  <div>
                    <p className="font-semibold">{equip.type}</p>
                    {equip.custom_description && (
                      <p className="text-sm text-gray-600">{equip.custom_description}</p>
                    )}
                  </div>
                  <Badge variant="success">Available</Badge>
                </div>
              ))}
              {equipment.filter(e => e.status === 'available').length === 0 && (
                <p className="text-gray-500 text-center py-4">No available equipment. All equipment is currently in use.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Company Settings Dialog */}
      <Dialog open={showCompanySettingsDialog} onOpenChange={setShowCompanySettingsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Company Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name</Label>
              <Input
                type="text"
                value={companySettings.name || ''}
                onChange={(e) => setCompanySettings({ ...companySettings, name: e.target.value })}
                placeholder="Enter company name"
                disabled={user.role !== 'company_admin'}
              />
              <p className="text-sm text-gray-500 mt-1">
                Displayed in one line in the dashboard header
              </p>
              {user.role !== 'company_admin' && (
                <p className="text-sm text-amber-600 mt-1">
                  ⚠️ Only Company Admin can change company name
                </p>
              )}
            </div>
            
            <div>
              <Label>Company Tagline</Label>
              <Input
                type="text"
                value={companySettings.tagline || ''}
                onChange={(e) => setCompanySettings({ ...companySettings, tagline: e.target.value })}
                placeholder="Enter company tagline (e.g., Building Excellence Since 1990)"
                disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
              />
              <p className="text-sm text-gray-500 mt-1">
                Shown below company name in smaller font
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name Font Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={companySettings.name_color || '#1e293b'}
                    onChange={(e) => setCompanySettings({ ...companySettings, name_color: e.target.value })}
                    disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
                    className="w-12 h-10 border rounded cursor-pointer disabled:opacity-50"
                  />
                  <Input
                    type="text"
                    value={companySettings.name_color || '#1e293b'}
                    onChange={(e) => setCompanySettings({ ...companySettings, name_color: e.target.value })}
                    placeholder="#1e293b"
                    disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
                    className="w-28"
                  />
                </div>
              </div>
              
              <div>
                <Label>Name Font Family</Label>
                <Select
                  value={companySettings.name_font || 'Inter'}
                  onValueChange={(value) => setCompanySettings({ ...companySettings, name_font: value })}
                  disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inter">Inter (Default)</SelectItem>
                    <SelectItem value="Arial">Arial</SelectItem>
                    <SelectItem value="Georgia">Georgia</SelectItem>
                    <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                    <SelectItem value="Verdana">Verdana</SelectItem>
                    <SelectItem value="Trebuchet MS">Trebuchet MS</SelectItem>
                    <SelectItem value="Impact">Impact</SelectItem>
                    <SelectItem value="Courier New">Courier New</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Live Preview */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <Label className="text-xs text-gray-500 mb-2 block">Preview</Label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {companySettings.logo_url ? (
                    <img 
                      src={companySettings.logo_url} 
                      alt="Logo" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-bold text-xl">
                      {companySettings.name ? companySettings.name.substring(0, 2).toUpperCase() : 'CO'}
                    </span>
                  )}
                </div>
                <div>
                  <h3 
                    style={{ 
                      color: companySettings.name_color || '#1e293b',
                      fontFamily: companySettings.name_font || 'Inter',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2
                    }}
                  >
                    {companySettings.name || 'Company Name'}
                  </h3>
                  <p style={{ 
                    color: '#64748b',
                    fontSize: '0.75rem',
                    lineHeight: 1.2
                  }}>
                    {companySettings.tagline || 'Company Tagline'}
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <Label>Company Logo</Label>
              <Input
                type="file"
                accept="image/*"
                disabled={uploadingLogo || user.role !== 'company_admin'}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  // Validate file type
                  if (!file.type.startsWith('image/')) {
                    toast.error('Please select an image file');
                    return;
                  }
                  
                  setUploadingLogo(true);
                  
                  const formData = new FormData();
                  formData.append('file', file);
                  
                  try {
                    const token = localStorage.getItem('token');
                    
                    const response = await fetch(`${API_URL}/api/company/logo`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                      body: formData
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      const fullLogoUrl = `${API_URL}${data.logo_url}`;
                      
                      // Set preview URL for immediate display
                      setLogoPreviewUrl(fullLogoUrl);
                      
                      // Update company settings
                      setCompanySettings(prev => ({
                        ...prev,
                        logo_url: fullLogoUrl
                      }));
                      
                      toast.success('Logo uploaded successfully!');
                      
                      // Refresh company settings to get the updated logo
                      fetchCompanySettings();
                    } else {
                      const error = await response.json();
                      toast.error(error.detail || 'Upload failed');
                    }
                  } catch (error) {
                    console.error('Upload error:', error);
                    toast.error('Upload error: ' + error.message);
                  } finally {
                    setUploadingLogo(false);
                  }
                }}
              />
              <p className="text-sm text-gray-500 mt-1">
                Recommended size: 400x200 pixels. Only Company Admin can upload.
              </p>
              {user.role !== 'company_admin' && (
                <p className="text-sm text-amber-600 mt-1">
                  ⚠️ Only Company Admin can change the logo
                </p>
              )}
              
              {uploadingLogo && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-600 font-medium">⏳ Uploading logo...</p>
                </div>
              )}
              
              {logoPreviewUrl && !uploadingLogo && (
                <div className="mt-3 p-3 bg-gray-50 border-2 border-gray-300 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Logo Preview (400x200):</p>
                  <div className="bg-white p-4 rounded border">
                    <img 
                      src={logoPreviewUrl}
                      alt="Company logo preview" 
                      style={{ maxWidth: '400px', maxHeight: '200px', width: 'auto', height: 'auto' }}
                      className="object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-2">This logo will appear for all users in your company</p>
                </div>
              )}
              
              {!logoPreviewUrl && !uploadingLogo && (
                <div className="mt-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-center">
                  <p className="text-sm text-gray-500">
                    No logo uploaded yet. Recommended size: 400x200 pixels
                  </p>
                </div>
              )}
            </div>
            
            <div>
              <Label>Company Name Animation</Label>
              <Select
                value={companySettings.name_animation || 'none'}
                onValueChange={(value) => setCompanySettings({ ...companySettings, name_animation: value })}
                disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Animation</SelectItem>
                  <SelectItem value="fade">Fade In</SelectItem>
                  <SelectItem value="slide">Slide In</SelectItem>
                  <SelectItem value="bounce">Bounce</SelectItem>
                  <SelectItem value="glow">Glow Pulse</SelectItem>
                  <SelectItem value="rainbow">Rainbow Text</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                Choose an animation effect for company name
              </p>
            </div>

            <div>
              <Label>Logo Animation</Label>
              <Select
                value={companySettings.logo_animation || 'none'}
                onValueChange={(value) => setCompanySettings({ ...companySettings, logo_animation: value })}
                disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Animation</SelectItem>
                  <SelectItem value="rotate">Rotate</SelectItem>
                  <SelectItem value="pulse">Pulse</SelectItem>
                  <SelectItem value="float">Float</SelectItem>
                  <SelectItem value="spin">Spin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                Choose an animation effect for company logo
              </p>
            </div>
            
            <div>
              <Label>Bi-Weekly Pay Period Start Date</Label>
              <Input
                type="date"
                value={biWeeklyStartDate}
                onChange={(e) => setBiWeeklyStartDate(e.target.value)}
                placeholder="Select start date"
              />
              <p className="text-sm text-gray-500 mt-2">
                This date will be used to calculate bi-weekly pay periods (every 14 days)
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCompanySettingsDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCompanySettings}>
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Cost Dialog */}
      <Dialog open={showAddCostDialog} onOpenChange={setShowAddCostDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Order Cost Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCost} className="space-y-4 py-4">
            <div>
              <Label htmlFor="order_cost">Order Cost (without HST) *</Label>
              <Input
                id="order_cost"
                type="number"
                step="0.01"
                min="0"
                value={costForm.order_cost}
                onChange={(e) => setCostForm({ ...costForm, order_cost: e.target.value })}
                placeholder="0.00"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Total cost of all items excluding HST
              </p>
            </div>
            
            <div>
              <Label htmlFor="delivery_charges">Delivery Charges *</Label>
              <Input
                id="delivery_charges"
                type="number"
                step="0.01"
                min="0"
                value={costForm.delivery_charges}
                onChange={(e) => setCostForm({ ...costForm, delivery_charges: e.target.value })}
                placeholder="0.00"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Delivery charges for this order
              </p>
            </div>

            {/* Total Cost Preview */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-sm font-semibold text-blue-800">Total Cost Preview</Label>
              <p className="text-2xl font-bold text-blue-900">
                ${(
                  (parseFloat(costForm.order_cost) || 0) + 
                  (parseFloat(costForm.delivery_charges) || 0)
                ).toFixed(2)}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddCostDialog(false);
                  setCostForm({ order_cost: '', delivery_charges: '' });
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Cost'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Dialogs - Using Extracted Components */}
      <PayrollExportDialog
        open={showPayrollExportDialog}
        onOpenChange={setShowPayrollExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportPayroll}
      />

      <ExpenseExportDialog
        open={showExpenseExportDialog}
        onOpenChange={setShowExpenseExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        projects={projects}
        onExport={handleExportProjectExpenses}
      />

      <MaterialOrdersExportDialog
        open={showMaterialOrdersExportDialog}
        onOpenChange={setShowMaterialOrdersExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportMaterialOrders}
      />

      <CommunicationsExportDialog
        open={showCommunicationsExportDialog}
        onOpenChange={setShowCommunicationsExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportCommunications}
      />

      <StockExportDialog
        open={showStockExportDialog}
        onOpenChange={setShowStockExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportStockMovements}
      />

      {/* Employee Profile Dialog */}
      <Dialog open={showEmployeeProfileDialog} onOpenChange={setShowEmployeeProfileDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Employee Profile: {employeeProfile?.user?.name || employeeProfile?.name || 'Loading...'}
            </DialogTitle>
          </DialogHeader>
          {employeeProfile && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium">{employeeProfile.user?.email || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Phone</Label>
                  <p className="font-medium">{employeeProfile.user?.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Role</Label>
                  <p className="font-medium capitalize">{employeeProfile.role || employeeProfile.user?.role || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hourly Rate</Label>
                  <p className="font-medium">${employeeProfile.hourly_rate || 0}/hr</p>
                </div>
                <div>
                  <Label className="text-gray-500">YTD Hours</Label>
                  <p className="font-medium text-blue-600">{employeeProfile.ytd_hours || 0} hours</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hire Date</Label>
                  <p className="font-medium">{employeeProfile.hire_date || 'N/A'}</p>
                </div>
              </div>

              {/* Certificates Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">📜 Certificates</h3>
                  <Button size="sm" onClick={() => setShowAddCertificateDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Certificate
                  </Button>
                </div>
                {employeeProfile.certificates?.length > 0 ? (
                  <div className="space-y-2">
                    {employeeProfile.certificates.map((cert) => (
                      <div key={cert.id} className="flex justify-between items-center p-3 bg-green-50 rounded">
                        <div>
                          <p className="font-medium">{cert.name}</p>
                          <p className="text-sm text-gray-600">
                            {cert.issuing_authority} • #{cert.certificate_number}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires: {cert.expiry_date || 'No expiry'}
                          </p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => deleteCertificate(employeeProfile.id, cert.id)}>
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No certificates added</p>
                )}
              </div>

              {/* Licenses Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">🪪 Licenses</h3>
                  <Button size="sm" onClick={() => setShowAddLicenseDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add License
                  </Button>
                </div>
                {employeeProfile.licenses?.length > 0 ? (
                  <div className="space-y-2">
                    {employeeProfile.licenses.map((lic) => (
                      <div key={lic.id} className="flex justify-between items-center p-3 bg-purple-50 rounded">
                        <div>
                          <p className="font-medium">{lic.license_type}</p>
                          <p className="text-sm text-gray-600">
                            #{lic.license_number} • {lic.state || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires: {lic.expiry_date || 'No expiry'}
                          </p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => deleteLicense(employeeProfile.id, lic.id)}>
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No licenses added</p>
                )}
              </div>

              {/* Credentials Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">🏅 Credentials</h3>
                  <Button size="sm" onClick={() => setShowAddCredentialDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Credential
                  </Button>
                </div>
                {employeeProfile.credentials?.length > 0 ? (
                  <div className="space-y-2">
                    {employeeProfile.credentials.map((cred) => (
                      <div key={cred.id} className="flex justify-between items-center p-3 bg-blue-50 rounded">
                        <div>
                          <p className="font-medium">{cred.credential_type}</p>
                          <p className="text-sm text-gray-600">
                            #{cred.credential_number || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires: {cred.expiry_date || 'No expiry'} {cred.notes && `• ${cred.notes}`}
                          </p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => deleteCredential(employeeProfile.id, cred.id)}>
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No credentials added</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Certificate/License/Credential Dialogs - Using Extracted Components */}
      <AddCertificateDialog
        open={showAddCertificateDialog}
        onOpenChange={setShowAddCertificateDialog}
        newCertificate={newCertificate}
        setNewCertificate={setNewCertificate}
        onSave={addCertificate}
        onFileUpload={(e) => handleDocumentUpload(e, 'certificate')}
        isUploading={isUploadingDocument}
      />

      <AddLicenseDialog
        open={showAddLicenseDialog}
        onOpenChange={setShowAddLicenseDialog}
        newLicense={newLicense}
        setNewLicense={setNewLicense}
        onSave={addLicense}
        onFileUpload={(e) => handleDocumentUpload(e, 'license')}
        isUploading={isUploadingDocument}
      />

      <AddCredentialDialog
        open={showAddCredentialDialog}
        onOpenChange={setShowAddCredentialDialog}
        newCredential={newCredential}
        setNewCredential={setNewCredential}
        onSave={addCredential}
      />

      {/* Visibility Settings Dialog */}
      <Dialog open={showVisibilitySettingsDialog} onOpenChange={setShowVisibilitySettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Employee Data Visibility Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Control what employee information managers and accounts roles can see.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Certificates to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_certificates_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_certificates_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Licenses to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_licenses_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_licenses_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Credentials to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_credentials_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_credentials_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Hourly Rate to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_hourly_rate_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_hourly_rate_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show YTD Hours to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_ytd_hours_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_ytd_hours_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Contact Info to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_contact_info_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_contact_info_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowVisibilitySettingsDialog(false)}>Cancel</Button>
              <Button onClick={saveVisibilitySettings}>Save Settings</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Poppins:wght@600;700;800&display=swap');

        .admin-dashboard {
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          padding: 0;
        }

        .modern-bg {
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          background-attachment: fixed;
        }

        /* Company Header styles moved to /src/styles/companyHeader.css */

        .logout-btn {
          background: white;
          border: 1px solid #e2e8f0;
          font-weight: 500;
        }

        /* Dashboard Header Modern */
        .dashboard-header-modern {
          background: white;
          margin: 2rem;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .personalized-title {
          font-size: 2.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
          font-family: 'Poppins', sans-serif;
        }

        .welcome-text {
          font-size: 1rem;
          color: #64748b;
          margin: 0.5rem 0 0 0;
          font-weight: 500;
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1rem;
        }

        .action-btn-small {
          font-weight: 600;
          border-radius: 8px;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .dashboard-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin: 0 2rem 2rem 2rem;
        }

        .stat-card {
          background: linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%);
          color: #1e293b;
          border: 2px solid rgba(91, 99, 211, 0.2);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(91, 99, 211, 0.15);
        }

        .stat-card.clickable {
          cursor: pointer;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 35px rgba(91, 99, 211, 0.25);
          border-color: rgba(91, 99, 211, 0.4);
        }

        .stat-title {
          color: #475569;
          font-size: 1rem;
          font-weight: 600;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 600;
        }

        /* Tabs Content Styling */
        [data-testid="tabs-list"] {
          background: white;
          padding: 0.5rem;
          border-radius: 12px;
          margin: 0 2rem 2rem 2rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          display: flex;
          flex-wrap: wrap;
          height: auto;
        }

        /* Make tab content cards white with shadows */
        .admin-dashboard > div > div > div > div[role="tabpanel"] > div {
          margin: 0 2rem 2rem 2rem;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .dashboard-tabs {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1rem;
        }

        .table-container-with-sticky-scroll {
          position: relative;
          max-height: calc(100vh - 250px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .table-responsive {
          overflow-x: auto;
          overflow-y: auto;
          flex: 1;
          position: relative;
        }
        
        /* Enhanced scrollbar styling */
        .table-responsive::-webkit-scrollbar {
          height: 14px;
          width: 14px;
        }
        
        .table-responsive::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 8px;
        }
        
        .table-responsive::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          border-radius: 8px;
          border: 2px solid #f8fafc;
        }
        
        .table-responsive::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #4a52c2 0%, #5a2890 100%);
        }
        
        .table-responsive::-webkit-scrollbar-corner {
          background: #f8fafc;
        }

        /* Sticky scrollbar at bottom of viewport */
        .sticky-scrollbar-container {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: white;
          border-top: 2px solid #e2e8f0;
          box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
          padding: 8px 16px;
        }

        .sticky-scrollbar-track {
          overflow-x: auto;
          overflow-y: hidden;
          height: 20px;
          background: #f8fafc;
          border-radius: 10px;
        }

        .sticky-scrollbar-track::-webkit-scrollbar {
          height: 16px;
        }

        .sticky-scrollbar-track::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .sticky-scrollbar-track::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          border-radius: 10px;
          border: 3px solid #f1f5f9;
        }

        .sticky-scrollbar-track::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #4a52c2 0%, #5a2890 100%);
        }

        .sticky-scrollbar-content {
          height: 1px;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          text-align: left;
          padding: 0.75rem;
          background: #f8fafc;
          font-weight: 600;
          color: #475569;
        }

        .data-table td {
          padding: 0.75rem;
          border-top: 1px solid #e2e8f0;
        }

        .messages-container {
          max-height: 400px;
          overflow-y: auto;
          margin-bottom: 1rem;
        }

        .message {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          margin-bottom: 0.75rem;
        }

        .message-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .message-header span {
          color: #64748b;
        }

        .message-form {
          display: flex;
          gap: 0.75rem;
        }

        .subscription-plans {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .plan-price {
          font-size: 2rem;
          font-weight: 700;
          color: #667eea;
          margin: 0.5rem 0;
        }

        .team-assignment {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .team-list {
          max-height: 400px;
          overflow-y: auto;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
        }

        .team-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
        }

        .team-item:last-child {
          border-bottom: none;
        }

        .team-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .team-item label {
          cursor: pointer;
          flex: 1;
        }

        .edit-stock-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 1rem 0;
        }

        .current-stock, .new-stock {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .stock-display {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          text-align: center;
        }

        .stock-display.new {
          color: #667eea;
          background: #f0f4ff;
        }

        .stock-adjustment {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .adjustment-controls {
          display: grid;
          grid-template-columns: 60px 1fr 60px;
          gap: 0.75rem;
          align-items: center;
        }

        .adjustment-input {
          text-align: center;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .dialog-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        /* ====================================
           MOBILE RESPONSIVE STYLES
           ==================================== */
        
        @media (max-width: 768px) {
          /* Dashboard Layout */
          .dashboard {
            padding: 0.5rem;
          }

          /* Header */
          .dashboard-header {
            flex-direction: column;
            padding: 1rem;
            gap: 0.75rem;
          }

          .dashboard-header > div:first-child {
            text-align: center;
          }

          .dashboard-header h1 {
            font-size: 1.5rem;
          }

          .dashboard-header p {
            font-size: 0.875rem;
          }

          .header-actions {
            width: 100%;
            flex-direction: column;
            gap: 0.5rem;
          }

          .header-actions button {
            width: 100%;
          }

          /* Company Logo */
          .company-logo {
            width: 50px !important;
            height: 50px !important;
          }

          /* Stats Grid */
          .stats-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem;
            padding: 0 0.5rem;
          }

          .stat-card {
            padding: 1rem !important;
          }

          .stat-value {
            font-size: 1.75rem !important;
          }

          /* Tabs */
          .tabs-container {
            padding: 0 0.5rem;
          }

          .tabs-list {
            flex-wrap: wrap !important;
            gap: 0.5rem !important;
            overflow: visible !important;
            white-space: normal !important;
            height: auto !important;
          }

          .tabs-trigger {
            flex: 0 1 calc(33.333% - 0.35rem) !important;
            min-width: 90px !important;
            max-width: none !important;
            font-size: 0.75rem !important;
            padding: 0.5rem 0.25rem !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          /* Tables - Mobile Optimized */
          .table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          table {
            min-width: 600px;
            font-size: 0.8125rem;
          }

          th, td {
            padding: 0.5rem !important;
            white-space: nowrap;
          }

          /* Action Buttons in Tables */
          .action-buttons {
            flex-wrap: nowrap;
            gap: 0.25rem;
          }

          .action-buttons button {
            padding: 0.375rem 0.5rem !important;
            font-size: 0.75rem !important;
            min-height: 36px !important;
          }

          /* Dialogs */
          .dialog-content {
            max-width: 95vw !important;
            max-height: 90vh !important;
            overflow-y: auto;
            padding: 1rem !important;
          }

          .dialog-header {
            padding-bottom: 0.75rem;
          }

          .dialog-title {
            font-size: 1.25rem !important;
          }

          .dialog-description {
            font-size: 0.875rem !important;
          }

          /* Forms */
          form {
            gap: 1rem !important;
          }

          input, textarea, select {
            font-size: 16px !important; /* Prevents iOS zoom */
            min-height: 44px !important;
            padding: 0.75rem !important;
          }

          label {
            font-size: 0.875rem !important;
          }

          /* Buttons */
          button {
            min-height: 44px !important;
            padding: 0.75rem 1rem !important;
          }

          .dialog-actions {
            flex-direction: column-reverse;
            gap: 0.5rem;
          }

          .dialog-actions button {
            width: 100%;
          }

          /* Employee List */
          .employee-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem;
          }

          .employee-card {
            padding: 1rem !important;
          }

          /* Project List */
          .projects-list {
            gap: 0.75rem;
          }

          .project-card {
            padding: 1rem !important;
          }

          /* Material Order Details */
          .order-details {
            padding: 1rem !important;
          }

          .order-items-grid {
            grid-template-columns: 1fr !important;
          }

          /* Stock Management */
          .stock-grid {
            grid-template-columns: 1fr !important;
          }

          .stock-item {
            padding: 0.75rem !important;
          }

          /* Expense Cards */
          .expense-card {
            padding: 1rem !important;
          }

          .expense-details {
            font-size: 0.8125rem !important;
          }

          /* Settings Form */
          .settings-section {
            gap: 1rem !important;
          }

          /* Material Order Form */
          .material-tabs {
            overflow-x: auto;
          }

          /* Cards */
          .card {
            margin-bottom: 1rem;
          }

          .card-content {
            padding: 1rem !important;
          }
        }

        @media (max-width: 375px) {
          .dashboard-header h1 {
            font-size: 1.25rem !important;
          }

          .stats-grid {
            padding: 0 0.25rem;
          }

          .stat-value {
            font-size: 1.5rem !important;
          }

          .tabs-trigger {
            font-size: 0.8125rem;
            padding: 0.375rem;
          }

          table {
            font-size: 0.75rem;
          }

          th, td {
            padding: 0.375rem !important;
          }
        }
      `}</style>

      {/* Image Viewer Dialog */}
      <Dialog open={showImageViewer} onOpenChange={setShowImageViewer}>
        <DialogContent className="max-w-4xl">
          <div className="relative">
            {viewerImages.length > 0 && (
              <>
                <img 
                  src={`${API_URL}/api/image/${viewerImages[viewerIndex].split('/').pop()}`}
                  alt="View" 
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={(e) => {
                    console.error('Image failed to load:', viewerImages[viewerIndex]);
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle">Image not found</text></svg>';
                  }}
                />
                
                {/* Navigation Arrows */}
                {viewerImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setViewerIndex((viewerIndex - 1 + viewerImages.length) % viewerImages.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 text-xl"
                    >
                      ←
                    </button>
                    <button
                      onClick={() => setViewerIndex((viewerIndex + 1) % viewerImages.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 text-xl"
                    >
                      →
                    </button>
                  </>
                )}
                
                {/* Image Counter */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded">
                  {viewerIndex + 1} / {viewerImages.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Driving License Prompt for Admin Users */}
      <DrivingLicensePrompt user={user} onComplete={fetchAllData} />

      {/* Welcome Guide for New Users (after registration) */}
      {showWelcomeGuide && (
        <div className="fixed inset-0 z-[200]">
          <WelcomeGuide 
            companyName={newCompanyName || companySettings.company_name}
            onComplete={() => setShowWelcomeGuide(false)}
          />
        </div>
      )}

      {/* Help Center Dialog */}
      <Dialog open={showHelpCenter} onOpenChange={setShowHelpCenter}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <HelpCenter onClose={() => setShowHelpCenter(false)} />
        </DialogContent>
      </Dialog>

      {/* Onboarding Tour (shows on first login) */}
      <OnboardingTour user={user} />

      {/* Personal Stats Breakdown Dialog */}
      <Dialog open={showPersonalStatsDialog} onOpenChange={setShowPersonalStatsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {personalStatsType === 'weekly-hours' && 'Your Weekly Hours Breakdown'}
              {personalStatsType === 'biweekly-hours' && 'Your Bi-Weekly Hours Breakdown'}
              {personalStatsType === 'biweekly-pay' && 'Your Bi-Weekly Pay Breakdown'}
            </DialogTitle>
          </DialogHeader>
          {personalStatsLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Fetching your {personalStatsType === 'biweekly-pay' ? 'pay' : 'hours'}...</p>
            </div>
          ) : personalStatsData ? (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-700 font-medium">
                  👤 {personalStatsData?.employee_name || 'Your'} {personalStatsType === 'biweekly-pay' ? 'Earnings' : 'Hours'}
                </p>
                <p className="text-2xl font-bold text-green-800">
                  {personalStatsType === 'biweekly-pay' 
                    ? `$${(personalStatsData?.total_pay || 0).toFixed(2)}`
                    : `${(personalStatsData?.total_hours || 0).toFixed(2)} hours`
                  }
                </p>
              </div>
              {personalStatsType === 'biweekly-pay' && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Rate: ${personalStatsData?.hourly_rate || 0}/hr x {(personalStatsData?.total_hours || 0).toFixed(2)} hrs
                  </p>
                </div>
              )}
              {personalStatsData?.daily_breakdown && (
                <div>
                  <h4 className="font-medium mb-2">Daily Breakdown</h4>
                  <div className="space-y-1">
                    {personalStatsData.daily_breakdown.map((day, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1 border-b border-gray-100">
                        <span>{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        <span className="font-medium">
                          {personalStatsType === 'biweekly-pay' 
                            ? `$${(day.pay || day.hours * (personalStatsData?.hourly_rate || 0)).toFixed(2)}`
                            : `${day.hours.toFixed(2)} hrs`
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onOpenChange={setShowChangePasswordDialog}
      />

      {/* Mobile Quick Schedule Builder — opened from the tile grid. */}
      <MobileScheduleBuilder
        open={showQuickBuilder}
        onOpenChange={setShowQuickBuilder}
        projects={projects}
        employees={employees}
        onSaved={() => fetchSchedules()}
      />

      {/* My Profile Dialog */}
      <Dialog open={showMyProfileDialog} onOpenChange={setShowMyProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="my-profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              My Profile
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="personal" data-testid="personal-tab">Personal Info</TabsTrigger>
              <TabsTrigger value="earnings" data-testid="earnings-tab">Earnings</TabsTrigger>
            </TabsList>
            <TabsContent value="personal" className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium">{user?.name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{user?.email || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Role</p>
                  <p className="font-medium">{user?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{user?.phone || '-'}</p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="earnings" className="space-y-4">
              <MyEarnings />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}

